import { useState, useEffect } from 'react';
import {
  LogOut, BarChart3, Users, BookOpen, TrendingUp, DollarSign,
  Settings, Shield, Activity, AlertCircle, CheckCircle, XCircle,
  Eye, EyeOff, Download, Upload, RefreshCw, Plus, Edit, Trash2,
  Search, Filter, Calendar, Clock, Award, Zap, Star, Target,
  PieChart, TrendingDown, UserPlus, FileText, Database, Server, Ticket,
  Globe, Languages, Trophy, Check, Play, Mail, Bell, Send, CreditCard, Save,
  Copy, RefreshCcw, Sparkles, Palette, Bookmark, Maximize2, Newspaper, Layout,
  HelpCircle, ChevronDown, ChevronUp, ArrowUp, ArrowDown, ShoppingCart, Gift, Smartphone,
  X, ZoomIn, Loader, Lock
} from 'lucide-react';
import React from 'react';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';
import SystemSettings from './SystemSettings';
import DiscountCodeManager from './DiscountCodeManager';
import PremiumQuizMode from './PremiumQuizMode';
import ImageLightbox from './ImageLightbox';
import BlogAdminTab from './BlogAdminTab';
import HomepageAdminTab from './HomepageAdminTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to fix image URLs - handles old /uploads/ and new /api/uploads/ formats
// Also handles old domain URLs that need to be redirected to current domain
const fixImageUrl = (url) => {
  if (!url) return null;
  
  // If already a full http URL
  if (url.startsWith('http')) {
    // If URL is from Cloudflare R2 (images.ehliyet.nu), return as-is
    if (url.includes('images.ehliyet.nu') || url.includes('.r2.dev')) {
      return url;
    }
    
    // List of old domains that need to be redirected to current domain
    const oldDomains = [
      'swedriving.preview.emergentagent.com',
      'teoriprov-3.preview.emergentagent.com',
      'teoriprov-2.preview.emergentagent.com',
      'teoriprov.preview.emergentagent.com',
      'drivingtheory-1.preview.emergentagent.com',
      'drivingtheory.preview.emergentagent.com',
      'turkswedtheory.preview.emergentagent.com',
      'svenskteori.preview.emergentagent.com',
      'ehliyet.preview.emergentagent.com',
      'swedish-driver-test.preview.emergentagent.com',
      'swedish-theory-test-1.preview.emergentagent.com',
      'swedish-theory-test.preview.emergentagent.com',
      'testprep-tr.preview.emergentagent.com',
      'testprep-tr.emergent.host',
      'testtheory.preview.emergentagent.com'
    ];
    
    // Check if URL is from an old domain
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        // Extract the path after the domain
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        // Ensure path has /api prefix
        const fixedPath = path.startsWith('/api') ? path : `/api${path}`;
        // Return using current backend URL with proper path
        return `${BACKEND_URL}${fixedPath}`;
      }
    }
    
    // Fix old format URLs that don't have /api prefix
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  
  // Relative URL - ensure /api prefix
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/api/uploads/${url}`;
};

export default function AdminDashboard({ user, onLogout, onBackToHome, onTestCustomerView }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [staticPages, setStaticPages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [questionReports, setQuestionReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  
  // Migration state
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  
  // User Management Modal States
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalType, setUserModalType] = useState('all'); // 'all', 'premium', 'free', 'recent'
  const [userModalUsers, setUserModalUsers] = useState([]);
  const [userActionLoading, setUserActionLoading] = useState(null);
  
  // User Impersonation State
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [showImpersonationView, setShowImpersonationView] = useState(false);
  
  // PASSWORD PROTECTION
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState(localStorage.getItem('admin_password') || '');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  
  // Migration function - Migrate old exam data to user_answers for category stats
  const runMigration = async () => {
    setIsMigrating(true);
    setMigrationStatus(null);
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.post(`${API}/admin/migrate-user-answers`, null, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setMigrationStatus({
        success: true,
        message: response.data.message,
        migrated: response.data.migrated,
        skipped: response.data.skipped
      });
      console.log('✅ Migration complete:', response.data);
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationStatus({
        success: false,
        message: error.response?.data?.detail || 'Migration failed'
      });
    } finally {
      setIsMigrating(false);
    }
  };
  
  // ============= DATA FETCH FUNCTIONS - Must be defined before useEffect =============
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Categories error:', error);
      setCategories([]);
    }
  };
  
  const fetchQuestionReports = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.get(`${API}/admin/question-reports`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setQuestionReports(response.data || []);
      console.log('🚨 Question Reports:', response.data?.length || 0);
    } catch (error) {
      console.error('Question reports error:', error);
      setQuestionReports([]);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Testimonials fetch error:', error);
    }
  };

  const fetchStaticPages = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.get(`${API}/admin/pages`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setStaticPages(response.data);
    } catch (error) {
      console.error('Static pages error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats?nocache=true`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setStats(response.data);
      console.log('📊 Stats:', response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { 
          'X-Admin-Password': adminPassword,
          'Cache-Control': 'no-cache' 
        }
      });
      setAnalytics(response.data);
      console.log('📈 Analytics:', response.data);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.get(`${API}/admin/recent-activities`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setRecentActivities(response.data);
      console.log('📋 Recent activities:', response.data);
    } catch (error) {
      console.error('Recent activities error:', error);
      setRecentActivities([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      console.log('🔍 Fetching users with password:', adminPassword ? 'SET' : 'NOT SET');
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      console.log('✅ Users fetched:', response.data.length);
      setUsers(response.data);
    } catch (error) {
      console.error('❌ Users error:', error.response?.status, error.message);
      setUsers([]);
    }
  };

  const fetchQuestions = async () => {
    try {
      // randomize=false to keep questions in consistent order for admin
      const response = await axios.get(`${API}/questions?limit=5000&randomize=false`);
      setQuestions(response.data);
      console.log(`✅ ${response.data.length} soru yüklendi`);
    } catch (error) {
      console.error('Questions error:', error);
      setQuestions([]);
    }
  };
  
  const fetchAllData = async () => {
    await Promise.all([
      fetchStats(),
      fetchAnalytics(),
      fetchRecentActivities(),
      fetchUsers(),
      fetchQuestions(),
      fetchStaticPages(),
      fetchTestimonials(),
      fetchQuestionReports(),
      fetchCategories()
    ]);
  };

  // Check if already authenticated - MUST BE BEFORE CONDITIONAL RETURN
  useEffect(() => {
    const savedPassword = localStorage.getItem('admin_password');
    if (savedPassword) {
      // Verify saved password with backend (lightweight endpoint)
      axios.get(`${API}/admin/verify`, {
        headers: { 'X-Admin-Password': savedPassword },
        timeout: 10000
      }).then(() => {
        setAdminPassword(savedPassword);
        setIsAuthenticated(true);
      }).catch((err) => {
        // Only clear password on 401 (wrong password), NOT on network errors
        if (err.response?.status === 401) {
          localStorage.removeItem('admin_password');
          setAdminPassword('');
        }
      });
    }
  }, []);

  // Data fetching effects - MUST BE BEFORE CONDITIONAL RETURN
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
      // Auto-refresh disabled to prevent questions from changing while reading
      // Manually refresh by clicking the refresh button or switching tabs
      // const interval = setInterval(() => {
      //   fetchAllData();
      //   setLastUpdate(new Date());
      // }, 15000);
      // return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Listen for adminEditQuestion event from HomepageQuizTab
  useEffect(() => {
    const handleAdminEditQuestion = (e) => {
      const { question, switchTab } = e.detail;
      if (question && switchTab) {
        setActiveTab(switchTab);
        setEditingQuestion(question);
        setSearchTerm(question.question_number || question.id?.substring(0, 8) || '');
      }
    };
    
    window.addEventListener('adminEditQuestion', handleAdminEditQuestion);
    return () => window.removeEventListener('adminEditQuestion', handleAdminEditQuestion);
  }, []);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'questions' && questions.length === 0) {
      fetchQuestions();
    }
  }, [activeTab, isAuthenticated, questions.length]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordInput.trim()) {
      setPasswordError('Lütfen şifre girin');
      return;
    }
    setVerifyingPassword(true);
    setPasswordError('');
    
    try {
      // Verify password with backend
      const response = await axios.get(`${API}/admin/verify`, {
        headers: { 'X-Admin-Password': passwordInput },
        timeout: 15000
      });
      
      // If we get here, password is correct
      localStorage.setItem('admin_password', passwordInput);
      setAdminPassword(passwordInput);
      setIsAuthenticated(true);
      setPasswordError('');
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.response?.status === 401) {
        setPasswordError('Yanlış şifre!');
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        setPasswordError('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.');
      } else {
        setPasswordError('Sunucu hatası: ' + (error.response?.status || error.message));
      }
      setPasswordInput('');
    } finally {
      setVerifyingPassword(false);
    }
  };
  
  // User Modal Functions
  const openUserModal = (type) => {
    let filteredUsers = [];
    switch(type) {
      case 'premium':
        filteredUsers = users.filter(u => u.is_premium || u.subscription_status === 'active');
        break;
      case 'free':
        filteredUsers = users.filter(u => !u.is_premium && u.subscription_status !== 'active');
        break;
      case 'recent':
        // Last 24 hours
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
        filteredUsers = users.filter(u => new Date(u.created_at) > yesterday);
        break;
      default:
        filteredUsers = users;
    }
    setUserModalUsers(filteredUsers);
    setUserModalType(type);
    setShowUserModal(true);
  };
  
  const handleUserAction = async (userId, action) => {
    setUserActionLoading(userId);
    const adminPassword = localStorage.getItem('admin_password');
    
    // Find user to get email (more reliable than id)
    const targetUser = userModalUsers.find(u => u.id === userId || u.user_id === userId || u.email === userId);
    const userIdentifier = targetUser?.email || userId; // Use email if available
    
    try {
      if (action === 'delete') {
        if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
          setUserActionLoading(null);
          return;
        }
        await axios.delete(`${API}/admin/users/${encodeURIComponent(userIdentifier)}`, {
          headers: { 'X-Admin-Password': adminPassword }
        });
        setUserModalUsers(prev => prev.filter(u => u.id !== userId && u.email !== userIdentifier));
        await fetchUsers();
        alert('✅ Kullanıcı başarıyla silindi!');
      } else if (action === 'block') {
        await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/status`, 
          { status: 'blocked' },
          { headers: { 'X-Admin-Password': adminPassword } }
        );
        setUserModalUsers(prev => prev.map(u => 
          (u.id === userId || u.email === userIdentifier) ? {...u, account_status: 'blocked'} : u
        ));
        await fetchUsers();
        alert('✅ Kullanıcı engellendi!');
      } else if (action === 'activate') {
        await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/status`, 
          { status: 'active' },
          { headers: { 'X-Admin-Password': adminPassword } }
        );
        setUserModalUsers(prev => prev.map(u => 
          (u.id === userId || u.email === userIdentifier) ? {...u, account_status: 'active'} : u
        ));
        await fetchUsers();
        alert('✅ Kullanıcı aktifleştirildi!');
      } else if (action === 'make_premium') {
        await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/premium`, 
          { is_premium: true },
          { headers: { 'X-Admin-Password': adminPassword } }
        );
        setUserModalUsers(prev => prev.map(u => 
          (u.id === userId || u.email === userIdentifier) ? {...u, is_premium: true} : u
        ));
        await fetchUsers();
        alert('✅ Kullanıcı premium yapıldı!');
      } else if (action === 'remove_premium') {
        await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/premium`, 
          { is_premium: false },
          { headers: { 'X-Admin-Password': adminPassword } }
        );
        setUserModalUsers(prev => prev.map(u => 
          (u.id === userId || u.email === userIdentifier) ? {...u, is_premium: false} : u
        ));
        await fetchUsers();
        alert('✅ Premium kaldırıldı!');
      }
    } catch (error) {
      console.error('User action error:', error);
      alert('İşlem başarısız: ' + (error.response?.data?.detail || error.message));
    }
    setUserActionLoading(null);
  };
  
  // Show password screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-black text-white mb-2">Admin Paneli</h1>
              <p className="text-white/60">Giriş yapmak için şifre gerekli</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Admin Şifresi"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
                {passwordError && (
                  <p className="text-red-400 text-sm mt-2">{passwordError}</p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={verifyingPassword || !passwordInput.trim()}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {verifyingPassword ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Kontrol ediliyor...
                  </>
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const updateReportStatus = async (reportId, status) => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      await axios.put(`${API}/admin/question-report/${reportId}`, 
        { status },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchQuestionReports();
    } catch (error) {
      console.error('Update report status error:', error);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchAllData();
    setLastUpdate(new Date());
    setTimeout(() => setLoading(false), 500);
  };

  const handleViewAsCustomer = async (user) => {
    console.log('View as customer:', user);
    try {
      const adminPassword = localStorage.getItem('admin_password');
      // Call backend to set up impersonation
      const response = await axios.post(`${API}/admin/impersonate`, 
        { target_email: user.email },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      
      if (response.data.success) {
        // Store impersonation data and redirect to customer dashboard
        localStorage.setItem('impersonated_user', JSON.stringify(response.data.user));
        localStorage.setItem('is_impersonating', 'true');
        window.location.href = '/';  // Redirect to customer view
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      alert(`Hata: ${error.response?.data?.detail || 'Müşteri görünümü başlatılamadı'}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* User Management Modal */}
      <UserManagementModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        users={userModalUsers}
        type={userModalType}
        onUserAction={handleUserAction}
        actionLoading={userActionLoading}
        onViewAsUser={handleViewAsCustomer}
      />
      
      {/* TOP HEADER */}
      <header className="bg-black/40 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg animate-pulse">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white">Admin Command Center</h1>
                <p className="text-sm text-gray-400">Ehliyet.nu Management Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all flex items-center gap-2 ${
                  loading ? 'animate-spin' : ''
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                {loading ? 'Yenileniyor...' : 'Yenile'}
              </button>
              
              <button
                onClick={() => {
                  // Store admin password in localStorage for API authentication
                  const adminPassword = localStorage.getItem('admin_password') ;
                  localStorage.setItem('admin_password', adminPassword);
                  onTestCustomerView();
                }}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg transition-all flex items-center gap-2 shadow-lg"
              >
                <Eye className="w-4 h-4" />
                Test Müşteri Görünümü
              </button>

              <button
                onClick={onBackToHome}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
              >
                Ana Sayfa
              </button>

              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Çıkış
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* REAL-TIME STATS BANNER */}
        <div className="mb-8 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-lg border border-green-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <div>
                <h3 className="text-lg font-bold text-white">Sistem Canlı ve Çalışıyor</h3>
                <p className="text-sm text-gray-300">
                  Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR')}
                </p>
              </div>
              <button
                onClick={() => {
                  fetchAllData();
                  setLastUpdate(new Date());
                }}
                className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition-all flex items-center gap-2"
              >
                🔄 Yenile
              </button>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-white">{stats?.total_questions || 0}</div>
              <div className="text-sm text-gray-300">Toplam Soru</div>
            </div>
          </div>
        </div>

        {/* STATS CARDS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* Card 1: Sorular */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl blur opacity-50 group-hover:opacity-100 transition"></div>
            <div className="relative bg-slate-900 border border-purple-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <BookOpen className="w-8 h-8 text-purple-400" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-white">{stats?.total_questions || 0}</div>
                  <div className="text-sm text-gray-400">Soru</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Veritabanı</span>
                <div className="flex items-center gap-1 text-green-400">
                  <TrendingUp className="w-4 h-4" />
                  <span>Aktif</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Kategoriler */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl blur opacity-50 group-hover:opacity-100 transition"></div>
            <div className="relative bg-slate-900 border border-blue-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <PieChart className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-white">{stats?.total_categories || 0}</div>
                  <div className="text-sm text-gray-400">Kategori</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Trafik Konuları</span>
                <div className="flex items-center gap-1 text-blue-400">
                  <Target className="w-4 h-4" />
                  <span>5 Grup</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Kullanıcılar */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl blur opacity-50 group-hover:opacity-100 transition"></div>
            <div className="relative bg-slate-900 border border-orange-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-500/20 rounded-xl">
                  <Users className="w-8 h-8 text-orange-400" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-white">{users.length || 0}</div>
                  <div className="text-sm text-gray-400">Kullanıcı</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Kayıtlı</span>
                <div className="flex items-center gap-1 text-orange-400">
                  <UserPlus className="w-4 h-4" />
                  <span>Aktif</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Sınavlar */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-50 group-hover:opacity-100 transition"></div>
            <div className="relative bg-slate-900 border border-green-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <Award className="w-8 h-8 text-green-400" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-white">{stats?.total_exams_completed || 0}</div>
                  <div className="text-sm text-gray-400">Sınav</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Tamamlanan</span>
                <div className="flex items-center gap-1 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>Toplam</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'overview', label: 'Genel Bakış', icon: BarChart3 },
            { id: 'homepage', label: 'Ana Sayfa Editörü', icon: Layout },
            { id: 'test-mode', label: 'Test Modu', icon: Trophy },
            { id: 'analytics', label: 'Analitik', icon: Activity },
            { id: 'users', label: 'Kullanıcılar', icon: Users },
            { id: 'questions', label: 'Sorular', icon: BookOpen },
            { id: 'reported-questions', label: `Hatalı Sorular (${questionReports.filter(r => r.status === 'pending').length})`, icon: AlertCircle },
            { id: 'testimonials', label: 'Kullanıcı Yorumları', icon: Star },
            { id: 'welcome-settings', label: 'Hoş Geldin Ayarları', icon: Sparkles },
            { id: 'pricing', label: 'Fiyat Ayarları', icon: CreditCard },
            { id: 'swish-settings', label: 'Swish Ayarları', icon: Smartphone },
            { id: 'homepage-quiz', label: 'Ana Sayfa Quiz', icon: Play },
            { id: 'duplicate-management', label: 'Duplicate Yönetimi', icon: AlertCircle },
            { id: 'static-pages', label: 'Sayfa İçerikleri', icon: FileText },
            { id: 'blog', label: 'Blog Yönetimi', icon: Newspaper },
            { id: 'sss', label: 'SSS Yönetimi', icon: HelpCircle },
            { id: 'quiz-settings', label: 'Quiz Ayarları', icon: Zap },
            { id: 'notifications', label: 'Bildirimler & E-posta', icon: Mail },
            { id: 'footer-settings', label: 'Footer Ayarları', icon: Globe },
            { id: 'discount', label: 'İndirim Kodları', icon: Ticket },
            { id: 'system', label: 'Sistem', icon: Server },
            { id: 'settings', label: 'Ayarlar', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              data-tab={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`px-4 py-2.5 rounded-xl font-semibold transition-all whitespace-nowrap flex items-center gap-2 text-sm ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* DATA MIGRATION BANNER - Important for category stats */}
        <div className="bg-gradient-to-r from-orange-900/50 to-yellow-900/50 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-black text-white flex items-center gap-2 mb-2">
                <Database className="w-6 h-6 text-orange-400" />
                🔄 Veri Güncelleme (Kategori İstatistikleri)
              </h3>
              <p className="text-white/70 text-sm">
                Eski sınav verilerini kategori istatistiklerine aktarır. Kullanıcıların Kategori Bazlı Performans görünümünü düzeltir.
              </p>
              {migrationStatus && (
                <div className={`mt-2 p-3 rounded-xl ${migrationStatus.success ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                  {migrationStatus.success ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>{migrationStatus.message}</span>
                      <span className="font-bold">({migrationStatus.migrated} kayıt aktarıldı)</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      <span>{migrationStatus.message}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={runMigration}
              disabled={isMigrating}
              className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                isMigrating 
                  ? 'bg-gray-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg hover:scale-105'
              }`}
            >
              {isMigrating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <Database className="w-5 h-5" />
                  Verileri Güncelle
                </>
              )}
            </button>
          </div>
        </div>

        {/* TAB CONTENT */}
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} questions={questions} users={users} recentActivities={recentActivities} />
        )}

        {activeTab === 'homepage' && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <HomepageAdminTab adminPassword={localStorage.getItem('admin_password')} />
          </div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab stats={stats} analytics={analytics} users={users} onOpenUserModal={openUserModal} />
        )}

        {activeTab === 'users' && (
          <UsersTab users={users} fetchUsers={fetchUsers} handleViewAsCustomer={handleViewAsCustomer} adminPassword={adminPassword} />
        )}

        {activeTab === 'questions' && (
          <QuestionsTab 
            questions={questions} 
            stats={stats} 
            fetchQuestions={fetchQuestions} 
            categories={categories}
            initialEditingQuestion={editingQuestion}
            clearEditingQuestion={() => setEditingQuestion(null)}
            initialSearchTerm={searchTerm}
            onSearchTermUsed={() => setSearchTerm('')}
          />
        )}

        {activeTab === 'quiz-settings' && (
          <QuizSettingsTab questions={questions} fetchQuestions={fetchQuestions} />
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab />
        )}

        {activeTab === 'test-mode' && (
          <TestModeTab />
        )}

        {activeTab === 'duplicate-management' && (
          <DuplicateManagementTab />
        )}

        {activeTab === 'swedish-terms' && (
          <SwedishTermsTab />
        )}

        {activeTab === 'footer-settings' && (
          <FooterSettingsTab />
        )}

        {activeTab === 'discount' && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <DiscountCodeManager />
          </div>
        )}

        {activeTab === 'static-pages' && (
          <StaticPagesTab staticPages={staticPages} fetchStaticPages={fetchStaticPages} />
        )}

        {activeTab === 'blog' && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <BlogAdminTab adminPassword={localStorage.getItem('admin_password')} />
          </div>
        )}

        {activeTab === 'sss' && (
          <SSSTab />
        )}

        {activeTab === 'testimonials' && (
          <TestimonialsTab testimonials={testimonials} fetchTestimonials={fetchTestimonials} />
        )}

        {/* WELCOME SETTINGS TAB */}
        {activeTab === 'welcome-settings' && (
          <WelcomeSettingsTab />
        )}

        {/* PRICING TAB */}
        {activeTab === 'pricing' && (
          <PricingTab />
        )}

        {/* SWISH SETTINGS TAB */}
        {activeTab === 'swish-settings' && (
          <SwishSettingsTab />
        )}

        {/* HOMEPAGE QUIZ TAB */}
        {activeTab === 'homepage-quiz' && (
          <HomepageQuizTab questions={questions} fetchQuestions={fetchQuestions} />
        )}

        {/* REPORTED QUESTIONS TAB */}
        {activeTab === 'reported-questions' && (
          <ReportedQuestionsTab 
            questionReports={questionReports}
            fetchQuestionReports={fetchQuestionReports}
            updateReportStatus={updateReportStatus}
            setActiveTab={setActiveTab}
            setSearchTerm={setSearchTerm}
            setPreviewQuestion={setPreviewQuestion}
          />
        )}

        {activeTab === 'system' && (
          <SystemTab stats={stats} lastUpdate={lastUpdate} />
        )}

        {activeTab === 'settings' && (
          <SettingsTab />
        )}
      </div>

      {/* Preview Question Modal */}
      {previewQuestion && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl border border-white/10 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-bold text-white">Soru Önizleme</h2>
                {previewQuestion.question_number && (
                  <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-lg font-mono text-sm">
                    {previewQuestion.question_number}
                  </span>
                )}
              </div>
              <button
                onClick={() => setPreviewQuestion(null)}
                className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-all"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Question Text */}
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-gray-400 text-sm font-semibold mb-2">Soru Metni</h3>
                <p className="text-white text-lg">{previewQuestion.question_text || 'Soru metni yok'}</p>
              </div>

              {/* Error Description */}
              {previewQuestion.error_description && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <h3 className="text-red-400 text-sm font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Bildirilen Hata
                  </h3>
                  <p className="text-red-200">{previewQuestion.error_description}</p>
                </div>
              )}

              {/* Reporter */}
              {previewQuestion.reported_by && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-gray-400 text-sm font-semibold mb-2">Bildiren</h3>
                  <p className="text-white">{previewQuestion.reported_by}</p>
                </div>
              )}

              {/* Question ID */}
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-gray-400 text-sm font-semibold mb-2">Soru ID</h3>
                <p className="text-blue-400 font-mono text-sm">{previewQuestion.question_id}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  const searchValue = previewQuestion.question_number || previewQuestion.question_id || '';
                  console.log('Searching for:', searchValue);
                  setPreviewQuestion(null);
                  setActiveTab('questions');
                  // Use setTimeout to ensure tab switch happens first
                  setTimeout(() => {
                    setSearchTerm(searchValue);
                    window.scrollTo(0, 0);
                  }, 100);
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
              >
                <Search className="w-5 h-5" />
                Soruya Git ve Düzenle
              </button>
              <button
                onClick={() => setPreviewQuestion(null)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// OVERVIEW TAB
function OverviewTab({ stats, questions, users, recentActivities }) {
  const [visitorStats, setVisitorStats] = useState(null);
  const [loadingVisitors, setLoadingVisitors] = useState(true);
  const [resettingStats, setResettingStats] = useState(false);

  useEffect(() => {
    fetchVisitorStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchVisitorStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchVisitorStats = async () => {
    try {
      setLoadingVisitors(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/visitor-stats`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setVisitorStats(response.data);
    } catch (error) {
      console.error("Visitor stats error:", error);
    } finally {
      setLoadingVisitors(false);
    }
  };

  const resetVisitorStats = async () => {
    if (!window.confirm("Tüm ziyaretçi istatistiklerini sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz!")) {
      return;
    }
    try {
      setResettingStats(true);
      const adminPassword = localStorage.getItem("admin_password");
      await axios.delete(`${API}/admin/visitor-stats/reset`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      await fetchVisitorStats();
      alert("✅ İstatistikler sıfırlandı!");
    } catch (error) {
      alert("❌ Sıfırlama başarısız: " + (error.response?.data?.detail || error.message));
    } finally {
      setResettingStats(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Real-time Visitor Stats */}
      <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" />
            Ziyaretçi İstatistikleri
            <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full ml-2">
              Canlı
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchVisitorStats}
              disabled={loadingVisitors}
              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 hover:text-blue-200 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50 transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loadingVisitors ? 'animate-spin' : ''}`} />
              {loadingVisitors ? 'Yenileniyor...' : 'Yenile'}
            </button>
            <button 
              onClick={resetVisitorStats}
              disabled={resettingStats}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50 transition-all"
            >
              <Trash2 className={`w-4 h-4 ${resettingStats ? 'animate-spin' : ''}`} />
              {resettingStats ? 'Sıfırlanıyor...' : 'Sıfırla'}
            </button>
          </div>
        </div>
        
        {loadingVisitors && !visitorStats ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto" />
          </div>
        ) : (
          <>
            {/* Today's Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-black text-white">{visitorStats?.today?.views || 0}</div>
                <div className="text-blue-300 text-sm">Bugün Görüntüleme</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-black text-green-400">{visitorStats?.today?.unique_visitors || 0}</div>
                <div className="text-green-300 text-sm">Tekil Ziyaretçi</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-black text-yellow-400">{visitorStats?.last_hour || 0}</div>
                <div className="text-yellow-300 text-sm">Son 1 Saat</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-black text-purple-400">
                  {visitorStats?.device_stats?.mobile || 0}/{visitorStats?.device_stats?.desktop || 0}
                </div>
                <div className="text-purple-300 text-sm">Mobil/Masaüstü</div>
              </div>
            </div>

            {/* Weekly Chart */}
            {visitorStats?.daily_stats && (
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Son 7 Gün</h4>
                <div className="flex items-end justify-between h-32 gap-2">
                  {visitorStats.daily_stats.map((day, idx) => {
                    const maxViews = Math.max(...visitorStats.daily_stats.map(d => d.views || 1));
                    const height = ((day.views || 0) / maxViews) * 100;
                    const isToday = idx === visitorStats.daily_stats.length - 1;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex flex-col items-center">
                          <span className="text-xs text-white mb-1">{day.views}</span>
                          <div 
                            className={`w-full rounded-t transition-all ${
                              isToday ? 'bg-gradient-to-t from-blue-500 to-blue-400' : 'bg-blue-500/50'
                            }`}
                            style={{ height: `${Math.max(height, 10)}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs mt-1 ${isToday ? 'text-blue-300 font-bold' : 'text-gray-400'}`}>
                          {day.day}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Pages */}
            {visitorStats?.top_pages && visitorStats.top_pages.length > 0 && (
              <div className="mt-4 bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">En Çok Ziyaret Edilen Sayfalar</h4>
                <div className="space-y-2">
                  {visitorStats.top_pages.slice(0, 5).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-white/70 truncate">{page.page || "/"}</span>
                      <span className="text-blue-300 font-semibold">{page.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quick Stats */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-400" />
            Hızlı İstatistikler
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Toplam Soru</span>
              <span className="text-2xl font-bold text-white">{stats?.total_questions || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Kategoriler</span>
              <span className="text-2xl font-bold text-white">{stats?.total_categories || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Kullanıcılar</span>
              <span className="text-2xl font-bold text-white">{users?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Sınavlar</span>
              <span className="text-2xl font-bold text-white">{stats?.total_exams_completed || 0}</span>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-green-400" />
            Sistem Durumu
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">API Durumu</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold">Online</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Veritabanı</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold">Bağlı</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Frontend</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold">Aktif</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Uptime</span>
              <span className="text-white font-semibold">99.9%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-blue-400" />
          Son Aktiviteler
        </h3>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {recentActivities && recentActivities.length > 0 ? (
            recentActivities.map((activity, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                {activity.type === 'exam' && <Award className="w-5 h-5 text-yellow-400" />}
                {activity.type === 'user' && <UserPlus className="w-5 h-5 text-blue-400" />}
                {activity.type === 'payment' && <CreditCard className="w-5 h-5 text-green-400" />}
                {activity.type === 'question' && <CheckCircle className="w-5 h-5 text-purple-400" />}
                {!['exam', 'user', 'payment', 'question'].includes(activity.type) && (
                  <Clock className="w-5 h-5 text-gray-400" />
                )}
                <div className="flex-1">
                  <div className="text-white font-semibold">{activity.title}</div>
                  <div className="text-sm text-gray-400">{activity.description}</div>
                  <div className="text-xs text-gray-500">{activity.time}</div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-center py-4">Henüz aktivite verisi yok</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ANALYTICS TAB - FULLY FEATURED
function AnalyticsTab({ stats, analytics, users, onOpenUserModal }) {
  if (!analytics) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-white/60">Analitik verileri yükleniyor...</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
        <div className="relative bg-gradient-to-br from-blue-900/40 to-purple-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <h2 className="text-4xl font-black text-white flex items-center gap-4 mb-2">
            <BarChart3 className="w-12 h-12 animate-pulse" />
            📊 Platform Analitik Dashboard
          </h2>
          <p className="text-white/70 text-lg">Gerçek zamanlı veriler ve detaylı istatistikler</p>
        </div>
      </div>

      {/* Key Metrics - 4 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard 
          title="Toplam Gelir"
          value={`${analytics.total_revenue.toLocaleString('tr-TR')} SEK`}
          change="Gerçek Veri"
          positive={true}
          icon="💰"
          color="green"
        />
        <MetricCard 
          title="Aktif Kullanıcılar"
          value={analytics.active_users.toLocaleString('tr-TR')}
          change={`${analytics.recent_registrations} yeni (24s)`}
          positive={true}
          icon="👥"
          color="blue"
        />
        <MetricCard 
          title="Dönüşüm Oranı"
          value={`${analytics.conversion_rate}%`}
          change={`${analytics.premium_users} premium`}
          positive={analytics.conversion_rate > 10}
          icon="📈"
          color="purple"
        />
        <MetricCard 
          title="Ortalama Başarı"
          value={`${analytics.avg_success_rate}%`}
          change={`${analytics.total_exams} sınav`}
          positive={analytics.avg_success_rate > 70}
          icon="🎯"
          color="yellow"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-green-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <TrendingUp className="w-7 h-7 text-green-400" />
              💰 Gelir Trendi (Son 7 Gün)
            </h3>
            <div className="space-y-3">
              {analytics.revenue_by_day && analytics.revenue_by_day.length > 0 ? (
                analytics.revenue_by_day.map((dayData, index) => {
                  const maxValue = Math.max(...analytics.revenue_by_day.map(d => d.value));
                  return (
                    <RevenueBar 
                      key={index}
                      day={dayData.day} 
                      value={dayData.value} 
                      max={maxValue || 1} 
                      highlight={index === analytics.revenue_by_day.length - 1}
                    />
                  );
                })
              ) : (
                <p className="text-gray-400 text-center py-4">Henüz gelir verisi yok</p>
              )}
            </div>
            <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <div className="text-sm text-gray-300">Bu hafta toplam</div>
              <div className="text-3xl font-black text-green-400">
                {analytics.revenue_by_day 
                  ? analytics.revenue_by_day.reduce((sum, d) => sum + d.value, 0).toLocaleString('tr-TR')
                  : 0} SEK
              </div>
            </div>
          </div>
        </div>

        {/* User Activity */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 text-blue-400" />
              👥 Kullanıcı Aktivitesi
            </h3>
            <div className="space-y-4">
              <ClickableActivityItem 
                label="Toplam Kullanıcı"
                value={analytics.total_users.toString()}
                icon="🟢"
                color="green"
                onClick={() => onOpenUserModal('all')}
              />
              <ClickableActivityItem 
                label="Son 24 Saat Kayıt"
                value={analytics.recent_registrations.toString()}
                icon="📝"
                color="blue"
                onClick={() => onOpenUserModal('recent')}
              />
              <ClickableActivityItem 
                label="Premium Üye"
                value={analytics.premium_users.toString()}
                icon="👑"
                color="yellow"
                onClick={() => onOpenUserModal('premium')}
              />
              <ClickableActivityItem 
                label="Ücretsiz Üye"
                value={analytics.free_users.toString()}
                icon="⭐"
                color="gray"
                onClick={() => onOpenUserModal('free')}
              />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 text-center">
                <div className="text-2xl font-black text-blue-400">{analytics.conversion_rate}%</div>
                <div className="text-xs text-gray-400">Dönüşüm</div>
              </div>
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20 text-center">
                <div className="text-2xl font-black text-purple-400">{analytics.total_users}</div>
                <div className="text-xs text-gray-400">Toplam</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Performance */}
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
        <div className="relative bg-slate-900/80 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6">
          <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-purple-400" />
            📚 Soru Performansı & Zorluk Dağılımı
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DifficultyCard 
              level="Kolay"
              count={analytics.difficulty_distribution?.easy?.count?.toLocaleString('tr-TR') || '0'}
              percentage={`${Math.round((analytics.difficulty_distribution?.easy?.count / stats?.total_questions) * 100) || 0}%`}
              accuracy={`${analytics.difficulty_distribution?.easy?.accuracy || 0}%`}
              icon="🟢"
              color="green"
            />
            <DifficultyCard 
              level="Orta"
              count={analytics.difficulty_distribution?.medium?.count?.toLocaleString('tr-TR') || '0'}
              percentage={`${Math.round((analytics.difficulty_distribution?.medium?.count / stats?.total_questions) * 100) || 0}%`}
              accuracy={`${analytics.difficulty_distribution?.medium?.accuracy || 0}%`}
              icon="🟡"
              color="yellow"
            />
            <DifficultyCard 
              level="Zor"
              count={analytics.difficulty_distribution?.hard?.count?.toLocaleString('tr-TR') || '0'}
              percentage={`${Math.round((analytics.difficulty_distribution?.hard?.count / stats?.total_questions) * 100) || 0}%`}
              accuracy={`${analytics.difficulty_distribution?.hard?.accuracy || 0}%`}
              icon="🔴"
              color="red"
            />
          </div>
        </div>
      </div>

      {/* Category Performance */}
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
        <div className="relative bg-slate-900/80 backdrop-blur-xl border border-orange-500/20 rounded-2xl p-6">
          <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
            <Target className="w-7 h-7 text-orange-400" />
            🎯 Kategori Performansı
          </h3>
          <div className="space-y-4">
            {analytics.category_performance && analytics.category_performance.length > 0 ? (
              analytics.category_performance.map((cat, index) => {
                const colors = ['blue', 'green', 'purple', 'orange', 'teal'];
                return (
                  <CategoryPerformance 
                    key={index}
                    name={cat.name} 
                    completed={cat.completed} 
                    accuracy={cat.accuracy} 
                    color={colors[index % colors.length]} 
                  />
                );
              })
            ) : (
              <p className="text-gray-400 text-center py-4">Henüz kategori performans verisi yok</p>
            )}
          </div>
        </div>
      </div>

      {/* Exam Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-yellow-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Award className="w-7 h-7 text-yellow-400" />
              🏆 Sınav İstatistikleri
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
                <span className="text-gray-300">Toplam Sınav</span>
                <span className="text-2xl font-black text-white">{analytics.total_exams}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-green-500/10 rounded-lg">
                <span className="text-gray-300">Başarılı</span>
                <span className="text-2xl font-black text-green-400">
                  {analytics.passed_exams} ({analytics.total_exams > 0 ? Math.round((analytics.passed_exams / analytics.total_exams) * 100) : 0}%)
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-red-500/10 rounded-lg">
                <span className="text-gray-300">Başarısız</span>
                <span className="text-2xl font-black text-red-400">
                  {analytics.failed_exams} ({analytics.total_exams > 0 ? Math.round((analytics.failed_exams / analytics.total_exams) * 100) : 0}%)
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-blue-500/10 rounded-lg">
                <span className="text-gray-300">Ortalama Süre</span>
                <span className="text-2xl font-black text-blue-400">{analytics.avg_exam_time_minutes} dk</span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Activity className="w-7 h-7 text-cyan-400" />
              📊 Popüler Saatler
            </h3>
            <div className="space-y-2">
              {analytics.time_slot_activity && analytics.time_slot_activity.length > 0 ? (
                <>
                  <TimeSlot hour="00-06" activity={analytics.time_slot_activity[0]} />
                  <TimeSlot hour="06-09" activity={analytics.time_slot_activity[1]} />
                  <TimeSlot hour="09-12" activity={analytics.time_slot_activity[2]} />
                  <TimeSlot hour="12-15" activity={analytics.time_slot_activity[3]} />
                  <TimeSlot hour="15-18" activity={analytics.time_slot_activity[4]} highlight={analytics.time_slot_activity[4] === Math.max(...analytics.time_slot_activity)} />
                  <TimeSlot hour="18-21" activity={analytics.time_slot_activity[5]} />
                  <TimeSlot hour="21-24" activity={analytics.time_slot_activity[6]} />
                </>
              ) : (
                <p className="text-gray-400 text-center py-4">Henüz aktivite verisi yok</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition"></div>
        <div className="relative bg-slate-900/80 backdrop-blur-xl border border-indigo-500/20 rounded-2xl p-6">
          <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
            <Globe className="w-7 h-7 text-indigo-400" />
            🌍 Coğrafi Dağılım (İsveç)
          </h3>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📍</div>
            <p className="text-gray-400 text-lg mb-2">Henüz coğrafi veri bulunmuyor</p>
            <p className="text-gray-500 text-sm">Kullanıcılar kayıt oldukça şehir bazlı dağılım burada görünecek</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Analytics Helper Components
function MetricCard({ title, value, change, positive, icon, color }) {
  const colors = {
    green: 'from-green-500 to-emerald-500',
    blue: 'from-blue-500 to-cyan-500',
    purple: 'from-purple-500 to-pink-500',
    yellow: 'from-yellow-500 to-orange-500'
  };

  return (
    <div className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${colors[color]} rounded-2xl blur opacity-30 group-hover:opacity-60 transition`}></div>
      <div className="relative bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:scale-105 transition-all">
        <div className="text-4xl mb-3">{icon}</div>
        <div className="text-sm text-gray-400 mb-1">{title}</div>
        <div className="text-3xl font-black text-white mb-2">{value}</div>
        <div className={`text-sm font-bold ${positive ? 'text-green-400' : 'text-red-400'}`}>
          {positive ? '↗' : '↘'} {change}
        </div>
      </div>
    </div>
  );
}

function RevenueBar({ day, value, max, highlight }) {
  const percentage = (value / max) * 100;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-400 w-8">{day}</span>
      <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
        <div 
          className={`h-8 rounded-full transition-all duration-1000 flex items-center justify-end pr-3 ${
            highlight 
              ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
              : 'bg-gradient-to-r from-green-500/50 to-emerald-500/50'
          }`}
          style={{ width: `${percentage}%` }}
        >
          <span className="text-xs font-bold text-white">{value} SEK</span>
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ label, value, icon, color }) {
  const colors = {
    green: 'bg-green-500/10 border-green-500/20 text-green-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
    gray: 'bg-gray-500/10 border-gray-500/20 text-gray-400'
  };

  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border ${colors[color]}`}>
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <span className="text-white font-semibold">{label}</span>
      </div>
      <span className="text-2xl font-black">{value}</span>
    </div>
  );
}

// Clickable version for user management
function ClickableActivityItem({ label, value, icon, color, onClick }) {
  const colors = {
    green: 'bg-green-500/10 border-green-500/20 text-green-400 hover:bg-green-500/20',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400 hover:bg-blue-500/20',
    yellow: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20',
    gray: 'bg-gray-500/10 border-gray-500/20 text-gray-400 hover:bg-gray-500/20'
  };

  return (
    <div 
      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${colors[color]}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <span className="text-white font-semibold">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-2xl font-black">{value}</span>
        <Eye className="w-5 h-5 opacity-50" />
      </div>
    </div>
  );
}

// User Management Modal
function UserManagementModal({ isOpen, onClose, users, type, onUserAction, actionLoading, onViewAsUser }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!isOpen) return null;
  
  const getTitle = () => {
    switch(type) {
      case 'premium': return '👑 Premium Üyeler';
      case 'free': return '⭐ Ücretsiz Üyeler';
      case 'recent': return '📝 Son 24 Saat Kayıtlar';
      default: return '👥 Tüm Kullanıcılar';
    }
  };
  
  const filteredUsers = users.filter(u => 
    (u.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">{getTitle()}</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <XCircle className="w-6 h-6 text-gray-400" />
          </button>
        </div>
        
        {/* Search */}
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="İsim veya e-posta ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500"
            />
          </div>
        </div>
        
        {/* User List */}
        <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              Kullanıcı bulunamadı
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div 
                key={user.id || user.user_id}
                className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                        {(user.name || user.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-white font-semibold truncate">{user.name || 'İsimsiz'}</div>
                        <div className="text-gray-400 text-sm truncate">{user.email}</div>
                      </div>
                    </div>
                    
                    {/* Status badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {user.is_premium ? (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">👑 Premium</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">⭐ Ücretsiz</span>
                      )}
                      {user.account_status === 'blocked' && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">🚫 Engelli</span>
                      )}
                      {user.account_status === 'active' && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">✅ Aktif</span>
                      )}
                    </div>
                    
                    {/* Details */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                      <div>📅 Kayıt: {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</div>
                      {user.premium_until && (
                        <div>⏰ Premium Bitiş: {new Date(user.premium_until).toLocaleDateString('tr-TR')}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {user.is_premium ? (
                      <button
                        onClick={() => onUserAction(user.id || user.user_id, 'remove_premium')}
                        disabled={actionLoading === (user.id || user.user_id)}
                        className="px-3 py-1.5 bg-gray-500/20 hover:bg-gray-500/40 text-gray-300 text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === (user.id || user.user_id) ? '...' : 'Premium Kaldır'}
                      </button>
                    ) : (
                      <button
                        onClick={() => onUserAction(user.id || user.user_id, 'make_premium')}
                        disabled={actionLoading === (user.id || user.user_id)}
                        className="px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/40 text-yellow-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === (user.id || user.user_id) ? '...' : 'Premium Yap'}
                      </button>
                    )}
                    
                    {user.account_status === 'blocked' ? (
                      <button
                        onClick={() => onUserAction(user.id || user.user_id, 'activate')}
                        disabled={actionLoading === (user.id || user.user_id)}
                        className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/40 text-green-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === (user.id || user.user_id) ? '...' : 'Aktifleştir'}
                      </button>
                    ) : (
                      <button
                        onClick={() => onUserAction(user.id || user.user_id, 'block')}
                        disabled={actionLoading === (user.id || user.user_id)}
                        className="px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/40 text-orange-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === (user.id || user.user_id) ? '...' : 'Engelle'}
                      </button>
                    )}
                    
                    <button
                      onClick={() => onUserAction(user.id || user.user_id, 'delete')}
                      disabled={actionLoading === (user.id || user.user_id)}
                      className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === (user.id || user.user_id) ? '...' : '🗑️ Sil'}
                    </button>
                    
                    {/* View as User Button */}
                    <button
                      onClick={() => onViewAsUser(user)}
                      className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 text-xs rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      <span>Kullanıcı Olarak Gör</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-between items-center">
          <div className="text-gray-400 text-sm">
            Toplam: {filteredUsers.length} kullanıcı
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function DifficultyCard({ level, count, percentage, accuracy, icon, color }) {
  const colors = {
    green: 'from-green-500 to-emerald-500',
    yellow: 'from-yellow-500 to-orange-500',
    red: 'from-red-500 to-pink-500'
  };

  return (
    <div className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${colors[color]} rounded-xl blur opacity-30 group-hover:opacity-60 transition`}></div>
      <div className="relative bg-slate-900/80 border border-white/10 rounded-xl p-6 text-center">
        <div className="text-4xl mb-3">{icon}</div>
        <div className="text-2xl font-black text-white mb-2">{level}</div>
        <div className="text-3xl font-black text-white mb-2">{count}</div>
        <div className="text-sm text-gray-400 mb-3">{percentage} toplam</div>
        <div className={`inline-block px-4 py-2 rounded-lg bg-gradient-to-r ${colors[color]} text-white font-bold`}>
          {accuracy} doğruluk
        </div>
      </div>
    </div>
  );
}

function CategoryPerformance({ name, completed, accuracy, color }) {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-red-500',
    teal: 'from-teal-500 to-cyan-500'
  };

  return (
    <div className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white font-bold">{name}</span>
        <span className="text-gray-400">{completed.toLocaleString()} çözüm</span>
      </div>
      <div className="relative w-full bg-white/10 rounded-full h-6 overflow-hidden">
        <div 
          className={`h-6 rounded-full bg-gradient-to-r ${colors[color]} flex items-center justify-end pr-3 transition-all duration-1000`}
          style={{ width: `${accuracy}%` }}
        >
          <span className="text-xs font-bold text-white">{accuracy}%</span>
        </div>
      </div>
    </div>
  );
}

function TimeSlot({ hour, activity, highlight }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-400 w-16">{hour}</span>
      <div className="flex-1 bg-white/5 rounded-full h-6 overflow-hidden">
        <div 
          className={`h-6 rounded-full transition-all duration-1000 ${
            highlight 
              ? 'bg-gradient-to-r from-cyan-400 to-blue-500' 
              : 'bg-gradient-to-r from-cyan-500/50 to-blue-500/50'
          }`}
          style={{ width: `${activity}%` }}
        ></div>
      </div>
      <span className="text-sm font-bold text-white w-12">{activity}%</span>
    </div>
  );
}

// USERS TAB
function UsersTab({ users, fetchUsers, handleViewAsCustomer, adminPassword }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, premium, free, blocked, new
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, name, lastLogin
  const [actionLoading, setActionLoading] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalUser, setModalUser] = useState(null);

  // Filter and sort users
  let filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    switch (filterType) {
      case 'premium':
        return user.is_premium || user.subscription_status === 'active';
      case 'free':
        return !user.is_premium && user.subscription_status !== 'active';
      case 'blocked':
        return user.account_status === 'blocked' || user.account_status === 'suspended';
      case 'new':
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 7);
        return new Date(user.created_at) > yesterday;
      default:
        return true;
    }
  });

  // Sort users
  filteredUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'lastLogin':
        return new Date(b.last_login || 0) - new Date(a.last_login || 0);
      default:
        return 0;
    }
  });

  // User actions
  const handleBlockUser = async (user) => {
    const isBlocked = user.account_status === 'blocked';
    const action = isBlocked ? 'unblock' : 'block';
    
    if (!window.confirm(`${user.name || user.email} kullanıcısını ${isBlocked ? 'engelini kaldırmak' : 'engellemek'} istediğinize emin misiniz?`)) return;
    
    setActionLoading(user.user_id);
    try {
      const userIdentifier = user.user_id || user.id || user.email;
      await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/status`, 
        { status: isBlocked ? 'active' : 'blocked' },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'İşlem başarısız'));
    }
    setActionLoading(null);
  };

  // Premium Modal State
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumTargetUser, setPremiumTargetUser] = useState(null);
  const [premiumDuration, setPremiumDuration] = useState(30);
  const [customDuration, setCustomDuration] = useState('');

  const handleTogglePremium = async (user) => {
    const isPremium = user.is_premium || user.subscription_status === 'active';
    
    if (isPremium) {
      // Remove premium directly
      if (!window.confirm(`${user.name || user.email} kullanıcısından premium kaldırmak istediğinize emin misiniz?`)) return;
      
      setActionLoading(user.user_id);
      try {
        const userIdentifier = user.user_id || user.id || user.email;
        await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/premium`, 
          { is_premium: false },
          { headers: { 'X-Admin-Password': adminPassword } }
        );
        alert('✅ Premium kaldırıldı!');
        fetchUsers();
      } catch (error) {
        alert('Hata: ' + (error.response?.data?.detail || 'İşlem başarısız'));
      }
      setActionLoading(null);
    } else {
      // Show premium duration modal
      setPremiumTargetUser(user);
      setPremiumDuration(30);
      setCustomDuration('');
      setShowPremiumModal(true);
    }
  };

  const handleGrantPremium = async () => {
    if (!premiumTargetUser) return;
    
    const duration = premiumDuration === 'custom' ? parseInt(customDuration) : premiumDuration;
    if (!duration || duration <= 0) {
      alert('Geçerli bir süre girin!');
      return;
    }
    
    setActionLoading(premiumTargetUser.user_id);
    try {
      const userIdentifier = premiumTargetUser.user_id || premiumTargetUser.id || premiumTargetUser.email;
      const response = await axios.put(`${API}/admin/users/${encodeURIComponent(userIdentifier)}/premium`, 
        { is_premium: true, duration_days: duration },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      alert(`✅ ${response.data.message}`);
      setShowPremiumModal(false);
      setPremiumTargetUser(null);
      fetchUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'İşlem başarısız'));
    }
    setActionLoading(null);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`⚠️ DİKKAT!\n\n${user.name || user.email} kullanıcısını SİLMEK istediğinize emin misiniz?\n\nBu işlem GERİ ALINAMAZ!`)) return;
    if (!window.confirm(`Son kez onaylayın: ${user.email} silinsin mi?`)) return;
    
    setActionLoading(user.user_id);
    try {
      const userIdentifier = user.user_id || user.id || user.email;
      await axios.delete(`${API}/admin/users/${encodeURIComponent(userIdentifier)}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Silme işlemi başarısız'));
    }
    setActionLoading(null);
  };

  const handleSendEmail = async (user) => {
    const subject = prompt('E-posta konusu:', 'Ehliyet.nu - Bilgilendirme');
    if (!subject) return;
    
    const message = prompt('E-posta mesajı:');
    if (!message) return;
    
    setActionLoading(user.user_id);
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(user.email)}/send-email`, 
        { subject, message },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      alert('✅ E-posta gönderildi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'E-posta gönderilemedi'));
    }
    setActionLoading(null);
  };

  const openUserDetails = (user) => {
    setModalUser(user);
    setShowUserModal(true);
  };

  // Calculate user stats
  const getUserStats = (user) => {
    const loginCount = user.login_count || 1;
    const questionsAnswered = user.questions_answered || user.free_questions_used || 0;
    const lastActive = user.last_login ? new Date(user.last_login) : null;
    const daysSinceLastLogin = lastActive ? Math.floor((new Date() - lastActive) / (1000 * 60 * 60 * 24)) : null;
    
    return { loginCount, questionsAnswered, lastActive, daysSinceLastLogin };
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" />
              Kullanıcı Yönetimi
            </h3>
            <div className="text-gray-400 text-sm mt-1">
              Toplam {users.length} kullanıcı • {users.filter(u => u.is_premium).length} premium
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={fetchUsers}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Yenile
            </button>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-white">{users.length}</div>
            <div className="text-xs text-gray-400">Toplam</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{users.filter(u => u.is_premium || u.subscription_status === 'active').length}</div>
            <div className="text-xs text-gray-400">Premium</div>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-blue-400">{users.filter(u => {
              const week = new Date();
              week.setDate(week.getDate() - 7);
              return new Date(u.created_at) > week;
            }).length}</div>
            <div className="text-xs text-gray-400">Bu Hafta</div>
          </div>
          <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-red-400">{users.filter(u => u.account_status === 'blocked').length}</div>
            <div className="text-xs text-gray-400">Engelli</div>
          </div>
        </div>
        
        {/* Search & Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Kullanıcı ara (isim, email, ID)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Tümü</option>
            <option value="premium">👑 Premium</option>
            <option value="free">⭐ Ücretsiz</option>
            <option value="blocked">🚫 Engelli</option>
            <option value="new">🆕 Yeni (7 gün)</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="newest">En Yeni</option>
            <option value="oldest">En Eski</option>
            <option value="name">İsme Göre</option>
            <option value="lastLogin">Son Giriş</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Kullanıcı</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Telefon</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Şifre</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Durum</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Premium</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Kayıt</th>
                <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Son Giriş</th>
                <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredUsers.map((user) => {
                const stats = getUserStats(user);
                const isBlocked = user.account_status === 'blocked';
                const isPremium = user.is_premium || user.subscription_status === 'active';
                
                return (
                  <tr
                    key={user.user_id || user.id}
                    data-testid={`user-row-${user.email}`}
                    className={`hover:bg-white/5 transition-colors cursor-pointer ${isBlocked ? 'opacity-60' : ''}`}
                    onClick={() => openUserDetails(user)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          isPremium ? 'bg-gradient-to-br from-yellow-500 to-orange-500' : 
                          isBlocked ? 'bg-gray-600' :
                          'bg-gradient-to-br from-purple-500 to-pink-500'
                        }`}>
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
                          ) : (
                            user.name?.charAt(0).toUpperCase() || 'U'
                          )}
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm flex items-center gap-1">
                            {user.name || 'İsimsiz'}
                            {user.is_admin && <span className="text-xs bg-red-500/20 text-red-300 px-1.5 rounded">Admin</span>}
                          </div>
                          <div className="text-gray-400 text-xs">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-300">{user.phone || <span className="text-gray-600">-</span>}</div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <PasswordCell user={user} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          isBlocked ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'
                        }`}>
                          {isBlocked ? 'Engelli' : 'Aktif'}
                        </span>
                        {user.auth_provider && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">
                            {user.auth_provider === 'google' ? 'Google' : 'Email'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          isPremium ? 'bg-yellow-500/20 text-yellow-300' : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {isPremium ? 'Premium' : 'Free'}
                        </span>
                        {isPremium && user.premium_until && (
                          <div className="text-gray-500 text-xs mt-1">
                            {new Date(user.premium_until).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                        {isPremium && user.premium_package && (
                          <div className="text-gray-500 text-xs">{user.premium_package}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-300">
                        {new Date(user.created_at).toLocaleDateString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-300">
                        {user.last_login ? (
                          <>
                            <div>{new Date(user.last_login).toLocaleDateString('tr-TR')}</div>
                            <div className="text-gray-500 text-xs">
                              {stats.daysSinceLastLogin === 0 ? 'Bugün' : `${stats.daysSinceLastLogin}g önce`}
                            </div>
                          </>
                        ) : <span className="text-gray-600">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleViewAsCustomer(user)}
                          className="p-1.5 hover:bg-purple-500/20 rounded-lg text-purple-400 transition-colors"
                          title="Müşteri Olarak Gör"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleTogglePremium(user)}
                          disabled={actionLoading === user.user_id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isPremium ? 'hover:bg-yellow-500/20 text-yellow-400' : 'hover:bg-green-500/20 text-green-400'
                          }`}
                          title={isPremium ? 'Premium Kaldır' : 'Premium Ver'}
                        >
                          <Award className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleBlockUser(user)}
                          disabled={actionLoading === user.user_id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isBlocked ? 'hover:bg-green-500/20 text-green-400' : 'hover:bg-orange-500/20 text-orange-400'
                          }`}
                          title={isBlocked ? 'Engeli Kaldır' : 'Engelle'}
                        >
                          {isBlocked ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={actionLoading === user.user_id}
                          className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center text-gray-400 py-12">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Kullanıcı bulunamadı</p>
          </div>
        )}
      </div>

      {/* End of Users Table */}

      {/* User Detail Modal */}
      {showUserModal && modalUser && (
        <UserDetailModal 
          user={modalUser}
          onClose={() => setShowUserModal(false)}
          onViewAsCustomer={handleViewAsCustomer}
          onTogglePremium={handleTogglePremium}
          onBlockUser={handleBlockUser}
          onSendEmail={handleSendEmail}
          onDeleteUser={handleDeleteUser}
          fetchUsers={fetchUsers}
          adminPassword={adminPassword}
        />
      )}

      {/* Premium Duration Modal */}
      {showPremiumModal && premiumTargetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setShowPremiumModal(false)}>
          <div className="bg-slate-900 rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Premium Ver</h3>
                    <p className="text-white/70 text-sm">{premiumTargetUser.name || premiumTargetUser.email}</p>
                  </div>
                </div>
                <button onClick={() => setShowPremiumModal(false)} className="p-2 hover:bg-white/20 rounded-lg text-white">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-6">
              <p className="text-white/70 text-center">Premium üyelik süresini seçin:</p>
              
              {/* Duration Options */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 1, label: '1 Gün', emoji: '⚡' },
                  { value: 7, label: '1 Hafta', emoji: '📅' },
                  { value: 30, label: '1 Ay', emoji: '🗓️' },
                  { value: 90, label: '3 Ay', emoji: '📆' },
                  { value: 180, label: '6 Ay', emoji: '🎯' },
                  { value: 365, label: '1 Yıl', emoji: '👑' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setPremiumDuration(option.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      premiumDuration === option.value
                        ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400'
                        : 'border-white/20 text-white hover:border-white/40'
                    }`}
                  >
                    <div className="text-2xl mb-1">{option.emoji}</div>
                    <div className="font-bold">{option.label}</div>
                  </button>
                ))}
              </div>
              
              {/* Custom Duration */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => setPremiumDuration('custom')}
                  className={`w-full p-3 rounded-xl border-2 mb-3 transition-all ${
                    premiumDuration === 'custom'
                      ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                      : 'border-white/20 text-white hover:border-white/40'
                  }`}
                >
                  🔧 Özel Süre Belirle
                </button>
                
                {premiumDuration === 'custom' && (
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      value={customDuration}
                      onChange={(e) => setCustomDuration(e.target.value)}
                      placeholder="Gün sayısı"
                      className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 text-center text-lg"
                      min="1"
                      max="3650"
                    />
                    <span className="text-white/60">gün</span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold"
                >
                  İptal
                </button>
                <button
                  onClick={handleGrantPremium}
                  disabled={actionLoading || (premiumDuration === 'custom' && !customDuration)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Award className="w-5 h-5" />
                      Premium Ver
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Password Cell - inline password visibility toggle
function PasswordCell({ user }) {
  const [visible, setVisible] = useState(false);
  const pwd = user.password_plain;
  const isGoogle = user.auth_provider === 'google';
  
  if (isGoogle) return <span className="text-gray-500 text-xs italic">Google</span>;
  if (!pwd) return <span className="text-gray-600 text-xs">-</span>;
  
  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-gray-300 font-mono">{visible ? pwd : '••••••'}</span>
      <button
        onClick={() => setVisible(!visible)}
        className="p-1 hover:bg-white/10 rounded text-gray-500 hover:text-gray-300"
        title={visible ? 'Gizle' : 'Göster'}
      >
        {visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

// User Detail Modal - comprehensive user view with password management
function UserDetailModal({ user, onClose, onViewAsCustomer, onTogglePremium, onBlockUser, onSendEmail, onDeleteUser, fetchUsers, adminPassword }) {
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const isPremium = user.is_premium || user.subscription_status === 'active';
  const isBlocked = user.account_status === 'blocked';

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Şifre en az 6 karakter olmalıdır');
      return;
    }
    setChangingPassword(true);
    try {
      const uid = user.user_id || user.id || user.email;
      await axios.post(`${API}/admin/users/${encodeURIComponent(uid)}/reset-password`,
        { new_password: newPassword },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      alert('Şifre başarıyla değiştirildi!');
      setNewPassword('');
      setShowPasswordChange(false);
      fetchUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Şifre değiştirilemedi'));
    }
    setChangingPassword(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold ${
                isPremium ? 'bg-yellow-500' : 'bg-white/20'
              }`}>
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
                ) : (
                  user.name?.charAt(0).toUpperCase() || 'U'
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{user.name || 'İsimsiz'}</h3>
                <p className="text-white/70 text-sm">{user.email}</p>
                {user.phone && <p className="text-white/50 text-xs">{user.phone}</p>}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { onViewAsCustomer(user); onClose(); }} className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" /> Müşteri Görünümü
            </button>
            <button onClick={() => onTogglePremium(user)} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${isPremium ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'} text-white`}>
              <Award className="w-4 h-4" /> {isPremium ? 'Premium Kaldır' : 'Premium Ver'}
            </button>
            <button onClick={() => onBlockUser(user)} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${isBlocked ? 'bg-green-500 hover:bg-green-600' : 'bg-orange-500 hover:bg-orange-600'} text-white`}>
              {isBlocked ? 'Engeli Kaldır' : 'Engelle'}
            </button>
            <button onClick={() => onSendEmail(user)} className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" /> E-posta
            </button>
          </div>

          {/* User Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">E-posta</div>
              <div className="text-white text-sm font-medium break-all">{user.email}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">Telefon</div>
              <div className="text-white text-sm">{user.phone || <span className="text-gray-600">Kayıtlı değil</span>}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">Giriş Yöntemi</div>
              <div className="text-white text-sm">{user.auth_provider === 'google' ? 'Google' : 'E-posta/Şifre'}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">Üyelik</div>
              <div className={`text-sm font-bold ${isPremium ? 'text-yellow-400' : 'text-gray-300'}`}>
                {isPremium ? 'Premium' : 'Ücretsiz'}
              </div>
              {isPremium && user.premium_until && (
                <div className="text-gray-500 text-xs">Bitiş: {new Date(user.premium_until).toLocaleDateString('tr-TR')}</div>
              )}
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">Kayıt Tarihi</div>
              <div className="text-white text-sm">{new Date(user.created_at).toLocaleString('tr-TR')}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-gray-400 text-xs mb-1">Son Giriş</div>
              <div className="text-white text-sm">{user.last_login ? new Date(user.last_login).toLocaleString('tr-TR') : 'Hiç'}</div>
            </div>
          </div>

          {/* Password Section */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Şifre Yönetimi
            </h4>
            
            {user.auth_provider === 'google' ? (
              <p className="text-gray-400 text-sm">Bu kullanıcı Google ile kayıt olmuş. Şifresi yok.</p>
            ) : (
              <div className="space-y-3">
                {/* Current Password */}
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm w-24">Mevcut Şifre:</span>
                  <span className="text-white font-mono text-sm">{showPassword ? (user.password_plain || 'Kayıtlı değil') : '••••••••'}</span>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Change Password */}
                {!showPasswordChange ? (
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm font-medium"
                    data-testid="change-password-btn"
                  >
                    Şifre Değiştir
                  </button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Yeni şifre (min 6 karakter)"
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="new-password-input"
                    />
                    <button
                      onClick={handleResetPassword}
                      disabled={changingPassword || newPassword.length < 6}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                      data-testid="save-password-btn"
                    >
                      {changingPassword ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                      onClick={() => { setShowPasswordChange(false); setNewPassword(''); }}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 text-gray-400 rounded-lg text-sm"
                    >
                      İptal
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Activity Stats */}
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-5">
            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" /> Aktivite
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{user.login_count || 1}</div>
                <div className="text-gray-400 text-xs">Giriş</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{user.questions_answered || user.free_questions_used || 0}</div>
                <div className="text-gray-400 text-xs">Soru</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{user.correct_answers || 0}</div>
                <div className="text-gray-400 text-xs">Doğru</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">{user.exams_completed || 0}</div>
                <div className="text-gray-400 text-xs">Sınav</div>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="border border-red-500/30 rounded-xl p-4">
            <h4 className="text-red-400 font-bold mb-2 text-sm">Tehlikeli Bölge</h4>
            <button
              onClick={() => { onDeleteUser(user); onClose(); }}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center gap-2 text-sm"
            >
              <Trash2 className="w-4 h-4" /> Kalıcı Olarak Sil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// QUESTIONS TAB
function QuestionsTab({ questions, stats, fetchQuestions, categories = [], initialEditingQuestion = null, clearEditingQuestion, initialSearchTerm = '', onSearchTermUsed }) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100); // 100 soru per sayfa
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [mediaFilter, setMediaFilter] = useState('all'); // all, image, video, none
  const [sortBy, setSortBy] = useState('id_asc'); // default, newest, oldest, recently_edited, id_asc, id_desc
  const [deletingId, setDeletingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [hoverPreview, setHoverPreview] = useState(null); // Hover preview için state
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 }); // Hover popup pozisyonu
  const [renumbering, setRenumbering] = useState(false); // Yeniden numaralandırma state

  // Soruları yeniden numaralandır
  const handleRenumberQuestions = async () => {
    if (!window.confirm('Tüm sorular 1\'den başlayarak yeniden numaralandırılacak. Devam etmek istiyor musunuz?')) {
      return;
    }
    
    setRenumbering(true);
    try {
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.post(`${BACKEND_URL}/api/admin/renumber-questions`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      
      alert(`✅ ${response.data.message}\nToplam: ${response.data.total_questions} soru`);
      fetchQuestions(); // Soruları yeniden yükle
    } catch (error) {
      console.error('Renumber error:', error);
      alert('❌ Numaralandırma hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRenumbering(false);
    }
  };

  // Update searchTerm when initialSearchTerm changes
  useEffect(() => {
    if (initialSearchTerm && initialSearchTerm !== searchTerm) {
      setSearchTerm(initialSearchTerm);
      setCurrentPage(1); // Reset to first page
      if (onSearchTermUsed) onSearchTermUsed();
    }
  }, [initialSearchTerm]);

  // Format question number: "Q-00259" -> "Soru 259" or show index
  const formatQuestionNumber = (questionNumber, index) => {
    if (!questionNumber) return `Soru ${index}`;
    // Extract number from Q-00259 format
    const match = questionNumber.match(/Q-(\d+)/i);
    if (match) {
      const num = parseInt(match[1], 10);
      return `Soru ${num}`;
    }
    // If not Q-format, try to extract any number
    const numMatch = questionNumber.match(/\d+/);
    if (numMatch) {
      return `Soru ${parseInt(numMatch[0], 10)}`;
    }
    return `Soru ${index}`;
  };

  // Handle initial editing question from parent (for reported questions)
  useEffect(() => {
    if (initialEditingQuestion) {
      setEditingQuestion(initialEditingQuestion);
      setShowEditModal(true);
      if (clearEditingQuestion) {
        clearEditingQuestion();
      }
    }
  }, [initialEditingQuestion, clearEditingQuestion]);

  useEffect(() => {
    if (questions.length === 0) {
      fetchQuestions();
    }
  }, []);

  // Delete question handler
  const handleDeleteQuestion = async (questionId, questionNumber) => {
    if (!window.confirm(`"${questionNumber}" numaralı soruyu silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }
    
    try {
      setDeletingId(questionId);
      const adminPassword = localStorage.getItem('admin_password');
      await axios.delete(`${API}/admin/questions/${questionId}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert('✅ Soru başarıyla silindi!');
      await fetchQuestions();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('❌ Soru silinemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingId(null);
    }
  };

  // Copy question handler
  const handleCopyQuestion = async (questionId, questionNumber) => {
    if (!window.confirm(`"${questionNumber}" numaralı soruyu kopyalamak istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      setCopyingId(questionId);
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.post(`${API}/admin/questions/${questionId}/copy`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert(`✅ Soru kopyalandı!\n\nYeni soru numarası: ${response.data.new_question_number}`);
      await fetchQuestions();
    } catch (error) {
      console.error('Kopyalama hatası:', error);
      alert('❌ Soru kopyalanamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCopyingId(null);
    }
  };

  // Kategorileri al (unique)
  // Categories now come from props
  const categoryIds = [...new Set(questions.map(q => q.category || q.category_id).filter(Boolean))];

  // Medya türüne göre soru sayılarını hesapla (filtre sayıları için)
  const mediaCounts = React.useMemo(() => {
    const keywords = ['resim', 'tabela', 'işaret', 'aşağıdaki', 'hangisi', 'görseldeki', 'fotoğraf', 'şekil', 'sembol', 'simge'];
    
    let hasMedia = 0, onlyImage = 0, onlyVideo = 0, noMedia = 0, missingImage = 0;
    
    questions.forEach(q => {
      const hasImg = !!q.image_url;
      const hasVid = !!q.video_url;
      
      if (hasImg || hasVid) hasMedia++;
      if (hasImg && !hasVid) onlyImage++;
      if (hasVid && !hasImg) onlyVideo++;
      if (!hasImg && !hasVid) noMedia++;
      
      // Resim eksik kontrolü
      const questionText = (q.question_text || '').toLowerCase();
      const hasKeyword = keywords.some(kw => questionText.includes(kw));
      if (hasKeyword && !hasImg) missingImage++;
    });
    
    return {
      all: questions.length,
      has_media: hasMedia,
      image: onlyImage,
      video: onlyVideo,
      none: noMedia,
      missing_image: missingImage
    };
  }, [questions]);

  const filteredQuestions = questions.filter(q => {
    // Arama filtresi - ID, question_text, question_number alanlarında ara
    const cleanSearch = searchTerm.replace(/^#\s*/, '').toLowerCase().trim();
    
    // Extract just the number from search term (e.g., "Q-01432" -> "01432", "1432" -> "1432")
    const searchNumber = cleanSearch.replace(/^q[-\s]*/i, '').replace(/^0+/, '');
    const qNumber = (q.question_number || '').toLowerCase().replace(/^q[-\s]*/i, '').replace(/^0+/, '');
    
    const matchesSearch = !searchTerm || 
      q.id?.toLowerCase().includes(cleanSearch) ||
      q.question_text?.toLowerCase().includes(cleanSearch) ||
      q.question_number?.toLowerCase().includes(cleanSearch) ||
      qNumber.includes(searchNumber); // Match by just the number part
    
    // Kategori filtresi
    const matchesCategory = categoryFilter === 'all' || 
      q.category === categoryFilter || 
      q.category_id === categoryFilter;
    
    // Zorluk filtresi
    const matchesDifficulty = difficultyFilter === 'all' || 
      q.difficulty === difficultyFilter;
    
    // Medya filtresi (Resim/Video)
    let matchesMedia = true;
    if (mediaFilter === 'image') {
      matchesMedia = !!q.image_url;
    } else if (mediaFilter === 'video') {
      matchesMedia = !!q.video_url;
    } else if (mediaFilter === 'none') {
      matchesMedia = !q.image_url && !q.video_url;
    } else if (mediaFilter === 'missing_image') {
      // Soru metni resim gerektiriyor ama resim yok
      // "resim", "tabelası", "işareti", "hangisi", "aşağıdaki" gibi kelimeler içeren ama image_url olmayan sorular
      const keywords = ['resim', 'tabela', 'işaret', 'aşağıdaki', 'hangisi', 'görseldeki', 'fotoğraf', 'şekil', 'sembol', 'simge'];
      const questionText = (q.question_text || '').toLowerCase();
      const hasKeyword = keywords.some(kw => questionText.includes(kw));
      matchesMedia = hasKeyword && !q.image_url;
    } else if (mediaFilter === 'has_media') {
      // Resim VEYA video olan sorular
      matchesMedia = !!q.image_url || !!q.video_url;
    }
    
    return matchesSearch && matchesCategory && matchesDifficulty && matchesMedia;
  });

  // Sıralama
  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    // Helper function to extract number from question_number (Q-00001 -> 1)
    const extractNum = (q) => {
      const qNum = q.question_number || '';
      const match = qNum.match(/Q-(\d+)/i);
      if (match) return parseInt(match[1], 10);
      const numMatch = qNum.match(/\d+/);
      if (numMatch) return parseInt(numMatch[0], 10);
      return 99999; // Put questions without number at end
    };
    
    if (sortBy === 'newest') {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    } else if (sortBy === 'oldest') {
      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    } else if (sortBy === 'recently_edited') {
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    } else if (sortBy === 'id_asc') {
      // Soru numarasına göre artan (Soru 1, Soru 2, Soru 3...)
      return extractNum(a) - extractNum(b);
    } else if (sortBy === 'id_desc') {
      // Soru numarasına göre azalan (Soru 2000, Soru 1999...)
      return extractNum(b) - extractNum(a);
    }
    return 0; // default - orijinal sıra
  });

  // Pagination
  const totalPages = Math.ceil(sortedQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuestions = sortedQuestions.slice(startIndex, endIndex);

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setShowEditModal(true);
  };

  const handleSaveQuestion = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      
      // Use question_number or id (try multiple identifiers)
      const questionIdentifier = editingQuestion.id || editingQuestion.question_number || editingQuestion._id;
      
      if (!questionIdentifier) {
        console.error('Missing question identifier:', editingQuestion);
        alert('❌ Soru ID bulunamadı! Lütfen sayfayı yenileyip tekrar deneyin.');
        return;
      }
      
      console.log('Kaydediliyor:', editingQuestion, 'ID:', questionIdentifier);
      
      // Prepare data - ensure all fields are present
      const updateData = {
        question_text: editingQuestion.question_text || '',
        choices: editingQuestion.choices || [],
        correct_answer: editingQuestion.correct_answer || '',
        explanation: editingQuestion.explanation || '',
        difficulty: editingQuestion.difficulty || 'medium',
        category_id: editingQuestion.category_id || null,
        // Send actual URL values, not null for empty
        image_url: editingQuestion.image_url || '',
        image_urls: editingQuestion.image_urls || [],
        video_url: editingQuestion.video_url || ''
      };
      
      console.log('📤 Sending update data:', updateData);
      
      const response = await axios.put(
        `${API}/admin/questions/${questionIdentifier}`,
        updateData,
        {
          headers: { 'X-Admin-Password': adminPassword }
        }
      );
      
      console.log('✅ Kaydedildi:', response.data);
      alert('✅ Soru başarıyla güncellendi!');
      setShowEditModal(false);
      setEditingQuestion(null);
      await fetchQuestions();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      console.error('Error response:', error.response?.data);
      alert('❌ Soru güncellenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddQuestion = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      
      if (!editingQuestion.question_text || !editingQuestion.category_id) {
        alert('❌ Lütfen soru metni ve kategori girin!');
        return;
      }

      // Use the new manual question add endpoint
      const response = await axios.post(
        `${API}/questions/add`,
        {
          category_id: editingQuestion.category_id,
          custom_id: editingQuestion.custom_id || null,
          question_data: {
            question_text: editingQuestion.question_text,
            custom_id: editingQuestion.custom_id || null,
            choices: editingQuestion.choices || [
              { letter: 'A', text: '' },
              { letter: 'B', text: '' },
              { letter: 'C', text: '' },
              { letter: 'D', text: '' }
            ],
            correct_answer: editingQuestion.correct_answer || 'A',
            explanation: editingQuestion.explanation || '',
            difficulty: editingQuestion.difficulty || 'medium',
            image_url: editingQuestion.image_url || '',
            image_urls: editingQuestion.image_urls || [],
            video_url: editingQuestion.video_url || ''
          }
        },
        {
          headers: { 'X-Admin-Password': adminPassword }
        }
      );

      alert(`✅ Yeni soru başarıyla eklendi!\nSoru ID: ${response.data.question_id}`);
      setShowAddModal(false);
      setEditingQuestion(null);
      await fetchQuestions();
    } catch (error) {
      console.error('Ekleme hatası:', error);
      alert('❌ Soru eklenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleFileUpload = async (file, type, addToMultiple = false) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.post(`${API}/admin/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Password': adminPassword
        }
      });

      if (type === 'image') {
        if (addToMultiple) {
          // Add to image_urls array
          const currentUrls = editingQuestion.image_urls || [];
          setEditingQuestion({ 
            ...editingQuestion, 
            image_urls: [...currentUrls, response.data.url] 
          });
        } else {
          // Set as primary image
          setEditingQuestion({ ...editingQuestion, image_url: response.data.url });
        }
      } else {
        setEditingQuestion({ ...editingQuestion, video_url: response.data.url });
      }
      alert('✅ Dosya yüklendi!');
    } catch (error) {
      alert('❌ Dosya yüklenemedi: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Remove image from image_urls array
  const removeMultipleImage = (indexToRemove) => {
    const currentUrls = editingQuestion.image_urls || [];
    setEditingQuestion({
      ...editingQuestion,
      image_urls: currentUrls.filter((_, index) => index !== indexToRemove)
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Soru Yönetimi</h3>
            <div className="text-gray-400 text-sm">
              Toplam: <span className="text-white font-bold">{questions.length}</span> soru • 
              Gösterilen: <span className="text-purple-300 font-bold">{sortedQuestions.length}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRenumberQuestions}
              disabled={renumbering}
              className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg"
              title="Tüm soruları 1'den başlayarak yeniden numaralandır"
            >
              {renumbering ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Numaralandırılıyor...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Yeniden Numaralandır
                </>
              )}
            </button>
            <button
              onClick={() => {
                setEditingQuestion({
                  custom_id: '',
                  question_text: '',
                  choices: [
                    { letter: 'A', text: '' },
                    { letter: 'B', text: '' },
                    { letter: 'C', text: '' },
                    { letter: 'D', text: '' }
                  ],
                  correct_answer: 'A',
                  explanation: '',
                  difficulty: 'medium',
                  image_url: null,
                  image_urls: [],
                  video_url: null,
                  category_id: ''
                });
                setShowAddModal(true);
              }}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Yeni Soru Ekle
            </button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Soru numarası veya metin ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Kategori Filtresi */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Kategori</label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Tüm Kategoriler</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name_turkish || cat.name}</option>
              ))}
            </select>
          </div>

          {/* Zorluk Filtresi */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Zorluk Seviyesi</label>
            <select
              value={difficultyFilter}
              onChange={(e) => {
                setDifficultyFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Tüm Seviyeler</option>
              <option value="easy">Kolay (Easy)</option>
              <option value="medium">Orta (Medium)</option>
              <option value="hard">Zor (Hard)</option>
            </select>
          </div>

          {/* Medya Filtresi (Resim/Video) */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Medya Türü</label>
            <select
              value={mediaFilter}
              onChange={(e) => {
                setMediaFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Tümü ({mediaCounts.all})</option>
              <option value="has_media">📷🎬 Resimli veya Videolu ({mediaCounts.has_media})</option>
              <option value="image">📷 Sadece Resimli ({mediaCounts.image})</option>
              <option value="video">🎬 Sadece Videolu ({mediaCounts.video})</option>
              <option value="none">📝 Sadece Metin ({mediaCounts.none})</option>
              <option value="missing_image">⚠️ Resim Eksik ({mediaCounts.missing_image})</option>
            </select>
          </div>

          {/* Sıralama */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sıralama</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="default">Varsayılan</option>
              <option value="newest">🆕 Son Eklenen</option>
              <option value="oldest">📅 İlk Eklenen</option>
              <option value="recently_edited">✏️ Son Düzenlenen</option>
              <option value="id_asc">🔢 No: Küçükten Büyüğe (Q-001→)</option>
              <option value="id_desc">🔢 No: Büyükten Küçüğe (→Q-001)</option>
            </select>
          </div>

          {/* Sayfa Başına Soru Sayısı */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sayfa Başına</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value={50}>50 soru</option>
              <option value={100}>100 soru</option>
              <option value={200}>200 soru</option>
              <option value={500}>500 soru</option>
            </select>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-3">
        {currentQuestions.map((question, index) => (
          <div
            key={question.id}
            className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-purple-500/50 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm font-bold">
                    {formatQuestionNumber(question.question_number, startIndex + index + 1)}
                  </span>
                  {(question.category || question.category_id) && (
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                      {question.category || question.category_id}
                    </span>
                  )}
                  {question.difficulty && (
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      question.difficulty === 'easy' ? 'bg-green-500/20 text-green-300' :
                      question.difficulty === 'hard' ? 'bg-red-500/20 text-red-300' :
                      'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {question.difficulty === 'easy' ? '🟢 Kolay' : 
                       question.difficulty === 'hard' ? '🔴 Zor' : 
                       '🟡 Orta'}
                    </span>
                  )}
                  {question.image_url && (
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Resim
                    </span>
                  )}
                  {question.video_url && (
                    <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded text-xs flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Video
                    </span>
                  )}
                </div>
                <p className="text-white line-clamp-2">{question.question_text}</p>
              </div>
              {/* Hover Preview Button - Hem hover hem tıklama çalışır */}
              <div 
                className="relative"
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoverPosition({ 
                    x: rect.left - 720, // Popup sola açılsın (büyük popup için)
                    y: Math.min(rect.top - 150, window.innerHeight - 720) // Ekran dışına çıkmasın
                  });
                  setHoverPreview(question);
                }}
                onMouseLeave={() => setHoverPreview(null)}
                onClick={() => {
                  setHoverPreview(null);
                  setEditingQuestion(question);
                  setShowPreviewModal(true);
                }}
              >
                <span className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/40 text-purple-300 rounded-lg text-sm cursor-pointer flex items-center gap-1.5 transition-all whitespace-nowrap">
                  <Eye className="w-4 h-4" />
                  Ön İzleme
                </span>
              </div>
              <button
                onClick={() => handleEdit(question)}
                className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-all"
                title="Düzenle"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleCopyQuestion(question.id, question.question_number)}
                disabled={copyingId === question.id}
                className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-all disabled:opacity-50"
                title="Kopyala"
              >
                {copyingId === question.id ? (
                  <RefreshCcw className="w-5 h-5 animate-spin" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => handleDeleteQuestion(question.id, question.question_number)}
                disabled={deletingId === question.id}
                className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all disabled:opacity-50"
                title="Sil"
              >
                {deletingId === question.id ? (
                  <RefreshCcw className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Hover Preview Popup - Fare ile "Ön İzleme" üzerine gelince görünür - 2x BÜYÜK */}
      {hoverPreview && (
        <div 
          className="fixed z-[60] bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 border-2 border-purple-500/70 rounded-2xl shadow-2xl w-[700px] max-h-[700px] overflow-hidden pointer-events-none"
          style={{ 
            left: Math.max(10, Math.min(hoverPosition.x, window.innerWidth - 720)),
            top: Math.max(10, Math.min(hoverPosition.y, window.innerHeight - 720)),
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 bg-purple-500/20">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 bg-purple-500/30 text-purple-200 rounded-lg text-base font-bold">
                {hoverPreview.displayNumber ? `Soru ${hoverPreview.displayNumber}` : `ID: ${hoverPreview.id?.substring(0,8) || '?'}`}
              </span>
              {hoverPreview.difficulty && (
                <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  hoverPreview.difficulty === 'easy' ? 'bg-green-500/20 text-green-300' :
                  hoverPreview.difficulty === 'hard' ? 'bg-red-500/20 text-red-300' :
                  'bg-yellow-500/20 text-yellow-300'
                }`}>
                  {hoverPreview.difficulty === 'easy' ? '🟢 Kolay' : hoverPreview.difficulty === 'hard' ? '🔴 Zor' : '🟡 Orta'}
                </span>
              )}
              {hoverPreview.image_url && (
                <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">📷 Resimli</span>
              )}
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4 overflow-y-auto max-h-[600px]">
            {/* Question Text */}
            <p className="text-white text-lg leading-relaxed">{hoverPreview.question_text}</p>
            
            {/* Image if exists - BÜYÜK */}
            {hoverPreview.image_url && (
              <div className="flex justify-center bg-slate-800/50 rounded-xl p-4">
                <img 
                  src={fixImageUrl(hoverPreview.image_url)}
                  alt="Soru görseli" 
                  className="rounded-lg max-h-64 w-auto object-contain border border-white/20"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            {/* Choices - BÜYÜK */}
            <div className="space-y-2">
              {hoverPreview.choices?.map((choice, idx) => (
                <div 
                  key={choice.letter || idx}
                  className={`p-3 rounded-xl text-base flex items-center gap-3 ${
                    choice.letter === hoverPreview.correct_answer
                      ? 'bg-green-500/20 border-2 border-green-400/50 text-green-100'
                      : 'bg-white/5 border border-white/10 text-white/80'
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                    choice.letter === hoverPreview.correct_answer
                      ? 'bg-green-500 text-white'
                      : 'bg-white/20 text-white'
                  }`}>
                    {choice.letter}
                  </span>
                  <span className="flex-1 text-sm">{choice.text || '—'}</span>
                  {choice.letter === hoverPreview.correct_answer && (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                </div>
              ))}
            </div>
            
            {/* Explanation preview - BÜYÜK */}
            {hoverPreview.explanation && (
              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                <p className="text-blue-300 text-sm font-semibold mb-2">💡 Açıklama:</p>
                <p className="text-white/80 text-sm leading-relaxed line-clamp-4">{hoverPreview.explanation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal - Müşteri Görünümü */}
      {showPreviewModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 border border-white/20 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Eye className="w-7 h-7 text-purple-400" />
                Müşteri Ön İzleme
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg text-white/60 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8">
              {/* Question Number Badge */}
              <div className="flex items-center gap-3 mb-6">
                <span className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-xl text-lg font-bold">
                  {formatQuestionNumber(editingQuestion.question_number, 1)}
                </span>
                {editingQuestion.category && (
                  <span className="px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg text-sm">
                    {editingQuestion.category}
                  </span>
                )}
              </div>

              {/* Question Text */}
              <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/10">
                <p className="text-white text-xl leading-relaxed">{editingQuestion.question_text}</p>
              </div>

              {/* Question Image if exists */}
              {editingQuestion.image_url && (
                <div className="mb-6 flex justify-center">
                  <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/20 inline-block max-w-lg">
                    <ImageLightbox 
                      src={fixImageUrl(editingQuestion.image_url)}
                      alt="Soru görseli" 
                      className="rounded-lg max-h-48 w-auto object-contain"
                      containerClassName="relative"
                    />
                  </div>
                </div>
              )}

              {/* Answer Choices */}
              <div className="space-y-3 mb-8">
                {editingQuestion.choices?.length > 0 ? (
                  editingQuestion.choices.map((choice, idx) => (
                    <div 
                      key={choice.letter || idx}
                      className={`p-4 rounded-xl border transition-all cursor-pointer ${
                        choice.letter === editingQuestion.correct_answer
                          ? 'bg-green-500/20 border-green-400/50 text-green-100'
                          : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span className={`w-10 h-10 flex items-center justify-center rounded-full font-bold text-lg ${
                          choice.letter === editingQuestion.correct_answer
                            ? 'bg-green-500 text-white'
                            : 'bg-white/20 text-white'
                        }`}>
                          {choice.letter}
                        </span>
                        <span className="flex-1 text-lg leading-relaxed">
                          {choice.text || <span className="text-red-400 italic">Şık metni yok!</span>}
                        </span>
                        {choice.letter === editingQuestion.correct_answer && (
                          <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <p className="text-red-300 font-bold">⚠️ Bu soruda şık bulunmuyor!</p>
                    <p className="text-red-200 text-sm mt-2">Lütfen soruyu düzenleyip şıkları ekleyin.</p>
                  </div>
                )}
              </div>

              {/* Explanation */}
              {editingQuestion.explanation ? (
                <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-blue-300 font-bold text-lg mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Açıklama
                  </h4>
                  <p className="text-white/90 leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>{editingQuestion.explanation}</p>
                </div>
              ) : (
                <div className="bg-yellow-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-yellow-300 font-bold text-lg mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Açıklama Yok
                  </h4>
                  <p className="text-yellow-200 text-sm">Bu soruda henüz açıklama eklenmemiş.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 flex justify-between">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  handleEdit(editingQuestion);
                }}
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl flex items-center gap-2"
              >
                <Edit className="w-5 h-5" />
                Düzenle
              </button>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {sortedQuestions.length > itemsPerPage && (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="text-gray-400">
              Gösterilen: {startIndex + 1} - {Math.min(endIndex, sortedQuestions.length)} / {sortedQuestions.length} soru
            </div>
            
            <div className="flex items-center gap-2">
              {/* Items per page */}
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value={25}>25 soru</option>
                <option value={50}>50 soru</option>
                <option value={100}>100 soru</option>
                <option value={200}>200 soru</option>
                <option value={500}>500 soru</option>
                <option value={1000}>1000 soru</option>
                <option value={2000}>2000 soru</option>
                <option value={5000}>Tümünü Göster</option>
              </select>

              {/* Previous button */}
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all"
              >
                ← Önceki
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                        currentPage === pageNum
                          ? 'bg-purple-500 text-white'
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Next button */}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all"
              >
                Sonraki →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white">Soru Düzenle</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <XCircle className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Question Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Soru Numarası</label>
                <input
                  type="text"
                  value={editingQuestion.question_number || ''}
                  disabled
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-mono opacity-50 cursor-not-allowed"
                />
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Soru Metni</label>
                <textarea
                  value={editingQuestion.question_text || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[100px]"
                />
              </div>

              {/* Choices (Şıklar) */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Şıklar</label>
                <div className="space-y-3">
                  {editingQuestion.choices?.map((choice, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-300 font-bold">
                        {choice.letter}
                      </div>
                      <input
                        type="text"
                        value={choice.text}
                        onChange={(e) => {
                          const newChoices = [...editingQuestion.choices];
                          newChoices[index].text = e.target.value;
                          setEditingQuestion({ ...editingQuestion, choices: newChoices });
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder={`Şık ${choice.letter}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Correct Answer */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Doğru Cevap</label>
                <select
                  value={editingQuestion.correct_answer || 'A'}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, correct_answer: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {editingQuestion.choices?.map((choice) => (
                    <option key={choice.letter} value={choice.letter}>
                      {choice.letter} - {(choice.text || '').substring(0, 30)}{(choice.text || '').length > 30 ? '...' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Açıklama</label>
                <textarea
                  value={editingQuestion.explanation || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[100px]"
                />
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Zorluk</label>
                <select
                  value={editingQuestion.difficulty || 'medium'}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="easy">Kolay</option>
                  <option value="medium">Orta</option>
                  <option value="hard">Zor</option>
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Resim</label>
                {editingQuestion.image_url ? (
                  <div className="relative flex justify-center">
                    <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/20 inline-block">
                      <ImageLightbox
                        src={fixImageUrl(editingQuestion.image_url)}
                        alt="Question"
                        className="rounded-lg max-h-40 w-auto object-contain"
                        containerClassName="relative"
                      />
                      <button
                        onClick={() => setEditingQuestion({ ...editingQuestion, image_url: null })}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg z-10"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <input
                      type="file"
                      accept="image/*,.webp,.svg"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image')}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer text-purple-400 hover:text-purple-300"
                    >
                      Resim yükle (.jpg, .png, .webp, .svg)
                    </label>
                  </div>
                )}
              </div>

              {/* Multiple Images Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">
                  Ek Resimler ({editingQuestion.image_urls?.length || 0} adet)
                </label>
                
                {/* Show existing multiple images */}
                {editingQuestion.image_urls && editingQuestion.image_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {editingQuestion.image_urls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={fixImageUrl(url)}
                          alt={`Ek resim ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                          style={{
                            backgroundColor: url.toLowerCase().endsWith('.svg') ? 'white' : 'transparent',
                            objectFit: url.toLowerCase().endsWith('.svg') ? 'contain' : 'cover',
                            padding: url.toLowerCase().endsWith('.svg') ? '4px' : '0'
                          }}
                        />
                        <button
                          onClick={() => removeMultipleImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add more images button */}
                <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*,.webp,.svg"
                    onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image', true)}
                    className="hidden"
                    id="multiple-image-upload"
                  />
                  <label
                    htmlFor="multiple-image-upload"
                    className="cursor-pointer text-green-400 hover:text-green-300 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Ek resim ekle (.jpg, .png, .webp, .svg)
                  </label>
                </div>
              </div>

              {/* Video Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video</label>
                {editingQuestion.video_url ? (
                  <div className="relative">
                    <video
                      src={fixImageUrl(editingQuestion.video_url)}
                      controls
                      className="w-full h-48 rounded-lg bg-black"
                    />
                    <button
                      onClick={() => setEditingQuestion({ ...editingQuestion, video_url: null })}
                      className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'video')}
                      className="hidden"
                      id="video-upload"
                    />
                    <label
                      htmlFor="video-upload"
                      className="cursor-pointer text-purple-400 hover:text-purple-300"
                    >
                      Video yükle
                    </label>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowPreviewModal(true)}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
                >
                  <Eye className="w-5 h-5" />
                  Önizleme
                </button>
                <button
                  onClick={handleSaveQuestion}
                  disabled={uploading}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  {uploading ? 'Yükleniyor...' : 'Kaydet'}
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-red-900 via-purple-900 to-slate-900 border border-white/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900/90 backdrop-blur-xl border-b border-white/10 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white">📋 Soru Önizleme</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <XCircle className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              {/* Question Number */}
              <div className="text-center">
                <span className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm font-mono">
                  {editingQuestion.question_number || 'Yeni Soru'}
                </span>
              </div>

              {/* Question Image */}
              {editingQuestion.image_url && (
                <div className="flex justify-center">
                  <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/20 inline-block max-w-md">
                    <ImageLightbox
                      src={fixImageUrl(editingQuestion.image_url)}
                      alt="Question"
                      className="rounded-lg max-h-52 w-auto object-contain"
                      containerClassName="relative"
                    />
                  </div>
                </div>
              )}

              {/* Multiple Images (image_urls) */}
              {editingQuestion.image_urls && editingQuestion.image_urls.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-400 text-center">Ek Resimler ({editingQuestion.image_urls.length} adet)</h4>
                  <div className="flex flex-wrap justify-center gap-3">
                    {editingQuestion.image_urls.map((url, index) => (
                      <div key={index} className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-white/20">
                        <ImageLightbox
                          src={fixImageUrl(url)}
                          alt={`Ek resim ${index + 1}`}
                          className="rounded-lg max-h-40 w-auto object-contain"
                          containerClassName="relative"
                        />
                        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded z-10">
                          #{index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Question Text */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                <h4 className="text-xl font-bold text-white mb-4">Soru:</h4>
                <p className="text-white text-lg leading-relaxed">{editingQuestion.question_text}</p>
              </div>

              {/* Choices */}
              <div className="space-y-3">
                {editingQuestion.choices?.map((choice) => (
                  <div
                    key={choice.letter}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      choice.letter === editingQuestion.correct_answer
                        ? 'bg-green-500/20 border-green-500 ring-2 ring-green-500/50'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                        choice.letter === editingQuestion.correct_answer
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-500/30 text-purple-300'
                      }`}>
                        {choice.letter}
                      </div>
                      <div className="flex-1 text-white">{choice.text}</div>
                      {choice.letter === editingQuestion.correct_answer && (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Question Video */}
              {editingQuestion.video_url && (
                <div className="rounded-xl overflow-hidden">
                  <video
                    src={fixImageUrl(editingQuestion.video_url)}
                    controls
                    className="w-full h-64 bg-black rounded-xl"
                  />
                </div>
              )}

              {/* Explanation */}
              {editingQuestion.explanation && (
                <div className="bg-blue-500/10 backdrop-blur-xl border border-blue-500/30 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-blue-300 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Açıklama:
                  </h4>
                  <p className="text-white/90 leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>{editingQuestion.explanation}</p>
                </div>
              )}

              {/* Difficulty Badge */}
              <div className="text-center">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  editingQuestion.difficulty === 'easy' ? 'bg-green-500/20 text-green-300' :
                  editingQuestion.difficulty === 'hard' ? 'bg-red-500/20 text-red-300' :
                  'bg-yellow-500/20 text-yellow-300'
                }`}>
                  Zorluk: {editingQuestion.difficulty === 'easy' ? 'Kolay' : editingQuestion.difficulty === 'hard' ? 'Zor' : 'Orta'}
                </span>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowPreviewModal(false)}
                className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-all"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Add Question Modal */}
      {showAddModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                <Plus className="w-6 h-6 text-green-400" />
                Yeni Soru Ekle
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <XCircle className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Custom ID */}
              <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-4">
                <label className="block text-sm font-semibold text-amber-300 mb-2">
                  🔑 Özel Soru ID (İsteğe Bağlı)
                </label>
                <input
                  type="text"
                  value={editingQuestion.custom_id || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, custom_id: e.target.value.toUpperCase() })}
                  placeholder="Örn: Q-12345 veya TRAFIK-001"
                  className="w-full px-4 py-3 bg-white/5 border border-amber-400/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono"
                />
                <p className="text-amber-200/60 text-xs mt-2">
                  💡 Boş bırakırsanız otomatik ID oluşturulur (Q-XXXXXXXX formatında)
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Kategori *</label>
                <select
                  value={editingQuestion.category_id || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, category_id: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name_turkish || cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Soru Metni *</label>
                <textarea
                  value={editingQuestion.question_text || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text: e.target.value })}
                  placeholder="Soru metnini girin..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[100px]"
                />
              </div>

              {/* Choices */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Şıklar *</label>
                <div className="space-y-3">
                  {editingQuestion.choices?.map((choice, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center text-green-300 font-bold">
                        {choice.letter}
                      </div>
                      <input
                        type="text"
                        value={choice.text}
                        onChange={(e) => {
                          const newChoices = [...editingQuestion.choices];
                          newChoices[index].text = e.target.value;
                          setEditingQuestion({ ...editingQuestion, choices: newChoices });
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder={`Şık ${choice.letter}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Correct Answer */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Doğru Cevap *</label>
                <select
                  value={editingQuestion.correct_answer || 'A'}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, correct_answer: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {editingQuestion.choices?.map((choice) => (
                    <option key={choice.letter} value={choice.letter}>
                      {choice.letter} - {(choice.text || '').substring(0, 30)}{(choice.text || '').length > 30 ? '...' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Açıklama</label>
                <textarea
                  value={editingQuestion.explanation || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                  placeholder="Sorunun açıklamasını girin..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[100px]"
                />
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Zorluk</label>
                <select
                  value={editingQuestion.difficulty || 'medium'}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="easy">🟢 Kolay</option>
                  <option value="medium">🟡 Orta</option>
                  <option value="hard">🔴 Zor</option>
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Resim (Opsiyonel)</label>
                {editingQuestion.image_url ? (
                  <div className="relative flex justify-center">
                    <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-white/20 inline-block">
                      <ImageLightbox
                        src={fixImageUrl(editingQuestion.image_url)}
                        alt="Question"
                        className="rounded-lg max-h-40 w-auto object-contain"
                        containerClassName="relative"
                      />
                      <button
                        onClick={() => setEditingQuestion({ ...editingQuestion, image_url: null })}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg z-10"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <input
                      type="file"
                      accept="image/*,.webp,.svg"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image')}
                      className="hidden"
                      id="add-image-upload"
                    />
                    <label
                      htmlFor="add-image-upload"
                      className="cursor-pointer text-green-400 hover:text-green-300"
                    >
                      Resim yükle (.jpg, .png, .webp, .svg)
                    </label>
                  </div>
                )}
              </div>

              {/* Multiple Images Upload for Add Modal */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">
                  Ek Resimler ({editingQuestion.image_urls?.length || 0} adet)
                </label>
                
                {/* Show existing multiple images */}
                {editingQuestion.image_urls && editingQuestion.image_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {editingQuestion.image_urls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={fixImageUrl(url)}
                          alt={`Ek resim ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                          style={{
                            backgroundColor: url.toLowerCase().endsWith('.svg') ? 'white' : 'transparent',
                            objectFit: url.toLowerCase().endsWith('.svg') ? 'contain' : 'cover',
                            padding: url.toLowerCase().endsWith('.svg') ? '4px' : '0'
                          }}
                        />
                        <button
                          onClick={() => removeMultipleImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add more images button */}
                <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*,.webp,.svg"
                    onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image', true)}
                    className="hidden"
                    id="add-multiple-image-upload"
                  />
                  <label
                    htmlFor="add-multiple-image-upload"
                    className="cursor-pointer text-green-400 hover:text-green-300 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Ek resim ekle (.jpg, .png, .webp, .svg)
                  </label>
                </div>
              </div>

              {/* Video Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video (Opsiyonel)</label>
                {editingQuestion.video_url ? (
                  <div className="relative">
                    <video
                      src={fixImageUrl(editingQuestion.video_url)}
                      controls
                      className="w-full h-48 rounded-lg bg-black"
                    />
                    <button
                      onClick={() => setEditingQuestion({ ...editingQuestion, video_url: null })}
                      className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'video')}
                      className="hidden"
                      id="add-video-upload"
                    />
                    <label
                      htmlFor="add-video-upload"
                      className="cursor-pointer text-green-400 hover:text-green-300"
                    >
                      Video yükle
                    </label>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowPreviewModal(true)}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
                >
                  <Eye className="w-5 h-5" />
                  Önizleme
                </button>
                <button
                  onClick={handleAddQuestion}
                  disabled={uploading}
                  className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  {uploading ? 'Yükleniyor...' : '✅ Soru Ekle'}
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// QUIZ SETTINGS TAB
function QuizSettingsTab() {
  const [freeQuestionCount, setFreeQuestionCount] = useState(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fetch current settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/quiz-settings`);
        setFreeQuestionCount(response.data.free_question_count || 10);
      } catch (error) {
        console.error('Settings load error:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const adminPassword = localStorage.getItem('admin_password');
      
      await axios.put(
        `${API}/admin/quiz-settings`,
        { free_question_count: freeQuestionCount },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      
      // Also save to localStorage for immediate effect
      localStorage.setItem('free_question_count', freeQuestionCount);
      
      alert('✅ Quiz ayarları kaydedildi!');
    } catch (error) {
      console.error('Save error:', error);
      alert('❌ Ayarlar kaydedilemedi!');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Zap className="w-7 h-7 text-yellow-400" />
          ⚙️ Quiz Ayarları
        </h3>

        <div className="space-y-8">
          {/* Free Question Count */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-white mb-2">Ücretsiz Soru Sayısı</h4>
                <p className="text-gray-400 text-sm">
                  Ana sayfada ücretsiz kullanıcılara kaç soru gösterileceğini ayarlayın
                </p>
              </div>
              <div className="text-4xl font-black text-purple-400">{freeQuestionCount}</div>
            </div>

            <div className="space-y-4">
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={freeQuestionCount}
                onChange={(e) => setFreeQuestionCount(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />

              <div className="flex justify-between text-sm text-gray-400">
                <span>5 soru</span>
                <span>25 soru</span>
                <span>50 soru</span>
                <span>75 soru</span>
                <span>100 soru</span>
              </div>

              {/* Quick Buttons */}
              <div className="grid grid-cols-5 gap-2">
                {[5, 10, 20, 40, 70].map((count) => (
                  <button
                    key={count}
                    onClick={() => setFreeQuestionCount(count)}
                    className={`py-2 rounded-lg font-semibold transition-all ${
                      freeQuestionCount === count
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quiz Modes */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">Quiz Modları</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <div className="text-white font-semibold">Alıştırma Modu</div>
                  <div className="text-sm text-gray-400">Süresiz, açıklamalarla</div>
                </div>
                <div className="px-4 py-2 bg-green-500/20 text-green-300 rounded-lg text-sm font-semibold">
                  Aktif
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <div className="text-white font-semibold">Sınav Modu</div>
                  <div className="text-sm text-gray-400">Süreli, gerçek sınav simülasyonu</div>
                </div>
                <div className="px-4 py-2 bg-green-500/20 text-green-300 rounded-lg text-sm font-semibold">
                  Aktif
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 backdrop-blur-xl border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-blue-300 font-bold mb-2">ℹ️ Bilgi</h4>
            <p className="text-white/80 text-sm leading-relaxed">
              Ücretsiz kullanıcılar ana sayfada belirlediğiniz soru sayısı kadar pratik yapabilir. 
              Premium özellikler (sınırsız soru, sınav modu, performans analizi) sadece Premium üyeler için aktiftir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


// SYSTEM TAB
function SystemTab({ stats, lastUpdate }) {
  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Server className="w-6 h-6 text-purple-400" />
          Sistem Bilgileri
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-400">Platform</span>
            <span className="text-white">Emergent Agent</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Version</span>
            <span className="text-white">2.0.0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Database</span>
            <span className="text-white">MongoDB (test_database)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// DUPLICATE MANAGEMENT TAB
function DuplicateManagementTab() {
  const [duplicates, setDuplicates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      const adminPassword = localStorage.getItem('admin_password');
      const response = await axios.get(`${API}/admin/check-duplicates`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setDuplicates(response.data);
    } catch (error) {
      console.error('Duplicate fetch error:', error);
      alert('❌ Duplicate kontrol hatası!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionNumber) => {
    if (!window.confirm(`${questionNumber} numaralı soruyu silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setDeleting(questionNumber);
      const adminPassword = localStorage.getItem('admin_password');
      
      await axios.delete(`${API}/admin/delete-duplicate/${questionNumber}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      
      alert('✅ Soru silindi!');
      fetchDuplicates(); // Refresh
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Soru silinemedi!');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
        <p className="text-white">Duplicate sorular kontrol ediliyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">Toplam Soru</h3>
          <p className="text-4xl font-black text-white">{duplicates?.total_questions || 0}</p>
        </div>
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">Benzersiz Soru</h3>
          <p className="text-4xl font-black text-green-400">{duplicates?.unique_questions || 0}</p>
        </div>
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">Duplicate</h3>
          <p className="text-4xl font-black text-red-400">{duplicates?.duplicate_count || 0}</p>
        </div>
      </div>

      {/* Duplicate List */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertCircle className="w-7 h-7 text-red-400" />
            Duplicate Sorular ({duplicates?.duplicate_count || 0})
          </h3>
          <button
            onClick={fetchDuplicates}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Yenile
          </button>
        </div>

        {duplicates?.duplicate_count === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-xl text-white font-bold">🎉 Harika! Duplicate soru yok!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {duplicates?.duplicates?.map((dup, index) => {
              // Format question number
              const formatNum = (qn) => {
                if (!qn) return 'Bilinmiyor';
                const match = qn.match(/Q-(\d+)/i);
                if (match) return `Soru ${parseInt(match[1], 10)}`;
                const numMatch = qn.match(/\d+/);
                if (numMatch) return `Soru ${parseInt(numMatch[0], 10)}`;
                return qn;
              };
              
              return (
                <div key={index} className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 bg-red-500 text-white rounded-lg font-bold text-sm">
                          {formatNum(dup.question_number)}
                        </span>
                        <span className="text-red-300 text-sm">
                          → Kopya: <strong>{formatNum(dup.duplicate_of)}</strong>
                        </span>
                      </div>
                      <p className="text-white leading-relaxed">{dup.question_text}</p>
                    </div>
                  <button
                    onClick={() => handleDelete(dup.question_number)}
                    disabled={deleting === dup.question_number}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {deleting === dup.question_number ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Siliniyor...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Sil
                      </>
                    )}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// SWEDISH TERMS TAB
function SwedishTermsTab() {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [batchSize, setBatchSize] = useState(50);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [history, setHistory] = useState([]);

  const handleAddSwedishTerms = async () => {
    if (!window.confirm(`${batchSize} soruya GPT-4o ile İsveççe terimler eklenecek. Devam edilsin mi?`)) {
      return;
    }

    try {
      setProcessing(true);
      const adminPassword = localStorage.getItem('admin_password');
      
      const response = await axios.post(
        `${API}/admin/add-swedish-terms?batch_size=${batchSize}`,
        {},
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      
      setResult(response.data);
      setTotalProcessed(prev => prev + (response.data.updated || 0));
      
      // Add to history
      setHistory(prev => [{
        timestamp: new Date().toLocaleString('tr-TR'),
        processed: response.data.processed,
        updated: response.data.updated,
        errors: response.data.errors || []
      }, ...prev.slice(0, 4)]); // Keep last 5 results
      
      if (response.data.updated > 0) {
        alert(`✅ ${response.data.updated} soruya İsveççe terim eklendi!\n\nİşlenen: ${response.data.processed}\nGüncellenen: ${response.data.updated}`);
      } else {
        alert('ℹ️ Hiçbir soru güncellenmedi. Tüm sorular zaten işlenmiş olabilir.');
      }
    } catch (error) {
      console.error('Add terms error:', error);
      alert('❌ İsveççe terimler eklenemedi! Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Languages className="w-7 h-7 text-blue-400" />
          🇸🇪 AI ile İsveççe Terim Ekleme (GPT-4o)
        </h3>

        <div className="space-y-6">
          {/* Total Progress */}
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-6">
            <div className="text-center">
              <p className="text-white/70 mb-2">Toplam Güncellenen Soru</p>
              <p className="text-5xl font-black text-blue-300">{totalProcessed}</p>
              <p className="text-white/60 text-sm mt-2">2,500 sorudan</p>
            </div>
          </div>

          {/* Batch Size Selector */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">⚙️ Batch Ayarı</h4>
            <div className="flex items-center gap-4">
              <label className="text-white/70">Her seferde işlenecek soru:</label>
              <select
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                disabled={processing}
              >
                <option value={25}>25 soru</option>
                <option value={50}>50 soru</option>
                <option value={100}>100 soru</option>
                <option value={200}>200 soru</option>
              </select>
            </div>
            <p className="text-white/50 text-sm mt-3">
              💡 Küçük batch = daha güvenli, büyük batch = daha hızlı
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
            <h4 className="text-lg font-bold text-blue-300 mb-3">ℹ️ Nasıl Çalışır?</h4>
            <ul className="space-y-2 text-white/80">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span><strong>GPT-4o</strong> ile her soru analiz edilir</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Türkçe trafik terimlerine doğru İsveççe karşılıklar eklenir</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Örnek: &quot;Hız sınırı&quot; → &quot;Hız sınırı (hastighetsbegränsning)&quot;</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Zaten işlenmiş sorular atlanır</span>
              </li>
            </ul>
          </div>

          {/* Swedish Terms Examples */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">📚 Eklenecek İsveççe Terimler (Örnekler)</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="text-white/70">Hız sınırı → <strong className="text-blue-300">hastighetsbegränsning</strong></div>
              <div className="text-white/70">Sollama → <strong className="text-blue-300">omkörning</strong></div>
              <div className="text-white/70">Dikiz aynası → <strong className="text-blue-300">backspegel</strong></div>
              <div className="text-white/70">Öncelik hakkı → <strong className="text-blue-300">förkörsrätt</strong></div>
              <div className="text-white/70">Yaya geçidi → <strong className="text-blue-300">övergångsställe</strong></div>
              <div className="text-white/70">Emniyet kemeri → <strong className="text-blue-300">säkerhetsbälte</strong></div>
            </div>
          </div>

          {/* Latest Result */}
          {result && (
            <div className={`${result.updated > 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'} border rounded-xl p-6`}>
              <h4 className="text-lg font-bold text-green-300 mb-3">
                {result.updated > 0 ? '✅ Başarılı' : 'ℹ️ Tamamlandı'}
              </h4>
              <div className="space-y-2 text-white">
                <p>İşlenen soru: <strong>{result.processed}</strong></p>
                <p>Güncellenen soru: <strong>{result.updated}</strong></p>
                <p className="text-green-300 font-bold">{result.message}</p>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-300 font-bold mb-1">Hatalar:</p>
                    <ul className="text-sm text-red-200 space-y-1">
                      {result.errors.map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
              <h4 className="text-lg font-bold text-white mb-4">📜 İşlem Geçmişi</h4>
              <div className="space-y-3">
                {history.map((item, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white/50 text-xs mb-1">{item.timestamp}</p>
                        <p className="text-white">İşlenen: <strong>{item.processed}</strong> | Güncellenen: <strong className="text-green-400">{item.updated}</strong></p>
                      </div>
                      {item.updated > 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleAddSwedishTerms}
            disabled={processing}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                GPT-4o ile işleniyor...
              </>
            ) : (
              <>
                <Languages className="w-6 h-6" />
                🤖 GPT-4o ile İşle ({batchSize} soru)
              </>
            )}
          </button>

          {/* Fix English Terms Button */}
          <button
            onClick={async () => {
              if (!window.confirm('Parantez içindeki İngilizce terimleri İsveççeye çevireceksiniz. Devam edilsin mi?')) return;
              try {
                setProcessing(true);
                const adminPassword = localStorage.getItem('admin_password');
                const response = await axios.post(
                  `${API}/admin/fix-english-terms`,
                  {},
                  { headers: { 'X-Admin-Password': adminPassword } }
                );
                alert(`✅ ${response.data.updated} soruda İngilizce terimler İsveççeye çevrildi!\n\nKontrol edilen: ${response.data.questions_checked}`);
              } catch (error) {
                alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
              } finally {
                setProcessing(false);
              }
            }}
            disabled={processing}
            className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            🔄 İngilizce Terimleri İsveççeye Çevir (roundabout → rondell)
          </button>

          <p className="text-white/60 text-center text-sm">
            ⚠️ Bu işlem Emergent LLM kredisi kullanır. Her batch sonunda tekrar tıklayarak devam edebilirsiniz.
          </p>
        </div>
      </div>
    </div>
  );
}

// FOOTER SETTINGS TAB
function FooterSettingsTab() {
  const [footerSettings, setFooterSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFooterSettings();
  }, []);

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${API}/footer-settings`);
      setFooterSettings(response.data);
    } catch (error) {
      console.error('Footer settings load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const adminPassword = localStorage.getItem('admin_password');
      
      await axios.put(
        `${API}/admin/footer-settings`,
        footerSettings,
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      
      alert('✅ Footer ayarları kaydedildi!');
    } catch (error) {
      console.error('Save error:', error);
      alert('❌ Ayarlar kaydedilemedi!');
    } finally {
      setSaving(false);
    }
  };

  const addSocialMedia = () => {
    setFooterSettings({
      ...footerSettings,
      social_media: [
        ...footerSettings.social_media,
        { name: 'Yeni', icon: 'Mail', url: '#', enabled: true }
      ]
    });
  };

  const removeSocialMedia = (index) => {
    const newSocial = footerSettings.social_media.filter((_, i) => i !== index);
    setFooterSettings({ ...footerSettings, social_media: newSocial });
  };

  const updateSocialMedia = (index, field, value) => {
    const newSocial = [...footerSettings.social_media];
    newSocial[index][field] = value;
    setFooterSettings({ ...footerSettings, social_media: newSocial });
  };

  const addLegalLink = () => {
    setFooterSettings({
      ...footerSettings,
      legal_links: [
        ...footerSettings.legal_links,
        { name: 'Yeni Link', url: '#', enabled: true }
      ]
    });
  };

  const removeLegalLink = (index) => {
    const newLinks = footerSettings.legal_links.filter((_, i) => i !== index);
    setFooterSettings({ ...footerSettings, legal_links: newLinks });
  };

  const updateLegalLink = (index, field, value) => {
    const newLinks = [...footerSettings.legal_links];
    newLinks[index][field] = value;
    setFooterSettings({ ...footerSettings, legal_links: newLinks });
  };

  if (loading) {
    return <div className="text-center py-12 text-white">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Globe className="w-7 h-7 text-blue-400" />
          🔧 Footer Ayarları
        </h3>

        <div className="space-y-8">
          {/* About Section */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">📝 Hakkımızda Bölümü</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Başlık</label>
                <input
                  type="text"
                  value={footerSettings.about_title}
                  onChange={(e) => setFooterSettings({ ...footerSettings, about_title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Açıklama Metni</label>
                <textarea
                  value={footerSettings.about_text}
                  onChange={(e) => setFooterSettings({ ...footerSettings, about_text: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">📱 Sosyal Medya</h4>
              <button
                onClick={addSocialMedia}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ekle
              </button>
            </div>

            <div className="space-y-3">
              {footerSettings.social_media.map((social, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                  <input
                    type="checkbox"
                    checked={social.enabled}
                    onChange={(e) => updateSocialMedia(index, 'enabled', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <input
                    type="text"
                    value={social.name}
                    onChange={(e) => updateSocialMedia(index, 'name', e.target.value)}
                    placeholder="İsim"
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                  <select
                    value={social.icon}
                    onChange={(e) => updateSocialMedia(index, 'icon', e.target.value)}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="Mail">📧 Email</option>
                    <option value="Facebook">📘 Facebook</option>
                    <option value="Instagram">📸 Instagram</option>
                    <option value="Twitter">🐦 Twitter</option>
                    <option value="Linkedin">💼 LinkedIn</option>
                    <option value="Youtube">📺 YouTube</option>
                  </select>
                  <input
                    type="text"
                    value={social.url}
                    onChange={(e) => updateSocialMedia(index, 'url', e.target.value)}
                    placeholder="URL"
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                  <button
                    onClick={() => removeSocialMedia(index)}
                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Legal Links (Çerez, Gizlilik, vb.) */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">⚖️ Yasal Linkler (Çerez, Gizlilik, vb.)</h4>
              <button
                onClick={addLegalLink}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ekle
              </button>
            </div>

            <div className="space-y-3">
              {footerSettings.legal_links.map((link, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                  <input
                    type="checkbox"
                    checked={link.enabled}
                    onChange={(e) => updateLegalLink(index, 'enabled', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <input
                    type="text"
                    value={link.name}
                    onChange={(e) => updateLegalLink(index, 'name', e.target.value)}
                    placeholder="Link Adı"
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                  <input
                    type="text"
                    value={link.url}
                    onChange={(e) => updateLegalLink(index, 'url', e.target.value)}
                    placeholder="URL"
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                  <button
                    onClick={() => removeLegalLink(index)}
                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Follow Title */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">👥 Bizi Takip Edin Başlığı</h4>
            <input
              type="text"
              value={footerSettings.follow_title}
              onChange={(e) => setFooterSettings({ ...footerSettings, follow_title: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Copyright & Made With */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
            <h4 className="text-lg font-bold text-white mb-4">©️ Alt Bilgi Metinleri</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Copyright Metni</label>
                <input
                  type="text"
                  value={footerSettings.copyright_text}
                  onChange={(e) => setFooterSettings({ ...footerSettings, copyright_text: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Made With Metni</label>
                <input
                  type="text"
                  value={footerSettings.made_with_text}
                  onChange={(e) => setFooterSettings({ ...footerSettings, made_with_text: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'Kaydediliyor...' : '💾 Footer Ayarlarını Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}


// SETTINGS TAB
function SettingsTab() {
  return <SystemSettings />;
}



// STATIC PAGES TAB
function StaticPagesTab({ staticPages, fetchStaticPages }) {
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async (pageId, title, content) => {
    try {
      setSaving(true);
      const adminPassword = localStorage.getItem('admin_password');
      
      await axios.put(
        `${API}/admin/pages/${pageId}`,
        {
          title_tr: title,
          content_tr: content
        },
        {
          headers: { 'X-Admin-Password': adminPassword }
        }
      );

      alert('✅ Sayfa başarıyla güncellendi!');
      setEditingPage(null);
      await fetchStaticPages();
    } catch (error) {
      console.error('Save error:', error);
      alert('❌ Sayfa güncellenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const initializePages = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      await axios.post(
        `${API}/admin/pages/initialize`,
        {},
        {
          headers: { 'X-Admin-Password': adminPassword }
        }
      );
      alert('✅ Sayfalar başarıyla başlatıldı!');
      await fetchStaticPages();
    } catch (error) {
      console.error('Initialize error:', error);
      alert('❌ Sayfalar başlatılamadı: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Sayfa İçerikleri</h3>
            <p className="text-gray-400 text-sm">
              Web sitenizdeki statik sayfaların içeriklerini buradan düzenleyebilirsiniz.
            </p>
          </div>
          {staticPages.length === 0 && (
            <button
              onClick={initializePages}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Varsayılan Sayfaları Oluştur
            </button>
          )}
        </div>
      </div>

      {/* Pages List */}
      <div className="space-y-4">
        {staticPages.map((page) => (
          <div
            key={page.id}
            className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden"
          >
            {editingPage === page.id ? (
              /* Edit Mode */
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Sayfa Başlığı</label>
                  <input
                    type="text"
                    defaultValue={page.title_tr}
                    id={`title-${page.id}`}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">İçerik (HTML destekler)</label>
                  <textarea
                    defaultValue={page.content_tr}
                    id={`content-${page.id}`}
                    rows={15}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const title = document.getElementById(`title-${page.id}`).value;
                      const content = document.getElementById(`content-${page.id}`).value;
                      handleSave(page.id, title, content);
                    }}
                    disabled={saving}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                  >
                    {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
                  </button>
                  <button
                    onClick={() => setEditingPage(null)}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-all"
                  >
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">{page.title_tr}</h4>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg font-mono">
                        /{page.slug}
                      </span>
                      <span className="text-gray-400">
                        Güncelleme: {new Date(page.updated_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingPage(page.id)}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-all flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Düzenle
                  </button>
                </div>

                <div
                  className="prose prose-sm prose-invert max-w-none line-clamp-3"
                  dangerouslySetInnerHTML={{ __html: page.content_tr }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {staticPages.length === 0 && (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
          <FileText className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Henüz sayfa oluşturulmamış</h3>
          <p className="text-gray-400 mb-6">
            Varsayılan sayfaları oluşturmak için yukarıdaki butona tıklayın.
          </p>
        </div>
      )}
    </div>
  );
}

// TEST MODE TAB
function TestModeTab() {
  const [showQuiz, setShowQuiz] = useState(false);
  
  if (showQuiz) {
    return <PremiumQuizMode onBack={() => setShowQuiz(false)} user={{ email: 'admin@ehliyet.se' }} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-2xl font-bold text-white mb-2">🎯 Test Modu</h3>
        <p className="text-gray-400">
          İstediğiniz ayarlarla özelleştirilebilir test yapın. Soru sayısı, süre ve kategori seçebilirsiniz.
        </p>
      </div>

      {/* Start Test Button */}
      <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <div className="text-center space-y-6">
          <Trophy className="w-20 h-20 text-yellow-400 mx-auto" />
          <h2 className="text-3xl font-black text-white">Özelleştirilebilir Test</h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Soru sayısı, süre ve kategori seçerek istediğiniz gibi test oluşturun. 
            Örnek: 20 soru - Sadece Trafik Kuralları - 20 dakika
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>Özel soru sayısı (5-2000)</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>Özel süre ayarı</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>Kategori seçimi</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>İşaretleme sistemi</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>Genel bakış & istatistik</span>
            </div>
          </div>

          <button
            onClick={() => setShowQuiz(true)}
            className="px-12 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 rounded-xl font-black text-xl transition-all shadow-2xl flex items-center gap-3 mx-auto"
          >
            <Play className="w-6 h-6" />
            Teste Başla
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-6">
          <h4 className="text-white font-bold mb-2 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Kısa Test
          </h4>
          <p className="text-gray-300 text-sm">20 soru - 20 dakika - Tek kategori</p>
        </div>
        
        <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-6">
          <h4 className="text-white font-bold mb-2 flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Orta Test
          </h4>
          <p className="text-gray-300 text-sm">50 soru - 50 dakika - Tüm kategoriler</p>
        </div>
        
        <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6">
          <h4 className="text-white font-bold mb-2 flex items-center gap-2">
            <Award className="w-5 h-5" />
            Tam Test
          </h4>
          <p className="text-gray-300 text-sm">70 soru - 50 dakika - Gerçek sınav formatı</p>
        </div>
      </div>
    </div>
  );
}

// PRICING TAB - Fiyat Ayarları
function PricingTab() {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedPackages, setEditedPackages] = useState({});

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/packages`, {
        headers: { 'X-Admin-Password': '123' }
      });
      setPackages(response.data.packages);
      setEditedPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Fallback to default packages
      const defaultPackages = {
        "3_days": { name: "3 Gün", price: 199, days: 3, currency: "SEK" },
        "15_days": { name: "15 Gün", price: 349, days: 15, currency: "SEK" },
        "1_month": { name: "1 Ay", price: 550, days: 30, currency: "SEK" },
        "2_months": { name: "2 Ay", price: 750, days: 60, currency: "SEK" },
        "3_months": { name: "3 Ay", price: 1100, days: 90, currency: "SEK" }
      };
      setPackages(defaultPackages);
      setEditedPackages(defaultPackages);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (packageId, field, value) => {
    setEditedPackages(prev => ({
      ...prev,
      [packageId]: {
        ...prev[packageId],
        [field]: field === 'price' || field === 'days' ? Number(value) : value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/packages`, 
        { packages: editedPackages },
        { headers: { 'X-Admin-Password': '123' } }
      );
      setPackages(editedPackages);
      alert('✅ Fiyatlar başarıyla güncellendi!');
    } catch (error) {
      console.error('Error saving packages:', error);
      alert('❌ Fiyatlar kaydedilemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(packages) !== JSON.stringify(editedPackages);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
        <span className="ml-3 text-white">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-black text-white mb-2 flex items-center gap-3">
              <CreditCard className="w-10 h-10 text-green-400" />
              Fiyat Ayarları
            </h2>
            <p className="text-white/60">Premium paket fiyatlarını buradan düzenleyebilirsiniz</p>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
              hasChanges 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:scale-105' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(editedPackages).map(([packageId, pkg]) => {
          const originalPkg = packages[packageId] || {};
          const priceChanged = pkg.price !== (originalPkg.price || originalPkg.amount);
          const nameChanged = pkg.name !== originalPkg.name;
          const daysChanged = pkg.days !== originalPkg.days;
          const hasPackageChanges = priceChanged || nameChanged || daysChanged;

          return (
            <div 
              key={packageId}
              className={`bg-slate-900/50 backdrop-blur-xl border rounded-2xl p-6 transition-all ${
                hasPackageChanges ? 'border-yellow-500/50 ring-2 ring-yellow-500/20' : 'border-white/10'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm font-mono">
                  {packageId}
                </span>
                {hasPackageChanges && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-xs">
                    Değiştirildi
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {/* Paket Adı */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Paket Adı</label>
                  <input
                    type="text"
                    value={pkg.name}
                    onChange={(e) => handlePriceChange(packageId, 'name', e.target.value)}
                    className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Fiyat */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Fiyat ({pkg.currency || 'SEK'})</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={pkg.price || pkg.amount || 0}
                      onChange={(e) => handlePriceChange(packageId, 'price', e.target.value)}
                      className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 text-xl font-bold"
                      min="0"
                      step="1"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                      {pkg.currency || 'SEK'}
                    </span>
                  </div>
                  {priceChanged && (
                    <p className="text-xs text-yellow-400 mt-1">
                      Eski: {originalPkg.price || originalPkg.amount} {pkg.currency || 'SEK'}
                    </p>
                  )}
                </div>

                {/* Gün */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Süre (Gün)</label>
                  <input
                    type="number"
                    value={pkg.days}
                    onChange={(e) => handlePriceChange(packageId, 'days', e.target.value)}
                    className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                  />
                </div>

                {/* Para Birimi */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Para Birimi</label>
                  <select
                    value={pkg.currency || 'SEK'}
                    onChange={(e) => handlePriceChange(packageId, 'currency', e.target.value)}
                    className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="SEK">SEK (İsveç Kronu)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="USD">USD (Dolar)</option>
                    <option value="TRY">TRY (Türk Lirası)</option>
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <p className="font-semibold mb-1">Bilgi</p>
            <ul className="list-disc list-inside space-y-1 text-blue-300/80">
              <li>Fiyat değişiklikleri hemen yürürlüğe girer</li>
              <li>Mevcut abonelikleri etkilemez, sadece yeni satışları etkiler</li>
              <li>Stripe'ta da fiyatları güncellemeyi unutmayın</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// SWISH SETTINGS TAB - Swish Ödeme Ayarları
function SwishSettingsTab() {
  const [settings, setSettings] = useState({
    swish_number: '123-377 74 30',
    swish_image: '/swish-qr.png',
    activation_message: 'Ödemeniz onaylandıktan sonra hesabınız en geç 24 saat içinde aktif edilecektir.',
    instructions: [
      'Swish uygulamanızı açın',
      'QR kodu tarayın veya numarayı girin',
      '{{amount}} tutarını gönderin',
      'Mesaj kısmına email adresinizi yazın'
    ]
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [activating, setActivating] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPendingPayments();
  }, []);

  const fetchSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/swish-settings`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Swish settings fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/swish-payments`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setPendingPayments(response.data.payments || []);
    } catch (error) {
      console.error("Pending payments fetch error:", error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(`${API}/admin/swish-settings`, settings, {
        headers: { "X-Admin-Password": adminPassword }
      });
      alert("✅ Swish ayarları kaydedildi!");
    } catch (error) {
      alert("❌ Kaydetme hatası: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingImage(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(`${API}/admin/swish-settings/upload-image`, formData, {
        headers: { 
          "X-Admin-Password": adminPassword,
          "Content-Type": "multipart/form-data"
        }
      });
      if (response.data.image_url) {
        setSettings(prev => ({ ...prev, swish_image: response.data.image_url }));
        alert("✅ QR kod resmi yüklendi!");
      }
    } catch (error) {
      alert("❌ Resim yüklenemedi: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...settings.instructions];
    newInstructions[index] = value;
    setSettings(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setSettings(prev => ({
      ...prev,
      instructions: [...prev.instructions, 'Yeni adım']
    }));
  };

  const removeInstruction = (index) => {
    setSettings(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const activatePayment = async (paymentId) => {
    try {
      setActivating(paymentId);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(`${API}/admin/swish-payments/${paymentId}/activate`, {}, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data.success) {
        alert("✅ " + response.data.message);
        fetchPendingPayments();
      } else {
        alert("❌ " + response.data.error);
      }
    } catch (error) {
      alert("❌ Aktivasyon hatası: " + (error.response?.data?.detail || error.message));
    } finally {
      setActivating(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full mx-auto mb-4"></div>
        <p className="text-white/60">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white flex items-center gap-3">
        <Smartphone className="w-8 h-8 text-blue-400" />
        Swish Ödeme Ayarları
      </h2>

      {/* Pending Payments Section */}
      <div className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-orange-400" />
          Bekleyen Swish Ödemeleri
          {pendingPayments.filter(p => p.status === 'pending').length > 0 && (
            <span className="bg-orange-500 text-white text-sm px-2 py-1 rounded-lg ml-2">
              {pendingPayments.filter(p => p.status === 'pending').length}
            </span>
          )}
        </h3>

        {loadingPayments ? (
          <div className="text-white/60 text-center py-4">Yükleniyor...</div>
        ) : pendingPayments.filter(p => p.status === 'pending').length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-white/70">Bekleyen ödeme yok</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingPayments.filter(p => p.status === 'pending').map((payment) => (
              <div key={payment.id} className="bg-white/10 rounded-xl p-4 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-white font-bold">{payment.user_name}</p>
                  <p className="text-white/60 text-sm">{payment.user_email}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {new Date(payment.created_at).toLocaleString('tr-TR')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{payment.amount} {payment.currency}</p>
                  <p className="text-white/60 text-sm">{payment.package_id}</p>
                </div>
                <button
                  onClick={() => activatePayment(payment.id)}
                  disabled={activating === payment.id}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50"
                >
                  {activating === payment.id ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></div>
                      Aktif Ediliyor...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Premium Aktif Et
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-6">Swish Sayfası Düzenleme</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column - QR Image */}
          <div>
            <label className="text-white/80 text-sm block mb-2">QR Kod Resmi (Büyütmek için tıklayın)</label>
            <div 
              className="rounded-2xl overflow-hidden mb-4 cursor-pointer hover:shadow-xl transition-all relative group"
              onClick={() => setShowLightbox(true)}
              data-testid="admin-swish-qr-preview"
              style={{ maxWidth: '100%' }}
            >
              {settings.swish_image ? (
                <img 
                  src={`${settings.swish_image}?t=${Date.now()}`}
                  alt="Swish QR" 
                  className="w-full h-auto block"
                  style={{ minHeight: '500px', objectFit: 'contain', background: '#fff' }}
                  onError={(e) => {
                    console.error('Image load error:', settings.swish_image);
                    e.target.src = '/swish-qr.png';
                  }}
                />
              ) : (
                <div className="bg-white text-gray-400 py-8 text-center">QR resmi yüklenmedi</div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center transition-all">
                <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
              </div>
            </div>
            <label className="block">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <span className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl cursor-pointer inline-flex items-center gap-2 font-medium">
                {uploadingImage ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></div>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Yeni QR Yükle (A4 boyutu desteklenir)
                  </>
                )}
              </span>
            </label>
            <p className="text-white/50 text-xs mt-2">Büyük resimler (A4 boyutu dahil) desteklenir</p>
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-4">
            <div>
              <label className="text-white/80 text-sm block mb-2">Swish Numarası</label>
              <input
                type="text"
                value={settings.swish_number}
                onChange={(e) => setSettings(prev => ({ ...prev, swish_number: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white"
                placeholder="123-456 78 90"
              />
            </div>

            <div>
              <label className="text-white/80 text-sm block mb-2">Aktivasyon Mesajı</label>
              <textarea
                value={settings.activation_message}
                onChange={(e) => setSettings(prev => ({ ...prev, activation_message: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white h-24 resize-none"
                placeholder="Ödemeniz onaylandıktan sonra..."
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6">
          <label className="text-white/80 text-sm block mb-3">Ödeme Adımları</label>
          <div className="space-y-3">
            {settings.instructions.map((instruction, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-8 h-8 bg-blue-500/30 rounded-full flex items-center justify-center text-blue-300 font-bold text-sm flex-shrink-0">
                  {idx + 1}
                </span>
                <input
                  type="text"
                  value={instruction}
                  onChange={(e) => updateInstruction(idx, e.target.value)}
                  className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
                />
                <button
                  onClick={() => removeInstruction(idx)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addInstruction}
            className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Yeni Adım Ekle
          </button>
          <p className="text-white/40 text-xs mt-2">
            İpucu: <code className="bg-white/10 px-1 rounded">{"{{amount}}"}</code> yazdığınızda otomatik olarak tutar bilgisi gösterilir.
          </p>
        </div>

        {/* Save Button */}
        <div className="mt-6 pt-4 border-t border-white/10 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Ayarları Kaydet
              </>
            )}
          </button>
        </div>
      </div>

      {/* Payment History */}
      {pendingPayments.filter(p => p.status === 'activated').length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Aktif Edilen Ödemeler</h3>
          <div className="space-y-2">
            {pendingPayments.filter(p => p.status === 'activated').slice(0, 10).map((payment) => (
              <div key={payment.id} className="bg-white/5 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{payment.user_name}</p>
                  <p className="text-white/50 text-sm">{payment.user_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{payment.amount} {payment.currency}</p>
                  <p className="text-green-400 text-sm">✓ Aktif</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox Modal for QR Image Preview */}
      {showLightbox && settings.swish_image && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
          data-testid="admin-swish-qr-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 bg-white/10 rounded-full"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={`${settings.swish_image}?t=${Date.now()}`}
            alt="Swish QR Code - Büyük Görünüm" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// HOMEPAGE QUIZ TAB - Ana Sayfa Quiz Yönetimi
function HomepageQuizTab({ questions, fetchQuestions }) {
  const [settings, setSettings] = useState({
    question_count: 10,
    selected_question_ids: [],
    use_random: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default'); // Sıralama filtresi
  const [mediaFilter, setMediaFilter] = useState('all'); // Medya filtresi: all, with_image, with_video, with_svg, no_media
  const [categoryFilter, setCategoryFilter] = useState('all'); // Kategori filtresi
  const [difficultyFilter, setDifficultyFilter] = useState('all'); // Zorluk filtresi
  const [showQuestionSelector, setShowQuestionSelector] = useState(false);
  const [currentQuizQuestions, setCurrentQuizQuestions] = useState([]);  // Şu anda ana sayfada gösterilen sorular
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null); // Ön izleme için seçilen soru
  const [hoverPreview, setHoverPreview] = useState(null); // Hover preview için
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 }); // Hover popup pozisyonu
  const [renumbering, setRenumbering] = useState(false); // Yeniden numaralandırma state
  const adminPassword = localStorage.getItem('admin_password') ;

  // Ana sayfa quiz sorularını yeniden numaralandır
  const handleRenumberQuestions = async () => {
    if (!window.confirm('Quiz\'deki sorular 1\'den başlayarak yeniden numaralandırılacak. Devam etmek istiyor musunuz?')) {
      return;
    }
    
    setRenumbering(true);
    try {
      const response = await axios.post(`${API}/admin/renumber-homepage-quiz`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      
      alert(`✅ ${response.data.message}\nToplam: ${response.data.total_questions} soru`);
      
      // Soruları ve quiz sorularını yeniden yükle
      fetchQuestions();
      fetchCurrentQuizQuestions();
    } catch (error) {
      console.error('Renumber error:', error);
      alert('❌ Numaralandırma hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRenumbering(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCurrentQuizQuestions();  // Ana sayfadaki soruları çek
    if (questions.length === 0) {
      fetchQuestions();
    }
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/homepage-quiz-settings`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching homepage quiz settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ana sayfada şu anda gösterilen soruları çek
  const fetchCurrentQuizQuestions = async () => {
    try {
      setLoadingPreview(true);
      const response = await axios.get(`${API}/homepage-questions`);
      setCurrentQuizQuestions(response.data);
      console.log('Current homepage quiz questions:', response.data);
    } catch (error) {
      console.error('Error fetching current quiz questions:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/homepage-quiz-settings`, settings, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert('✅ Ana sayfa quiz ayarları kaydedildi!');
      fetchCurrentQuizQuestions();  // Yenilenen soruları çek
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = (questionId) => {
    if (!settings.selected_question_ids.includes(questionId)) {
      setSettings(prev => ({
        ...prev,
        selected_question_ids: [...prev.selected_question_ids, questionId],
        use_random: false
      }));
    }
  };

  const removeQuestion = (questionId) => {
    setSettings(prev => ({
      ...prev,
      selected_question_ids: prev.selected_question_ids.filter(id => id !== questionId)
    }));
  };

  const moveQuestion = (fromIndex, toIndex) => {
    const newIds = [...settings.selected_question_ids];
    const [removed] = newIds.splice(fromIndex, 1);
    newIds.splice(toIndex, 0, removed);
    setSettings(prev => ({ ...prev, selected_question_ids: newIds }));
  };

  const getQuestionById = (id) => questions.find(q => q.id === id);

  // Sıralama fonksiyonu
  const sortQuestions = (questionsToSort) => {
    const sorted = [...questionsToSort];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case 'recently_edited':
        return sorted.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      case 'number_asc':
        return sorted.sort((a, b) => {
          const numA = parseInt((a.question_number || '').replace(/\D/g, '')) || 0;
          const numB = parseInt((b.question_number || '').replace(/\D/g, '')) || 0;
          return numA - numB;
        });
      case 'number_desc':
        return sorted.sort((a, b) => {
          const numA = parseInt((a.question_number || '').replace(/\D/g, '')) || 0;
          const numB = parseInt((b.question_number || '').replace(/\D/g, '')) || 0;
          return numB - numA;
        });
      default:
        return sorted;
    }
  };

  // Filtreleme ve sıralama
  const filteredQuestions = sortQuestions(
    questions.filter(q => {
      // Already selected filter
      if (settings.selected_question_ids.includes(q.id)) return false;
      
      // Text search filter
      const matchesSearch = searchTerm === '' || 
        q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.question_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Media filter
      const hasImage = q.image_url || (q.image_urls && q.image_urls.length > 0);
      const hasSvg = (q.image_url && q.image_url.toLowerCase().endsWith('.svg')) || 
                     (q.image_urls && q.image_urls.some(url => url.toLowerCase().endsWith('.svg')));
      const hasVideo = q.video_url;
      
      switch (mediaFilter) {
        case 'with_image':
          if (!hasImage) return false;
          break;
        case 'with_video':
          if (!hasVideo) return false;
          break;
        case 'with_svg':
          if (!hasSvg) return false;
          break;
        case 'with_media':
          if (!hasImage && !hasVideo) return false;
          break;
        case 'no_media':
          if (hasImage || hasVideo) return false;
          break;
      }
      
      // Category filter
      if (categoryFilter !== 'all' && q.category_id !== categoryFilter) return false;
      
      // Difficulty filter
      if (difficultyFilter !== 'all' && q.difficulty !== difficultyFilter) return false;
      
      return true;
    })
  );

  // Get unique categories from questions for filter dropdown
  const availableCategories = [...new Set(questions.map(q => q.category_id).filter(Boolean))];
  
  // Get category names (if available)
  const getCategoryName = (catId) => {
    // Try to find category name from a known list or return ID
    return catId || 'Kategorisiz';
  };

  // Medya türüne göre soru sayılarını hesapla (filtre sayıları için)
  const modalMediaCounts = React.useMemo(() => {
    const availableQuestions = questions.filter(q => !settings.selected_question_ids.includes(q.id));
    
    let withImage = 0, withSvg = 0, withVideo = 0, withMedia = 0, noMedia = 0;
    
    availableQuestions.forEach(q => {
      const hasImg = q.image_url || (q.image_urls && q.image_urls.length > 0);
      const hasSvg = (q.image_url && q.image_url.toLowerCase().endsWith('.svg')) || 
                     (q.image_urls && q.image_urls.some(url => url.toLowerCase().endsWith('.svg')));
      const hasVid = q.video_url;
      
      if (hasImg) withImage++;
      if (hasSvg) withSvg++;
      if (hasVid) withVideo++;
      if (hasImg || hasVid) withMedia++;
      if (!hasImg && !hasVid) noMedia++;
    });
    
    return {
      all: availableQuestions.length,
      with_image: withImage,
      with_svg: withSvg,
      with_video: withVideo,
      with_media: withMedia,
      no_media: noMedia
    };
  }, [questions, settings.selected_question_ids]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ŞU ANDA ANA SAYFADA GÖSTERİLEN SORULAR */}
      <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-white flex items-center gap-3">
            <Eye className="w-8 h-8 text-green-400" />
            🔴 ŞU ANDA ANA SAYFADA GÖSTERİLEN SORULAR
          </h3>
          <button
            onClick={fetchCurrentQuizQuestions}
            className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-xl font-bold transition-all flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPreview ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
        
        {loadingPreview ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-green-400 animate-spin mx-auto" />
            <p className="text-white/60 mt-2">Sorular yükleniyor...</p>
          </div>
        ) : currentQuizQuestions.length > 0 ? (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {currentQuizQuestions.map((question, index) => (
              <div key={question.id || index} className="bg-white/10 rounded-xl p-4 border border-green-500/20">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-black">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-medium mb-2">
                      {question.question_text}
                    </div>
                    
                    {/* Soru Resmi - varsa göster */}
                    {question.image_url && (
                      <div className="mb-3 flex justify-center">
                        <img 
                          src={fixImageUrl(question.image_url)}
                          alt="Soru görseli"
                          className="rounded-lg max-h-32 w-auto object-contain border border-white/20"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {(question.choices || []).map((choice, cIdx) => (
                        <div 
                          key={cIdx}
                          className={`p-2 rounded-lg ${
                            choice.letter === question.correct_answer 
                              ? 'bg-green-500/30 text-green-300 border border-green-400/50' 
                              : 'bg-white/5 text-white/60'
                          }`}
                        >
                          <span className="font-bold">{choice.letter})</span> {choice.text || '⚠️ BOŞ CEVAP'}
                        </div>
                      ))}
                    </div>
                    {/* Warning if any choice is empty */}
                    {(question.choices || []).some(c => !c.text || c.text.trim() === '') && (
                      <div className="mt-2 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                        <div className="flex items-center gap-2 text-red-300 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>⚠️ Bu soruda boş cevap var! Düzenlemeniz gerekiyor.</span>
                        </div>
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-xs text-white/40">
                        Soru {index + 1} | Kategori: {question.category_id?.substring(0,8) || 'N/A'}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Hover Önizleme */}
                        <div 
                          className="relative"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoverPosition({ 
                              x: rect.left - 720,
                              y: Math.min(rect.top - 150, window.innerHeight - 720)
                            });
                            setHoverPreview({...question, displayNumber: index + 1});
                          }}
                          onMouseLeave={() => setHoverPreview(null)}
                          onClick={() => {
                            setHoverPreview(null);
                            setPreviewQuestion({...question, displayNumber: index + 1});
                          }}
                        >
                          <span className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 rounded-lg text-sm cursor-pointer flex items-center gap-1.5 transition-all">
                            <Eye className="w-4 h-4" />
                            Ön İzle
                          </span>
                        </div>
                        {/* Düzenle Butonu */}
                        <button
                          onClick={async () => {
                            // Get the full question data and open edit modal
                            try {
                              const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questions/${question.id}`);
                              const fullQuestion = response.data;
                              setPreviewQuestion(fullQuestion);
                            } catch (error) {
                              // If API fails, use the current question data
                              setPreviewQuestion(question);
                            }
                          }}
                          className="px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-lg text-sm font-bold transition-all flex items-center gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Düzenle
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-white/60">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-yellow-400" />
            <p>Ana sayfada gösterilen soru bulunamadı.</p>
            <p className="text-sm mt-1">Aşağıdan soru ekleyip "Kaydet & Yayınla" butonuna tıklayın.</p>
          </div>
        )}
        
        <div className="mt-4 p-4 bg-green-500/10 rounded-xl border border-green-500/20">
          <div className="flex items-center gap-2 text-green-300 text-sm">
            <CheckCircle className="w-5 h-5" />
            <span>
              <strong>{currentQuizQuestions.length}</strong> soru şu anda ana sayfada gösteriliyor.
              {settings.use_random && ' (Rastgele seçim modu aktif)'}
            </span>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
        <h2 className="text-3xl font-black text-white mb-2 flex items-center gap-3">
          <Play className="w-10 h-10 text-purple-400" />
          Ana Sayfa Quiz Yönetimi
        </h2>
        <p className="text-white/60">Ana sayfadaki ücretsiz deneme quiz'ini düzenleyin</p>
      </div>

      {/* Yeniden Numaralandır Butonu */}
      <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-2xl p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-bold text-orange-300 mb-1">Quiz Soru Numaralandırma</h3>
            <p className="text-white/60 text-sm">Sadece bu quiz'deki soruları (seçili {settings.selected_question_ids?.length || 0} soru) 1'den başlayarak yeniden numaralandırır.</p>
          </div>
          <button
            onClick={handleRenumberQuestions}
            disabled={renumbering}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg"
          >
            {renumbering ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Numaralandırılıyor...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Quiz Sorularını Numaralandır
              </>
            )}
          </button>
        </div>
      </div>

      {/* Settings Card */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Settings className="w-6 h-6 text-purple-400" />
          Quiz Ayarları
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Question Count */}
          <div>
            <label className="block text-white/70 text-sm font-medium mb-2">
              Soru Sayısı
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={settings.question_count}
              onChange={(e) => setSettings(prev => ({ ...prev, question_count: parseInt(e.target.value) || 10 }))}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-400"
            />
          </div>

          {/* Random Mode Toggle */}
          <div>
            <label className="block text-white/70 text-sm font-medium mb-2">
              Soru Seçim Modu
            </label>
            <div className="flex gap-4">
              <button
                onClick={() => setSettings(prev => ({ ...prev, use_random: true }))}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  settings.use_random 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                🎲 Rastgele
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, use_random: false }))}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  !settings.use_random 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                ✋ Manuel Seçim
              </button>
            </div>
          </div>
        </div>

        {/* Selected Questions */}
        {!settings.use_random && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">
                Seçilen Sorular ({settings.selected_question_ids.filter(id => getQuestionById(id)).length}/{settings.question_count})
              </h4>
              <button
                onClick={() => setShowQuestionSelector(true)}
                className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-xl flex items-center gap-2 transition-all"
              >
                <Plus className="w-5 h-5" />
                Soru Ekle
              </button>
            </div>

            {settings.selected_question_ids.filter(id => getQuestionById(id)).length === 0 ? (
              <div className="bg-white/5 border border-dashed border-white/20 rounded-xl p-8 text-center">
                <BookOpen className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">Henüz soru seçilmedi. &quot;Soru Ekle&quot; butonuna tıklayın.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {settings.selected_question_ids
                  .map(id => ({ id, question: getQuestionById(id) }))
                  .filter(item => item.question !== null && item.question !== undefined)
                  .map((item, index) => {
                  const { id, question } = item;
                  return (
                    <div key={id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-4">
                      <div className="text-white/50 font-bold w-8">{index + 1}</div>
                      <div className="flex-1">
                        {/* Soru Numarası - Sıra numarasını göster */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-purple-500/30 text-purple-300 rounded text-xs font-bold">
                            Soru {index + 1}
                          </span>
                          {question.image_url && (
                            <span className="px-2 py-0.5 bg-blue-500/30 text-blue-300 rounded text-xs">📷 Resim</span>
                          )}
                        </div>
                        <div className="text-white text-sm line-clamp-1">{question.question_text}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Ön İzleme Butonu - Hem hover hem tıklama */}
                        <div 
                          className="relative"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoverPosition({ 
                              x: rect.left - 720,
                              y: Math.min(rect.top - 150, window.innerHeight - 720)
                            });
                            setHoverPreview({...question, displayNumber: index + 1});
                          }}
                          onMouseLeave={() => setHoverPreview(null)}
                          onClick={() => {
                            setHoverPreview(null);
                            setPreviewQuestion({...question, displayNumber: index + 1});
                          }}
                        >
                          <span className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 rounded-lg text-sm cursor-pointer flex items-center gap-1.5 transition-all whitespace-nowrap">
                            <Eye className="w-4 h-4" />
                            Ön İzleme
                          </span>
                        </div>
                        <button
                          onClick={() => index > 0 && moveQuestion(index, index - 1)}
                          disabled={index === 0}
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => index < settings.selected_question_ids.length - 1 && moveQuestion(index, index + 1)}
                          disabled={index === settings.selected_question_ids.length - 1}
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeQuestion(id)}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={saveSettings}
          disabled={saving}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
        >
          {saving ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Yayınlanıyor...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              🚀 KAYDET & ANA SAYFAYA YAYINLA
            </>
          )}
        </button>
      </div>

      {/* Question Selector Modal */}
      {showQuestionSelector && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Soru Seç</h3>
              <button
                onClick={() => setShowQuestionSelector(false)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <XCircle className="w-6 h-6 text-white/60" />
              </button>
            </div>

            <div className="p-4 border-b border-white/10 space-y-3">
              {/* Arama ve Sıralama Satırı */}
              <div className="flex gap-3">
                {/* Arama Kutusu */}
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="text"
                    placeholder="Soru numarası ile ara (örn: Q-00365) veya metin ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-400"
                  />
                </div>
                
                {/* Sıralama Dropdown */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-purple-500/20 border border-purple-500/30 text-white rounded-xl px-4 py-3 pr-10 cursor-pointer focus:outline-none focus:border-purple-400 min-w-[180px]"
                  >
                    <option value="default" className="bg-slate-800">📋 Varsayılan</option>
                    <option value="newest" className="bg-slate-800">🆕 Son Eklenen</option>
                    <option value="oldest" className="bg-slate-800">📜 İlk Eklenen</option>
                    <option value="recently_edited" className="bg-slate-800">✏️ Son Düzenlenen</option>
                    <option value="number_asc" className="bg-slate-800">🔢 No: Küçük→Büyük</option>
                    <option value="number_desc" className="bg-slate-800">🔢 No: Büyük→Küçük</option>
                  </select>
                  <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400 pointer-events-none" />
                </div>
              </div>
              
              {/* İkinci Satır: Medya, Kategori ve Zorluk Filtreleri */}
              <div className="flex gap-3 flex-wrap">
                {/* Medya Filtresi */}
                <select
                  value={mediaFilter}
                  onChange={(e) => setMediaFilter(e.target.value)}
                  className="appearance-none bg-green-500/20 border border-green-500/30 text-white rounded-xl px-4 py-2.5 cursor-pointer focus:outline-none focus:border-green-400 text-sm"
                >
                  <option value="all" className="bg-slate-800">📁 Tüm Medya ({modalMediaCounts.all})</option>
                  <option value="with_image" className="bg-slate-800">🖼️ Resimli ({modalMediaCounts.with_image})</option>
                  <option value="with_svg" className="bg-slate-800">📐 SVG Resimli ({modalMediaCounts.with_svg})</option>
                  <option value="with_video" className="bg-slate-800">🎬 Videolu ({modalMediaCounts.with_video})</option>
                  <option value="with_media" className="bg-slate-800">📎 Medyalı ({modalMediaCounts.with_media})</option>
                  <option value="no_media" className="bg-slate-800">📝 Medyasız ({modalMediaCounts.no_media})</option>
                </select>
                
                {/* Zorluk Filtresi */}
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="appearance-none bg-orange-500/20 border border-orange-500/30 text-white rounded-xl px-4 py-2.5 cursor-pointer focus:outline-none focus:border-orange-400 text-sm"
                >
                  <option value="all" className="bg-slate-800">⚡ Tüm Zorluklar</option>
                  <option value="easy" className="bg-slate-800">🟢 Kolay</option>
                  <option value="medium" className="bg-slate-800">🟡 Orta</option>
                  <option value="hard" className="bg-slate-800">🔴 Zor</option>
                </select>
                
                {/* Hızlı Filtre Butonları */}
                <div className="flex gap-2">
                  <button
                    onClick={() => { setMediaFilter('all'); setDifficultyFilter('all'); setCategoryFilter('all'); setSearchTerm(''); }}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl text-sm font-medium transition-all"
                  >
                    🔄 Sıfırla
                  </button>
                </div>
              </div>
              
              {/* Aktif Filtre Gösterimi */}
              <div className="flex items-center justify-between text-white/50 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>💡 İpucu: Soru numarası (Q-XXXXX), ID veya metin ile arama yapabilirsiniz</span>
                </div>
                <div className="flex items-center gap-3">
                  {mediaFilter !== 'all' && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs">
                      {mediaFilter === 'with_image' ? '🖼️ Resimli' : 
                       mediaFilter === 'with_svg' ? '📐 SVG' : 
                       mediaFilter === 'with_video' ? '🎬 Videolu' : 
                       mediaFilter === 'with_media' ? '📎 Medyalı' : '📝 Medyasız'}
                    </span>
                  )}
                  {difficultyFilter !== 'all' && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded text-xs">
                      {difficultyFilter === 'easy' ? '🟢 Kolay' : difficultyFilter === 'medium' ? '🟡 Orta' : '🔴 Zor'}
                    </span>
                  )}
                  <span className="text-purple-400">{filteredQuestions.length} sonuç</span>
                </div>
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {filteredQuestions.length === 0 ? (
                <div className="text-center py-8 text-white/50">
                  <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Arama sonucu bulunamadı.</p>
                  <p className="text-sm mt-1">Farklı bir terim deneyin veya soru numarasını tam yazın.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-white/40 text-sm mb-3">{filteredQuestions.length} soru bulundu (ilk 100 gösteriliyor)</p>
                  {filteredQuestions.slice(0, 100).map((question, qIndex) => (
                    <div key={question.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          {/* Soru Numarası - Sıra numarası göster */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-3 py-1 bg-purple-500/30 text-purple-300 rounded-lg text-sm font-bold">
                              Soru {qIndex + 1}
                            </span>
                            {question.image_url && (
                              <span className="px-2 py-1 bg-blue-500/30 text-blue-300 rounded text-xs">
                                {question.image_url.toLowerCase().endsWith('.svg') ? '📐 SVG' : '🖼️ Resim'}
                              </span>
                            )}
                            {question.image_urls && question.image_urls.length > 0 && (
                              <span className="px-2 py-1 bg-cyan-500/30 text-cyan-300 rounded text-xs">
                                📎 +{question.image_urls.length} ek resim
                              </span>
                            )}
                            {question.video_url && (
                              <span className="px-2 py-1 bg-pink-500/30 text-pink-300 rounded text-xs">🎬 Video</span>
                            )}
                            {question.difficulty && (
                              <span className={`px-2 py-1 rounded text-xs ${
                                question.difficulty === 'easy' ? 'bg-green-500/30 text-green-300' :
                                question.difficulty === 'hard' ? 'bg-red-500/30 text-red-300' :
                                'bg-yellow-500/30 text-yellow-300'
                              }`}>
                                {question.difficulty === 'easy' ? '🟢 Kolay' : 
                                 question.difficulty === 'hard' ? '🔴 Zor' : '🟡 Orta'}
                              </span>
                            )}
                            {question.category_id && (
                              <span className="px-2 py-1 bg-gray-500/30 text-gray-300 rounded text-xs">{question.category_id}</span>
                            )}
                          </div>
                          <div className="text-white text-sm line-clamp-2">{question.question_text}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Ön İzleme Butonu - Hem hover hem tıklama */}
                          <div 
                            className="relative"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setHoverPosition({ 
                                x: rect.left - 720,
                                y: Math.min(rect.top - 150, window.innerHeight - 720)
                              });
                              setHoverPreview(question);
                            }}
                            onMouseLeave={() => setHoverPreview(null)}
                            onClick={() => {
                              setHoverPreview(null);
                              setPreviewQuestion(question);
                            }}
                          >
                            <span className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 rounded-lg flex items-center gap-1 cursor-pointer transition-all">
                              <Eye className="w-4 h-4" />
                              Ön İzle
                            </span>
                          </div>
                          {/* Ekle Butonu */}
                          <button
                            onClick={() => {
                              addQuestion(question.id);
                              // Modal'ı kapatma, birden fazla soru ekleyebilsin
                            }}
                            className="px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Ekle
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Seçilen soru sayısı göstergesi */}
            <div className="p-4 border-t border-white/10 bg-white/5">
              <div className="flex items-center justify-between">
                <span className="text-white/60">
                  Seçili: <strong className="text-white">{settings.selected_question_ids.length}</strong> soru
                </span>
                <button
                  onClick={() => setShowQuestionSelector(false)}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium"
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hover Preview Popup - Ana Sayfa Quiz için 2x BÜYÜK */}
      {hoverPreview && (
        <div 
          className="fixed z-[70] bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 border-2 border-purple-500/70 rounded-2xl shadow-2xl w-[700px] max-h-[700px] overflow-hidden pointer-events-none"
          style={{ 
            left: Math.max(10, Math.min(hoverPosition.x, window.innerWidth - 720)),
            top: Math.max(10, Math.min(hoverPosition.y, window.innerHeight - 720)),
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 bg-purple-500/20">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 bg-purple-500/30 text-purple-200 rounded-lg text-base font-bold">
                {hoverPreview.displayNumber ? `Soru ${hoverPreview.displayNumber}` : `ID: ${hoverPreview.id?.substring(0,8) || '?'}`}
              </span>
              {hoverPreview.difficulty && (
                <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  hoverPreview.difficulty === 'easy' ? 'bg-green-500/20 text-green-300' :
                  hoverPreview.difficulty === 'hard' ? 'bg-red-500/20 text-red-300' :
                  'bg-yellow-500/20 text-yellow-300'
                }`}>
                  {hoverPreview.difficulty === 'easy' ? '🟢 Kolay' : hoverPreview.difficulty === 'hard' ? '🔴 Zor' : '🟡 Orta'}
                </span>
              )}
              {hoverPreview.image_url && (
                <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">📷 Resimli</span>
              )}
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4 overflow-y-auto max-h-[600px]">
            {/* Question Text */}
            <p className="text-white text-lg leading-relaxed">{hoverPreview.question_text}</p>
            
            {/* Image if exists - BÜYÜK */}
            {hoverPreview.image_url && (
              <div className="flex justify-center bg-slate-800/50 rounded-xl p-4">
                <img 
                  src={fixImageUrl(hoverPreview.image_url)}
                  alt="Soru görseli" 
                  className="rounded-lg max-h-64 w-auto object-contain border border-white/20"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            {/* Choices - BÜYÜK */}
            <div className="space-y-2">
              {hoverPreview.choices?.map((choice, idx) => (
                <div 
                  key={choice.letter || idx}
                  className={`p-3 rounded-xl text-base flex items-center gap-3 ${
                    choice.letter === hoverPreview.correct_answer
                      ? 'bg-green-500/20 border-2 border-green-400/50 text-green-100'
                      : 'bg-white/5 border border-white/10 text-white/80'
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                    choice.letter === hoverPreview.correct_answer
                      ? 'bg-green-500 text-white'
                      : 'bg-white/20 text-white'
                  }`}>
                    {choice.letter}
                  </span>
                  <span className="flex-1 text-sm">{choice.text || '—'}</span>
                  {choice.letter === hoverPreview.correct_answer && (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                </div>
              ))}
            </div>
            
            {/* Explanation preview - BÜYÜK */}
            {hoverPreview.explanation && (
              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                <p className="text-blue-300 text-sm font-semibold mb-2">💡 Açıklama:</p>
                <p className="text-white/80 text-sm leading-relaxed line-clamp-4">{hoverPreview.explanation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Question Preview Modal */}
      {previewQuestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-purple-500/30 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-blue-900/50">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-3 py-1 bg-purple-500/30 text-purple-300 rounded-lg text-sm font-bold">
                    {previewQuestion.displayNumber ? `Soru ${previewQuestion.displayNumber}` : `ID: ${previewQuestion.id?.substring(0, 8)}`}
                  </span>
                  {previewQuestion.image_url && (
                    <span className="px-2 py-1 bg-blue-500/30 text-blue-300 rounded text-xs">📷 Resimli</span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-white">Soru Ön İzleme</h3>
              </div>
              <button
                onClick={() => setPreviewQuestion(null)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <XCircle className="w-6 h-6 text-white/60" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Soru Metni */}
              <div className="bg-white/10 rounded-xl p-4 mb-4">
                <p className="text-white text-lg font-medium">{previewQuestion.question_text}</p>
              </div>

              {/* Resim varsa göster */}
              {previewQuestion.image_url && (
                <div className="mb-4 flex justify-center">
                  <div className="bg-white/5 rounded-xl p-3 inline-block">
                    <ImageLightbox 
                      src={fixImageUrl(previewQuestion.image_url)}
                      alt="Soru görseli"
                      className="max-h-48 rounded-lg"
                      containerClassName="relative"
                    />
                  </div>
                </div>
              )}

              {/* Cevap Seçenekleri */}
              <div className="space-y-2 mb-4">
                {(previewQuestion.choices || []).map((choice, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-xl border ${
                      choice.letter === previewQuestion.correct_answer
                        ? 'bg-green-500/20 border-green-500/50 text-green-300'
                        : 'bg-white/5 border-white/10 text-white/80'
                    }`}
                  >
                    <span className="font-bold">{choice.letter})</span> {choice.text}
                    {choice.letter === previewQuestion.correct_answer && (
                      <span className="ml-2 text-green-400">✓ Doğru Cevap</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Açıklama */}
              {previewQuestion.explanation && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <h4 className="text-blue-300 font-bold mb-2">📚 Açıklama</h4>
                  <p className="text-white/80" style={{ whiteSpace: 'pre-wrap' }}>{previewQuestion.explanation}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/10 flex items-center justify-between">
              <div className="text-white/50 text-sm">
                Kategori: {previewQuestion.category_id?.substring(0,8) || 'Belirtilmemiş'}
              </div>
              <div className="flex items-center gap-2">
                {/* Düzenle Butonu - Soru Bankasına Git */}
                <button
                  onClick={async () => {
                    // Save question ID and close modal, then scroll to questions tab
                    const questionId = previewQuestion.id;
                    setPreviewQuestion(null);
                    
                    // Try to find the question in questions array and set for editing
                    const foundQuestion = questions.find(q => q.id === questionId);
                    if (foundQuestion) {
                      // Dispatch event to main admin component to switch tab and edit
                      window.dispatchEvent(new CustomEvent('adminEditQuestion', { 
                        detail: { question: foundQuestion, switchTab: 'questions' }
                      }));
                    } else {
                      alert('Soru bankasında soru bulunamadı. Lütfen "Sorular" sekmesinden arayın.');
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Düzenle
                </button>
                <button
                  onClick={() => {
                    addQuestion(previewQuestion.id);
                    setPreviewQuestion(null);
                  }}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Quiz'e Ekle
                </button>
                <button
                  onClick={() => setPreviewQuestion(null)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// WELCOME SETTINGS TAB
function WelcomeSettingsTab() {
  const [settings, setSettings] = useState({
    duration: 4,
    theme: 'purple',
    show_confetti: true,
    show_rings: true,
    show_sparkles: true,
    title_text: 'Hoş Geldiniz!',
    subtitle_text: 'İsveç Ehliyet Sınavına Hazırsınız!',
    icon: 'crown'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  const adminPassword = localStorage.getItem('admin_password');
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/welcome-settings`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setSettings(response.data);
    } catch (error) {
      console.log('Using default settings');
    }
    setLoading(false);
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/welcome-settings`, settings, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert('✅ Hoş geldin ayarları kaydedildi!');
    } catch (error) {
      alert('❌ Kaydetme hatası: ' + error.message);
    }
    setSaving(false);
  };
  
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-white/60">Yükleniyor...</p>
      </div>
    );
  }
  
  const themes = [
    { id: 'purple', name: 'Mor/Mavi', color: 'from-purple-500 to-blue-500' },
    { id: 'red', name: 'Kırmızı/Beyaz', color: 'from-red-500 to-red-700' },
    { id: 'blue', name: 'Mavi', color: 'from-blue-500 to-cyan-500' },
    { id: 'green', name: 'Yeşil', color: 'from-green-500 to-emerald-500' }
  ];
  
  const icons = [
    { id: 'crown', name: 'Taç 👑', emoji: '👑' },
    { id: 'star', name: 'Yıldız ⭐', emoji: '⭐' },
    { id: 'trophy', name: 'Kupa 🏆', emoji: '🏆' },
    { id: 'car', name: 'Araba 🚗', emoji: '🚗' }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
        <h2 className="text-3xl font-black text-white mb-2">✨ Hoş Geldin Ekranı Ayarları</h2>
        <p className="text-white/60">Kullanıcıların giriş yaptığında göreceği hoş geldin animasyonunu özelleştirin</p>
      </div>
      
      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Duration */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Süre
          </h3>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="2"
              max="10"
              value={settings.duration}
              onChange={(e) => setSettings({...settings, duration: parseInt(e.target.value)})}
              className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-2xl font-bold text-white w-16 text-center">{settings.duration}s</span>
          </div>
          <p className="text-white/50 text-sm mt-2">Hoş geldin ekranı {settings.duration} saniye görüntülenecek</p>
        </div>
        
        {/* Theme Selection */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5" /> Tema
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => setSettings({...settings, theme: theme.id})}
                className={`p-4 rounded-xl border-2 transition-all ${
                  settings.theme === theme.id 
                    ? 'border-white bg-white/20' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className={`w-full h-8 rounded-lg bg-gradient-to-r ${theme.color} mb-2`}></div>
                <span className="text-white font-medium">{theme.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Icon Selection */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            🎨 İkon
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {icons.map((icon) => (
              <button
                key={icon.id}
                onClick={() => setSettings({...settings, icon: icon.id})}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  settings.icon === icon.id 
                    ? 'border-white bg-white/20' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-3xl">{icon.emoji}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Effects Toggles */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            🎆 Efektler
          </h3>
          <div className="space-y-4">
            {[
              { key: 'show_confetti', label: 'Konfeti', desc: 'Uçuşan parçacıklar' },
              { key: 'show_rings', label: 'Dönen Halkalar', desc: 'Animasyonlu çemberler' },
              { key: 'show_sparkles', label: 'Parıltılar', desc: 'Yıldız emojileri' }
            ].map((effect) => (
              <div key={effect.key} className="flex items-center justify-between">
                <div>
                  <span className="text-white font-medium">{effect.label}</span>
                  <p className="text-white/50 text-sm">{effect.desc}</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, [effect.key]: !settings[effect.key]})}
                  className={`w-14 h-7 rounded-full transition-all ${
                    settings[effect.key] ? 'bg-green-500' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings[effect.key] ? 'translate-x-8' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Text Settings */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          📝 Yazılar
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/80 text-sm mb-2">Başlık</label>
            <input
              type="text"
              value={settings.title_text}
              onChange={(e) => setSettings({...settings, title_text: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
              placeholder="Hoş Geldiniz!"
            />
          </div>
          <div>
            <label className="block text-white/80 text-sm mb-2">Alt Başlık</label>
            <input
              type="text"
              value={settings.subtitle_text}
              onChange={(e) => setSettings({...settings, subtitle_text: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
              placeholder="İsveç Ehliyet Sınavına Hazırsınız!"
            />
          </div>
        </div>
      </div>
      
      {/* Preview & Save */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => setPreviewMode(true)}
          className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-bold transition-all flex items-center gap-2"
        >
          👁️ Önizleme
        </button>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-bold transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
        </button>
      </div>
      
      {/* Preview Modal */}
      {previewMode && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center cursor-pointer overflow-hidden"
          onClick={() => setPreviewMode(false)}
          style={{
            background: settings.theme === 'red' 
              ? 'radial-gradient(ellipse at center, rgba(185, 28, 28, 0.9) 0%, rgba(69, 10, 10, 0.98) 70%)'
              : settings.theme === 'blue'
              ? 'radial-gradient(ellipse at center, rgba(30, 64, 175, 0.9) 0%, rgba(15, 23, 42, 0.98) 70%)'
              : settings.theme === 'green'
              ? 'radial-gradient(ellipse at center, rgba(21, 128, 61, 0.9) 0%, rgba(15, 23, 42, 0.98) 70%)'
              : 'radial-gradient(ellipse at center, rgba(88, 28, 135, 0.8) 0%, rgba(15, 23, 42, 0.98) 70%)'
          }}
        >
          <div className="text-center">
            <div className="text-6xl mb-6">
              {settings.icon === 'star' ? '⭐' : settings.icon === 'trophy' ? '🏆' : settings.icon === 'car' ? '🚗' : '👑'}
            </div>
            <h2 className="text-5xl font-black text-white mb-4">{settings.title_text} 🎉</h2>
            <p className="text-2xl text-white/80 mb-6">[Kullanıcı Adı]</p>
            <p className="text-xl text-white/60">🚗 {settings.subtitle_text} 🇸🇪</p>
            <p className="text-white/40 text-sm mt-8">Kapatmak için tıklayın • {settings.duration} saniye</p>
          </div>
        </div>
      )}
    </div>
  );
}

// TESTIMONIALS TAB
function TestimonialsTab({ testimonials, fetchTestimonials }) {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', location: '', rating: 5, text: '', date: '' });
  const [isAdding, setIsAdding] = useState(false);

  const handleEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData(testimonial);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({ name: '', location: '', rating: 5, text: '', date: '' });
  };

  const handleSave = async () => {
    try {
      const adminPassword = localStorage.getItem('admin_password');
      
      if (editingId) {
        // Update existing
        await axios.put(
          `${API}/admin/testimonials/${editingId}`,
          formData,
          { headers: { 'X-Admin-Password': adminPassword } }
        );
      } else {
        // Create new
        await axios.post(
          `${API}/admin/testimonials`,
          formData,
          { headers: { 'X-Admin-Password': adminPassword } }
        );
      }
      
      await fetchTestimonials();
      handleCancel();
    } catch (error) {
      console.error('Save testimonial error:', error);
      alert('Hata: ' + (error.response?.data?.detail || 'Yorum kaydedilemedi'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;
    
    try {
      const adminPassword = localStorage.getItem('admin_password');
      await axios.delete(
        `${API}/admin/testimonials/${id}`,
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      await fetchTestimonials();
    } catch (error) {
      console.error('Delete testimonial error:', error);
      alert('Hata: Yorum silinemedi');
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Star className="w-6 h-6 text-yellow-400" />
          Kullanıcı Yorumları
        </h2>
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Yeni Yorum Ekle
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="bg-white/10 rounded-xl p-6 mb-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4">
            {editingId ? 'Yorumu Düzenle' : 'Yeni Yorum Ekle'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="İsim"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <input
              type="text"
              placeholder="Şehir"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <select
              value={formData.rating}
              onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              {[5, 4, 3, 2, 1].map(r => (
                <option key={r} value={r}>{r} Yıldız</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Tarih (ör: Aralık 2026)"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>
          <textarea
            placeholder="Yorum metni"
            value={formData.text}
            onChange={(e) => setFormData({...formData, text: e.target.value})}
            rows={3}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow-500 mb-4"
          />
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
            >
              Kaydet
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Testimonials List */}
      <div className="space-y-4">
        {testimonials.length === 0 ? (
          <p className="text-white/60 text-center py-8">Henüz yorum yok</p>
        ) : (
          testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white/10 rounded-xl p-6 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-white">{testimonial.name}</h3>
                    <span className="text-white/60">• {testimonial.location}</span>
                  </div>
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(testimonial)}
                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-white/90 italic mb-3">&quot;{testimonial.text}&quot;</p>
              <p className="text-white/50 text-sm">{testimonial.date}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// REPORTED QUESTIONS TAB - 3 Tab System (Bekleyen, İncelendi, Silinen)
function ReportedQuestionsTab({ questionReports, fetchQuestionReports, updateReportStatus, setActiveTab, setSearchTerm, setPreviewQuestion }) {
  const [activeReportTab, setActiveReportTab] = useState('pending'); // pending, reviewed, deleted
  const [deletedQuestions, setDeletedQuestions] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const adminPassword = localStorage.getItem('admin_password');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Fetch deleted questions
  const fetchDeletedQuestions = async () => {
    setLoadingDeleted(true);
    try {
      const res = await axios.get(`${API}/admin/deleted-questions`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setDeletedQuestions(res.data || []);
    } catch (err) {
      console.error('Silinen sorular yüklenemedi:', err);
      setDeletedQuestions([]);
    } finally {
      setLoadingDeleted(false);
    }
  };

  useEffect(() => {
    if (activeReportTab === 'deleted') {
      fetchDeletedQuestions();
    }
  }, [activeReportTab]);

  const pendingReports = questionReports.filter(r => r.status === 'pending');
  const reviewedReports = questionReports.filter(r => r.status === 'reviewed');

  // Delete report (just from reports, not from question bank)
  const deleteReport = async (reportId) => {
    if (!window.confirm('Bu bildirimi silmek istediğinize emin misiniz?')) return;
    try {
      try {
        await axios.delete(`${API}/admin/question-reports/${reportId}`, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      } catch (e) {
        await axios.delete(`${API}/admin/error-reports/${reportId}`, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      }
      fetchQuestionReports();
    } catch (error) {
      console.error('Bildirim silinemedi:', error);
      alert('Bildirim silinemedi');
    }
  };

  // Delete question from question bank AND add to deleted list
  const deleteQuestionPermanently = async (report) => {
    if (!window.confirm(`Bu soruyu soru bankasından KALICI olarak silmek istediğinize emin misiniz?\n\nSoru: ${report.question_text_preview?.substring(0, 100)}...`)) return;
    try {
      // Delete from question bank
      await axios.delete(`${API}/admin/questions/${report.question_id}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      
      // Add to deleted questions list
      await axios.post(`${API}/admin/deleted-questions`, {
        question_id: report.question_id,
        question_number: report.question_number,
        question_text: report.question_text_preview,
        reason: report.reason,
        deleted_by: 'admin',
        report_id: report.id
      }, {
        headers: { 'X-Admin-Password': adminPassword }
      });

      // Remove the report
      await deleteReport(report.id);
      
      alert('Soru başarıyla silindi!');
      fetchQuestionReports();
    } catch (error) {
      console.error('Soru silinemedi:', error);
      alert('Soru silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Permanently delete from deleted list
  const removeFromDeletedList = async (deletedId) => {
    if (!window.confirm('Bu kaydı silinen sorular listesinden kaldırmak istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/deleted-questions/${deletedId}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchDeletedQuestions();
    } catch (error) {
      console.error('Kayıt silinemedi:', error);
      alert('Kayıt silinemedi');
    }
  };

  const ReportCard = ({ report, showMarkReviewed = false, showDeleteQuestion = false }) => (
    <div className="p-6 bg-white/5 hover:bg-white/10 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span className={`px-3 py-1 rounded-lg text-sm font-bold ${
              report.status === 'pending' 
                ? 'bg-yellow-500/20 text-yellow-300' 
                : 'bg-green-500/20 text-green-300'
            }`}>
              {report.status === 'pending' ? '⏳ Bekliyor' : '✅ İncelendi'}
            </span>
            <span className="text-white/40 text-sm">
              {report.created_at ? new Date(report.created_at).toLocaleString('tr-TR') : 'Tarih yok'}
            </span>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-blue-400 text-sm font-mono">ID: {report.question_id}</span>
              {report.question_number && (
                <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-sm font-bold">
                  {report.question_number}
                </span>
              )}
            </div>
            <div className="text-white">{report.question_text_preview || 'Soru metni yok'}</div>
          </div>
          
          <div className="bg-red-500/10 rounded-xl p-4">
            <div className="text-red-300 text-sm font-semibold mb-1">Hata Açıklaması:</div>
            <div className="text-white">{report.reason}</div>
          </div>
          
          {report.user_id && (
            <div className="text-white/40 text-sm mt-3">
              Bildiren: {report.user_id}
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          {showMarkReviewed && report.status === 'pending' && (
            <button
              onClick={() => updateReportStatus(report.id, 'reviewed')}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              İncelendi
            </button>
          )}
          <button
            onClick={() => {
              const searchValue = report.question_number || report.question_id || '';
              setActiveTab('questions');
              setTimeout(() => {
                setSearchTerm(searchValue);
                window.scrollTo(0, 0);
              }, 100);
            }}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Soruya Git
          </button>
          <button
            onClick={() => {
              setPreviewQuestion({
                question_number: report.question_number,
                question_text: report.question_text_preview,
                question_id: report.question_id,
                error_description: report.reason,
                reported_by: report.user_id
              });
            }}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Önizle
          </button>
          {showDeleteQuestion && (
            <button
              onClick={() => deleteQuestionPermanently(report)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Soruyu Sil
            </button>
          )}
          <button
            onClick={() => deleteReport(report.id)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
          >
            <XCircle className="w-4 h-4" />
            Bildirimi Kaldır
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6">
        <h2 className="text-3xl font-black text-white mb-2 flex items-center gap-3">
          <AlertCircle className="w-10 h-10 text-red-400" />
          Hatalı Soru Bildirimleri
        </h2>
        <p className="text-white/60">Kullanıcıların bildirdiği hatalı sorular</p>
      </div>
      
      {/* Tab Buttons */}
      <div className="flex gap-2 bg-white/5 p-2 rounded-xl">
        <button
          onClick={() => setActiveReportTab('pending')}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2 ${
            activeReportTab === 'pending'
              ? 'bg-yellow-500 text-black'
              : 'bg-transparent text-yellow-400 hover:bg-yellow-500/20'
          }`}
        >
          <Clock className="w-5 h-5" />
          Bekleyen ({pendingReports.length})
        </button>
        <button
          onClick={() => setActiveReportTab('reviewed')}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2 ${
            activeReportTab === 'reviewed'
              ? 'bg-green-500 text-black'
              : 'bg-transparent text-green-400 hover:bg-green-500/20'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          İncelendi ({reviewedReports.length})
        </button>
        <button
          onClick={() => setActiveReportTab('deleted')}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2 ${
            activeReportTab === 'deleted'
              ? 'bg-red-500 text-white'
              : 'bg-transparent text-red-400 hover:bg-red-500/20'
          }`}
        >
          <Trash2 className="w-5 h-5" />
          Silinen Sorular ({deletedQuestions.length})
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
        {/* Pending Tab */}
        {activeReportTab === 'pending' && (
          pendingReports.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <p className="text-white/60 text-xl">Bekleyen bildirim yok</p>
              <p className="text-white/40 mt-2">Tüm bildirimler incelendi</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {pendingReports.map((report, idx) => (
                <ReportCard 
                  key={report.id || idx} 
                  report={report} 
                  showMarkReviewed={true}
                  showDeleteQuestion={true}
                />
              ))}
            </div>
          )
        )}

        {/* Reviewed Tab */}
        {activeReportTab === 'reviewed' && (
          reviewedReports.length === 0 ? (
            <div className="text-center py-20">
              <Eye className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <p className="text-white/60 text-xl">İncelenen bildirim yok</p>
              <p className="text-white/40 mt-2">İncelenen bildirimler burada görünecek</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {reviewedReports.map((report, idx) => (
                <ReportCard 
                  key={report.id || idx} 
                  report={report} 
                  showMarkReviewed={false}
                  showDeleteQuestion={true}
                />
              ))}
            </div>
          )
        )}

        {/* Deleted Tab */}
        {activeReportTab === 'deleted' && (
          loadingDeleted ? (
            <div className="text-center py-20">
              <RefreshCw className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-spin" />
              <p className="text-white/60 text-xl">Yükleniyor...</p>
            </div>
          ) : deletedQuestions.length === 0 ? (
            <div className="text-center py-20">
              <Trash2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-white/60 text-xl">Silinen soru yok</p>
              <p className="text-white/40 mt-2">Soru bankasından silinen sorular burada görünecek</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {deletedQuestions.map((deleted, idx) => (
                <div key={deleted.id || idx} className="p-6 bg-red-500/5 hover:bg-red-500/10 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 rounded-lg text-sm font-bold bg-red-500/20 text-red-300">
                          🗑️ Silindi
                        </span>
                        <span className="text-white/40 text-sm">
                          {deleted.deleted_at ? new Date(deleted.deleted_at).toLocaleString('tr-TR') : 'Tarih yok'}
                        </span>
                      </div>
                      
                      <div className="bg-white/5 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-blue-400 text-sm font-mono">ID: {deleted.question_id}</span>
                          {deleted.question_number && (
                            <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-sm font-bold">
                              {deleted.question_number}
                            </span>
                          )}
                        </div>
                        <div className="text-white">{deleted.question_text || 'Soru metni yok'}</div>
                      </div>
                      
                      {deleted.reason && (
                        <div className="bg-red-500/10 rounded-xl p-4">
                          <div className="text-red-300 text-sm font-semibold mb-1">Silinme Sebebi:</div>
                          <div className="text-white">{deleted.reason}</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => removeFromDeletedList(deleted.id)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Kayıttan Kaldır
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}


// SSS TAB - Sık Sorulan Sorular Yönetimi
function SSSTab() {
  // Default FAQ data - same as SSS.jsx
  const DEFAULT_FAQ = [
    {
      category: "Teori Sınavı Hakkında",
      questions: [
        { q: "İsveç ehliyet sınavı kaç soru?", a: "İsveç ehliyet teorik sınavı (Teoriprov) 70 sorudan oluşur. Geçmek için en az 52 soruyu (%74) doğru cevaplamanız gerekir. Sınav süresi 50 dakikadır ve Türkçe dahil 13 farklı dilde yapılabilir." },
        { q: "Ehliyet sınavı Türkçe yapılabilir mi?", a: "Evet! İsveç'te Trafikverket teorik sınavı (teoriprov) Türkçe dahil 13 farklı dilde yapılabilmektedir. Sınav merkezinde tercüman eşliğinde değil, bilgisayarda Türkçe sorular çözersiniz." },
        { q: "Teori sınavından kaç kere kalınabilir?", a: "İsveç'te teori sınavından kalma sayısı sınırsızdır. Ancak her sınav için yeniden ücret ödemeniz gerekir (yaklaşık 400 SEK). Ehliyet.nu ile çalışarak ilk seferde geçme şansınızı %95'e çıkarabilirsiniz!" },
        { q: "Teori sınavı hangi konuları kapsıyor?", a: "Teori sınavı şu konuları kapsar: Trafik kuralları ve işaretleri, yol güvenliği, araç tekniği, çevre ve yakıt tasarrufu, ilk yardım, alkol ve uyuşturucu etkileri. Tüm bu konular Ehliyet.nu'de Türkçe olarak mevcuttur." },
        { q: "Teori sınavı ne kadar sürüyor?", a: "Teori sınavı için 50 dakika süre verilir. 70 soruyu bu sürede cevaplamanız gerekir. Genellikle 30-40 dakikada bitirebilirsiniz." }
      ]
    },
    {
      category: "İsveç'te Ehliyet Alma",
      questions: [
        { q: "İsveç'te ehliyet almak ne kadar sürer?", a: "İsveç'te ehliyet alma süreci ortalama 3-6 ay sürer. Bu süre teorik ve pratik eğitim, sınav randevuları ve kişisel çalışma temponuza bağlıdır. Yoğun çalışarak 2 ayda da tamamlayabilirsiniz." },
        { q: "İsveç'te ehliyet almanın toplam maliyeti nedir?", a: "2025 yılında İsveç'te ehliyet almanın toplam maliyeti 15.000-25.000 SEK arasındadır. Bu maliyet: Teori sınavı (400 SEK), pratik sınav (1000 SEK), sürüş dersleri (10.000-20.000 SEK), Risk 1-2 kursları (2000-3000 SEK) içerir." },
        { q: "Körkortstillstånd (ehliyet izni) nasıl alınır?", a: "Körkortstillstånd almak için Trafikverket'e başvuru yapmanız gerekir. Online başvuru yapabilirsiniz. Göz testi ve sağlık beyanı gerekir. İzin 5 yıl geçerlidir ve teorik sınavdan önce almanız şarttır." },
        { q: "Risk 1 ve Risk 2 kursu nedir?", a: "Risk 1 (Introduktionskurs): Alkol, uyuşturucu ve yorgunluğun sürüşe etkileri. Zorunlu, teorik sınavdan önce alınmalı. Risk 2 (Riskutbildning): Kaygan yolda sürüş pratiği. Pratik sınavdan önce alınmalı." },
        { q: "Türkiye ehliyetimi İsveç'te kullanabilir miyim?", a: "Türkiye ehliyeti İsveç'te 1 yıl süreyle geçerlidir (kalıcı oturumunuz varsa). Sonrasında İsveç ehliyetine çevirmeniz gerekir. Teori ve pratik sınavlarını geçmeniz şarttır." }
      ]
    },
    {
      category: "Ehliyet.nu Hakkında",
      questions: [
        { q: "Ücretsiz ehliyet soruları nereden çözebilirim?", a: "Ehliyet.nu'de 30 ücretsiz ehliyet sorusu çözebilirsiniz. Hiç kayıt olmadan, hemen başlayabilirsiniz. Premium üyelikle 2000+ soruya ve tüm özelliklere erişebilirsiniz." },
        { q: "Premium üyelik ne kadar?", a: "Premium üyelik tek seferlik 249 SEK'tir. Aylık ödeme yok, sınırsız süre erişim. 2000+ soru, gerçek sınav simülasyonu, ilerleme takibi ve Türkçe açıklamalar dahil." },
        { q: "Sorular gerçek sınav sorularıyla aynı mı?", a: "Sorularımız Trafikverket'in resmi müfredatına uygun hazırlanmıştır. Gerçek sınav formatında (70 soru, 50 dakika) pratik yapabilirsiniz. Kullanıcılarımızın %95'i ilk seferde geçiyor!" },
        { q: "Mobil cihazdan çalışabilir miyim?", a: "Evet! Ehliyet.nu tüm cihazlarda çalışır - telefon, tablet ve bilgisayar. İnternete bağlı olduğunuz her yerden çalışabilirsiniz." }
      ]
    },
    {
      category: "Sınav Günü",
      questions: [
        { q: "Teori sınavına ne götürmeliyim?", a: "Teori sınavına kimlik belgesi (pasaport veya İsveç kimliği) götürmeniz yeterlidir. Kalem, kağıt veya hesap makinesi götürmenize gerek yok - her şey bilgisayarda." },
        { q: "Sınav sonucu ne zaman açıklanır?", a: "Teori sınavı sonucu sınav biter bitmez ekranda görüntülenir. Anında geçip geçmediğinizi öğrenirsiniz. Detaylı sonuç raporu da verilir." },
        { q: "Sınavda kopya çekilebilir mi?", a: "Hayır, sınav salonunda sıkı güvenlik önlemleri var. Telefon ve elektronik cihazlar yasak. Her adayın soruları farklı sırada gelir. Kopya girişimi sınavın iptaliyle sonuçlanır." }
      ]
    }
  ];

  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const adminPassword = localStorage.getItem('admin_password');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const res = await axios.get(`${API}/admin/faq`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      const cats = res.data.categories || [];
      // If empty, use default FAQ data
      if (cats.length === 0) {
        setFaqCategories(DEFAULT_FAQ);
        // Expand first category by default
        setExpandedCategories({ 0: true });
      } else {
        setFaqCategories(cats);
        // Expand first category by default
        setExpandedCategories({ 0: true });
      }
    } catch (err) {
      console.error('FAQ fetch error:', err);
      // Set default categories if error
      setFaqCategories(DEFAULT_FAQ);
      setExpandedCategories({ 0: true });
    } finally {
      setLoading(false);
    }
  };

  const saveFAQ = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/faq`, 
        { categories: faqCategories },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      alert('SSS başarıyla kaydedildi!');
    } catch (err) {
      alert('Kaydetme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    setFaqCategories([...faqCategories, { category: "Yeni Kategori", questions: [] }]);
  };

  const removeCategory = (idx) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      setFaqCategories(faqCategories.filter((_, i) => i !== idx));
    }
  };

  const updateCategoryName = (idx, name) => {
    const updated = [...faqCategories];
    updated[idx].category = name;
    setFaqCategories(updated);
  };

  const addQuestion = (categoryIdx) => {
    const updated = [...faqCategories];
    updated[categoryIdx].questions.push({ q: "", a: "" });
    setFaqCategories(updated);
  };

  const removeQuestion = (categoryIdx, questionIdx) => {
    const updated = [...faqCategories];
    updated[categoryIdx].questions = updated[categoryIdx].questions.filter((_, i) => i !== questionIdx);
    setFaqCategories(updated);
  };

  const updateQuestion = (categoryIdx, questionIdx, field, value) => {
    const updated = [...faqCategories];
    updated[categoryIdx].questions[questionIdx][field] = value;
    setFaqCategories(updated);
  };

  // Move question up
  const moveQuestionUp = (categoryIdx, questionIdx) => {
    if (questionIdx === 0) return;
    const updated = [...faqCategories];
    const questions = [...updated[categoryIdx].questions];
    [questions[questionIdx - 1], questions[questionIdx]] = [questions[questionIdx], questions[questionIdx - 1]];
    updated[categoryIdx].questions = questions;
    setFaqCategories(updated);
  };

  // Move question down
  const moveQuestionDown = (categoryIdx, questionIdx) => {
    const updated = [...faqCategories];
    if (questionIdx >= updated[categoryIdx].questions.length - 1) return;
    const questions = [...updated[categoryIdx].questions];
    [questions[questionIdx], questions[questionIdx + 1]] = [questions[questionIdx + 1], questions[questionIdx]];
    updated[categoryIdx].questions = questions;
    setFaqCategories(updated);
  };

  // Move category up
  const moveCategoryUp = (idx) => {
    if (idx === 0) return;
    const updated = [...faqCategories];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setFaqCategories(updated);
    // Update expanded state
    setExpandedCategories(prev => {
      const newState = { ...prev };
      const temp = newState[idx - 1];
      newState[idx - 1] = newState[idx];
      newState[idx] = temp;
      return newState;
    });
  };

  // Move category down
  const moveCategoryDown = (idx) => {
    if (idx >= faqCategories.length - 1) return;
    const updated = [...faqCategories];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setFaqCategories(updated);
    // Update expanded state
    setExpandedCategories(prev => {
      const newState = { ...prev };
      const temp = newState[idx];
      newState[idx] = newState[idx + 1];
      newState[idx + 1] = temp;
      return newState;
    });
  };

  const toggleCategory = (idx) => {
    setExpandedCategories(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
          <span className="ml-3 text-white">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <HelpCircle className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">SSS Yönetimi</h2>
            <p className="text-gray-400 text-sm">Sık Sorulan Sorular sayfasını düzenleyin</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={addCategory}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold flex items-center gap-2 transition-all"
          >
            <Plus className="w-5 h-5" />
            Kategori Ekle
          </button>
          <button
            onClick={saveFAQ}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {faqCategories.map((cat, catIdx) => (
          <div key={catIdx} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {/* Category Header */}
            <div 
              className="flex items-center justify-between px-4 py-3 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
              onClick={() => toggleCategory(catIdx)}
            >
              <div className="flex items-center gap-3 flex-1">
                {expandedCategories[catIdx] ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
                <input
                  type="text"
                  value={cat.category}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateCategoryName(catIdx, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent text-white font-semibold text-lg border-b border-transparent hover:border-white/30 focus:border-purple-500 focus:outline-none flex-1"
                  placeholder="Kategori adı..."
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">{cat.questions.length} soru</span>
                {/* Move Up/Down */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveCategoryUp(catIdx);
                  }}
                  disabled={catIdx === 0}
                  className={`p-1.5 rounded-lg transition-colors ${catIdx === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'}`}
                  title="Yukarı taşı"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveCategoryDown(catIdx);
                  }}
                  disabled={catIdx === faqCategories.length - 1}
                  className={`p-1.5 rounded-lg transition-colors ${catIdx === faqCategories.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'}`}
                  title="Aşağı taşı"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCategory(catIdx);
                  }}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Questions */}
            {expandedCategories[catIdx] && (
              <div className="p-4 space-y-4">
                {cat.questions.map((q, qIdx) => (
                  <div key={qIdx} className="bg-black/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveQuestionUp(catIdx, qIdx)}
                          disabled={qIdx === 0}
                          className={`p-1 rounded transition-colors ${qIdx === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'}`}
                          title="Yukarı taşı"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveQuestionDown(catIdx, qIdx)}
                          disabled={qIdx === cat.questions.length - 1}
                          className={`p-1 rounded transition-colors ${qIdx === cat.questions.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20'}`}
                          title="Aşağı taşı"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-purple-400 font-bold mt-2">S{qIdx + 1}:</span>
                      <input
                        type="text"
                        value={q.q}
                        onChange={(e) => updateQuestion(catIdx, qIdx, 'q', e.target.value)}
                        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Soru..."
                      />
                      <button
                        onClick={() => removeQuestion(catIdx, qIdx)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-start gap-3 ml-14">
                      <span className="text-green-400 font-bold">C:</span>
                      <textarea
                        value={q.a}
                        onChange={(e) => updateQuestion(catIdx, qIdx, 'a', e.target.value)}
                        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                        placeholder="Cevap..."
                      />
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={() => addQuestion(catIdx)}
                  className="w-full py-3 border-2 border-dashed border-white/20 hover:border-purple-500/50 text-gray-400 hover:text-purple-400 rounded-xl flex items-center justify-center gap-2 transition-all"
                >
                  <Plus className="w-5 h-5" />
                  Soru Ekle
                </button>
              </div>
            )}
          </div>
        ))}

        {faqCategories.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <HelpCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>Henüz kategori eklenmemiş</p>
            <button
              onClick={addCategory}
              className="mt-4 px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold"
            >
              İlk Kategoriyi Ekle
            </button>
          </div>
        )}
      </div>

      {/* Preview Link */}
      <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/30 rounded-xl">
        <p className="text-blue-300 text-sm">
          💡 SSS sayfasını önizlemek için: <a href="/sss" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-200">/sss</a>
        </p>
      </div>
    </div>
  );
}


// NOTIFICATIONS TAB - Email Şablonları ve Bildirimler
function NotificationsTab() {
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [reminderSettings, setReminderSettings] = useState({
    auto_reminder_enabled: false,
    reminder_days: [3, 7, 14],
    include_free_users: false,
    include_premium_users: true,
    daily_limit: 50
  });
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("templates");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [daysInactive, setDaysInactive] = useState(3);
  const [sending, setSending] = useState(false);
  const [sendingAuto, setSendingAuto] = useState(false);
  const [includeFree, setIncludeFree] = useState(false);
  const [includePremium, setIncludePremium] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeSubTab === "reminder") {
      fetchInactiveUsers();
    }
  }, [activeSubTab, daysInactive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const adminPassword = localStorage.getItem("admin_password");
      
      const [templatesRes, emailsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/email-templates`, {
          headers: { "X-Admin-Password": adminPassword }
        }),
        axios.get(`${API}/admin/sent-emails`, {
          headers: { "X-Admin-Password": adminPassword }
        }),
        axios.get(`${API}/admin/reminder-settings`, {
          headers: { "X-Admin-Password": adminPassword }
        }).catch(() => ({ data: null }))
      ]);
      
      setEmailTemplates(templatesRes.data || []);
      setSentEmails(emailsRes.data || []);
      if (settingsRes.data) {
        setReminderSettings(settingsRes.data);
        setIncludeFree(settingsRes.data.include_free_users || false);
        setIncludePremium(settingsRes.data.include_premium_users !== false);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInactiveUsers = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/inactive-users?days=${daysInactive}`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setInactiveUsers(response.data.users || []);
    } catch (error) {
      console.error("Inactive users fetch error:", error);
    }
  };

  const saveTemplate = async (template) => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/email-template/${template.id}`,
        template,
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ Şablon kaydedildi!");
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      alert("❌ Şablon kaydedilemedi!");
    }
  };

  const saveReminderSettings = async () => {
    try {
      setSavingSettings(true);
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/reminder-settings`,
        {
          ...reminderSettings,
          include_free_users: includeFree,
          include_premium_users: includePremium
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ Ayarlar kaydedildi!");
    } catch (error) {
      alert("❌ Ayarlar kaydedilemedi: " + (error.response?.data?.detail || error.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleAutoReminder = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const newState = !reminderSettings.auto_reminder_enabled;
      await axios.put(
        `${API}/admin/reminder-settings`,
        { ...reminderSettings, auto_reminder_enabled: newState },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      setReminderSettings({ ...reminderSettings, auto_reminder_enabled: newState });
      alert(newState ? "✅ Otomatik hatırlatma AKTİF!" : "⏸️ Otomatik hatırlatma DURDURULDU!");
    } catch (error) {
      alert("❌ Ayar değiştirilemedi!");
    }
  };

  const sendReminder = async () => {
    try {
      setSending(true);
      setSendResult(null);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/send-reminder`,
        { 
          days_inactive: daysInactive,
          include_free_users: includeFree,
          include_premium_users: includePremium
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      setSendResult({
        success: true,
        message: response.data.message,
        sent: response.data.sent_count,
        failed: response.data.failed_count,
        details: response.data.details
      });
      fetchData();
    } catch (error) {
      setSendResult({
        success: false,
        message: error.response?.data?.detail || "Hatırlatma gönderilemedi!"
      });
    } finally {
      setSending(false);
    }
  };

  const runAutoReminder = async () => {
    try {
      setSendingAuto(true);
      setSendResult(null);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/run-auto-reminder`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      setSendResult({
        success: true,
        message: response.data.message,
        sent: response.data.total_sent,
        failed: response.data.total_failed,
        results_by_day: response.data.results_by_day
      });
      fetchData();
    } catch (error) {
      setSendResult({
        success: false,
        message: error.response?.data?.detail || "Otomatik hatırlatma çalıştırılamadı!"
      });
    } finally {
      setSendingAuto(false);
    }
  };

  const sendToSpecificUser = async (user) => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/send-reminder-to-user`,
        { 
          email: user.email,
          name: user.name,
          days_inactive: user.days_inactive
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert(`✅ ${user.email} adresine hatırlatma gönderildi!`);
      fetchData();
    } catch (error) {
      alert(`❌ Gönderilemedi: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    support_email: "support@ehliyet.nu",
    site_url: "https://ehliyet.nu",
    site_name: "Ehliyet.nu",
    tracking_enabled: true,
    footer_text: "© Ehliyet.nu"
  });
  const [emailTracking, setEmailTracking] = useState({ tracking: [], stats: {} });
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(null);

  // Subscription Expiry Reminder States
  const [expiringUsers, setExpiringUsers] = useState([]);
  const [expiryReminderSettings, setExpiryReminderSettings] = useState({
    auto_reminder_enabled: true,
    reminder_days: [7, 3, 1],
    daily_limit: 100
  });
  const [loadingExpiry, setLoadingExpiry] = useState(false);
  const [sendingExpiry, setSendingExpiry] = useState(null);
  const [runningExpiryReminders, setRunningExpiryReminders] = useState(false);
  const [expiryDaysFilter, setExpiryDaysFilter] = useState(7);

  // Free to Premium Promotion States
  const [freeUsers, setFreeUsers] = useState([]);
  const [promotionSettings, setPromotionSettings] = useState({
    auto_promotion_enabled: true,
    promotion_days: [3, 7, 14],
    daily_limit: 50
  });
  const [loadingPromotion, setLoadingPromotion] = useState(false);
  const [sendingPromotion, setSendingPromotion] = useState(null);
  const [runningPromotion, setRunningPromotion] = useState(false);
  const [promotionDaysFilter, setPromotionDaysFilter] = useState(7);

  // Cart Abandonment States
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [cartSettings, setCartSettings] = useState({
    auto_reminder_enabled: true,
    first_reminder_hours: 1,
    second_reminder_hours: 24,
    daily_limit: 50
  });
  const [loadingCarts, setLoadingCarts] = useState(false);
  const [sendingCartReminder, setSendingCartReminder] = useState(null);
  const [runningCartReminders, setRunningCartReminders] = useState(false);

  // Manual mark email as read
  const markEmailAsRead = async (trackingId) => {
    try {
      setMarkingAsRead(trackingId);
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/email-tracking/${trackingId}/mark-read`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      await fetchEmailTracking();
      alert("✅ E-posta okundu olarak işaretlendi!");
    } catch (error) {
      alert("❌ İşlem başarısız: " + (error.response?.data?.detail || error.message));
    } finally {
      setMarkingAsRead(null);
    }
  };

  // Fetch email settings and tracking
  useEffect(() => {
    if (activeSubTab === "settings" || activeSubTab === "tracking") {
      fetchEmailSettings();
    }
    if (activeSubTab === "tracking") {
      fetchEmailTracking();
    }
  }, [activeSubTab]);

  const fetchEmailSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setEmailSettings(response.data);
      }
    } catch (error) {
      console.error("Email settings fetch error:", error);
    }
  };

  const [refreshingTracking, setRefreshingTracking] = useState(false);

  const fetchEmailTracking = async () => {
    try {
      setRefreshingTracking(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/email-tracking?limit=100`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setEmailTracking(response.data);
      }
    } catch (error) {
      console.error("Email tracking fetch error:", error);
    } finally {
      setRefreshingTracking(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setSavingEmailSettings(true);
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/email-settings`,
        emailSettings,
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ E-posta ayarları kaydedildi!");
    } catch (error) {
      alert("❌ Ayarlar kaydedilemedi: " + (error.response?.data?.detail || error.message));
    } finally {
      setSavingEmailSettings(false);
    }
  };

  // Subscription Expiry Reminder Functions
  const fetchExpiringUsers = async () => {
    try {
      setLoadingExpiry(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/expiring-subscriptions?days=${expiryDaysFilter}`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setExpiringUsers(response.data.users || []);
    } catch (error) {
      console.error("Expiring users fetch error:", error);
    } finally {
      setLoadingExpiry(false);
    }
  };

  const fetchExpirySettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/expiry-reminder-settings`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setExpiryReminderSettings(response.data);
      }
    } catch (error) {
      console.error("Expiry settings fetch error:", error);
    }
  };

  const saveExpirySettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/expiry-reminder-settings`,
        expiryReminderSettings,
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ Ayarlar kaydedildi!");
    } catch (error) {
      alert("❌ Ayarlar kaydedilemedi: " + (error.response?.data?.detail || error.message));
    }
  };

  const sendExpiryReminderToUser = async (user) => {
    try {
      setSendingExpiry(user.email);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/send-expiry-reminder`,
        {
          email: user.email,
          name: user.name,
          days_left: user.days_left,
          expiry_date: new Date(user.premium_until).toLocaleDateString("tr-TR")
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        alert(`✅ ${user.email} adresine hatırlatma gönderildi!`);
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Gönderilemedi: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingExpiry(null);
    }
  };

  const runAllExpiryReminders = async () => {
    try {
      setRunningExpiryReminders(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/run-expiry-reminders`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        alert(`✅ ${response.data.results?.emails_sent || 0} hatırlatma e-postası gönderildi!`);
        fetchExpiringUsers();
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Çalıştırılamadı: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRunningExpiryReminders(false);
    }
  };

  // Fetch expiry data when tab changes
  useEffect(() => {
    if (activeSubTab === "expiry") {
      fetchExpiringUsers();
      fetchExpirySettings();
    }
  }, [activeSubTab, expiryDaysFilter]);

  // Free to Premium Promotion Functions
  const fetchFreeUsers = async () => {
    try {
      setLoadingPromotion(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/free-users?days=${promotionDaysFilter}`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setFreeUsers(response.data.users || []);
    } catch (error) {
      console.error("Free users fetch error:", error);
    } finally {
      setLoadingPromotion(false);
    }
  };

  const fetchPromotionSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/promotion-settings`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setPromotionSettings(response.data);
      }
    } catch (error) {
      console.error("Promotion settings fetch error:", error);
    }
  };

  const savePromotionSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/promotion-settings`,
        promotionSettings,
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ Ayarlar kaydedildi!");
    } catch (error) {
      alert("❌ Ayarlar kaydedilemedi: " + (error.response?.data?.detail || error.message));
    }
  };

  const sendPromotionToUser = async (user) => {
    try {
      setSendingPromotion(user.email);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/send-promotion-email`,
        {
          email: user.email,
          name: user.name,
          days_since_registration: user.days_since_registration
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        alert(`✅ ${user.email} adresine promosyon gönderildi!`);
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Gönderilemedi: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingPromotion(null);
    }
  };

  const runPromotionCampaign = async () => {
    try {
      setRunningPromotion(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/run-promotion-campaign`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        alert(`✅ ${response.data.results?.emails_sent || 0} promosyon e-postası gönderildi!`);
        fetchFreeUsers();
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Çalıştırılamadı: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRunningPromotion(false);
    }
  };

  // Fetch promotion data when tab changes
  useEffect(() => {
    if (activeSubTab === "promotion") {
      fetchFreeUsers();
      fetchPromotionSettings();
    }
  }, [activeSubTab, promotionDaysFilter]);

  // Cart Abandonment Functions
  const fetchAbandonedCarts = async () => {
    try {
      setLoadingCarts(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/abandoned-carts?hours=48`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      setAbandonedCarts(response.data.carts || []);
    } catch (error) {
      console.error("Abandoned carts fetch error:", error);
    } finally {
      setLoadingCarts(false);
    }
  };

  const fetchCartSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.get(`${API}/admin/cart-abandonment-settings`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      if (response.data) {
        setCartSettings(response.data);
      }
    } catch (error) {
      console.error("Cart settings fetch error:", error);
    }
  };

  const saveCartSettings = async () => {
    try {
      const adminPassword = localStorage.getItem("admin_password");
      await axios.put(
        `${API}/admin/cart-abandonment-settings`,
        cartSettings,
        { headers: { "X-Admin-Password": adminPassword } }
      );
      alert("✅ Ayarlar kaydedildi!");
    } catch (error) {
      alert("❌ Ayarlar kaydedilemedi: " + (error.response?.data?.detail || error.message));
    }
  };

  const sendCartReminderToUser = async (cart) => {
    try {
      setSendingCartReminder(cart.email);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/send-cart-reminder`,
        {
          email: cart.email,
          name: cart.name,
          hours_ago: Math.round(cart.hours_since_visit)
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        alert(`✅ ${cart.email} adresine hatırlatma gönderildi!`);
        fetchAbandonedCarts();
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Gönderilemedi: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingCartReminder(null);
    }
  };

  const runCartReminders = async () => {
    try {
      setRunningCartReminders(true);
      const adminPassword = localStorage.getItem("admin_password");
      const response = await axios.post(
        `${API}/admin/run-cart-reminders`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        const total = (response.data.results?.first_reminder_sent || 0) + (response.data.results?.second_reminder_sent || 0);
        alert(`✅ ${total} sepet hatırlatması gönderildi!`);
        fetchAbandonedCarts();
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ Çalıştırılamadı: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRunningCartReminders(false);
    }
  };

  // Fetch cart data when tab changes
  useEffect(() => {
    if (activeSubTab === "cart") {
      fetchAbandonedCarts();
      fetchCartSettings();
    }
  }, [activeSubTab]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-white">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
        <h2 className="text-3xl font-black text-white mb-2 flex items-center gap-3">
          <Mail className="w-10 h-10 text-purple-400" />
          Bildirimler & E-posta Yönetimi
        </h2>
        <p className="text-white/60">E-posta şablonlarını düzenleyin ve hatırlatmalar gönderin</p>
      </div>

      {/* Sub Tabs */}
      <div className="flex gap-3 flex-wrap">
        {[
          { id: "templates", label: "E-posta Şablonları", icon: FileText },
          { id: "settings", label: "E-posta Ayarları", icon: Settings },
          { id: "reminder", label: "Hatırlatma Gönder", icon: Bell },
          { id: "expiry", label: "Üyelik Bitiş", icon: Clock },
          { id: "promotion", label: "Premium Pazarlama", icon: Star },
          { id: "cart", label: "Sepet Hatırlatma", icon: ShoppingCart },
          { id: "auto", label: "Otomatik Sistem", icon: Zap },
          { id: "tracking", label: "Okuma Takibi", icon: Eye },
          { id: "history", label: "Gönderim Geçmişi", icon: Clock }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-5 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
              activeSubTab === tab.id
                ? "bg-purple-500 text-white"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Email Templates */}
      {activeSubTab === "templates" && (
        <div className="space-y-4">
          {emailTemplates.map((template) => (
            <div key={template.id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">{template.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      template.is_active ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"
                    }`}>
                      {template.is_active ? "Aktif" : "Pasif"}
                    </span>
                    <span className="text-white/40 text-sm">Tetikleyici: {template.trigger}</span>
                  </div>
                </div>
                <button
                  onClick={() => setEditingTemplate(template)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Düzenle
                </button>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-purple-300 font-semibold mb-2">Konu: {template.subject}</div>
                <pre className="text-white/70 text-sm whitespace-pre-wrap font-sans">{template.body.substring(0, 200)}...</pre>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Send Reminder - ENHANCED */}
      {activeSubTab === "reminder" && (
        <div className="space-y-6">
          {/* Manual Send Section */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Send className="w-8 h-8 text-yellow-400" />
              Manuel Hatırlatma Gönder
            </h3>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-yellow-300">
                🔔 Belirli bir süre giriş yapmamış kullanıcılara manuel olarak hatırlatma e-postası gönderir.
              </p>
            </div>
            
            {/* Days Selector */}
            <div className="mb-6">
              <label className="text-white/80 text-sm block mb-2">Kaç gündür giriş yapmayan kullanıcılara gönderilsin?</label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="1"
                  max="30"
                  value={daysInactive}
                  onChange={(e) => setDaysInactive(Number(e.target.value))}
                  className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                />
                <span className="text-3xl font-black text-yellow-400 w-24 text-center">{daysInactive} gün</span>
              </div>
            </div>

            {/* User Type Filters */}
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <label className="text-white/80 text-sm block mb-3">Hangi kullanıcılara gönderilsin?</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includePremium}
                    onChange={(e) => setIncludePremium(e.target.checked)}
                    className="w-5 h-5 rounded accent-yellow-500"
                  />
                  <span className="text-white flex items-center gap-2">
                    👑 Premium Kullanıcılar
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeFree}
                    onChange={(e) => setIncludeFree(e.target.checked)}
                    className="w-5 h-5 rounded accent-yellow-500"
                  />
                  <span className="text-white flex items-center gap-2">
                    ⭐ Ücretsiz Kullanıcılar
                  </span>
                </label>
              </div>
            </div>

            {/* Inactive Users Preview */}
            {inactiveUsers.length > 0 && (
              <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-orange-300 font-bold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {daysInactive}+ gün inaktif kullanıcılar ({inactiveUsers.length} kişi)
                  </h4>
                  <button
                    onClick={fetchInactiveUsers}
                    className="text-orange-300 hover:text-orange-200 text-sm flex items-center gap-1"
                  >
                    <RefreshCw className="w-4 h-4" /> Yenile
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {inactiveUsers.slice(0, 10).map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                      <div>
                        <div className="text-white font-medium flex items-center gap-2">
                          {user.name}
                          {user.is_premium && <span className="text-yellow-400 text-xs">👑</span>}
                        </div>
                        <div className="text-white/50 text-sm">{user.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400 font-bold">{user.days_inactive} gün</span>
                        <button
                          onClick={() => sendToSpecificUser(user)}
                          className="bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 px-3 py-1 rounded-lg text-sm"
                        >
                          Gönder
                        </button>
                      </div>
                    </div>
                  ))}
                  {inactiveUsers.length > 10 && (
                    <p className="text-white/40 text-center text-sm">...ve {inactiveUsers.length - 10} kişi daha</p>
                  )}
                </div>
              </div>
            )}

            {/* Send Result */}
            {sendResult && (
              <div className={`mb-6 p-4 rounded-xl ${sendResult.success ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                <div className={`font-bold ${sendResult.success ? 'text-green-300' : 'text-red-300'}`}>
                  {sendResult.success ? '✅' : '❌'} {sendResult.message}
                </div>
                {sendResult.success && sendResult.details && (
                  <div className="mt-2 text-sm text-white/70">
                    Gönderilen: {sendResult.sent} | Başarısız: {sendResult.failed}
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={sendReminder}
              disabled={sending || (!includeFree && !includePremium)}
              className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Hatırlatma E-postası Gönder ({inactiveUsers.length} kişi)
              </>
            )}
          </button>
        </div>
      </div>
      )}

      {/* Automatic System Tab */}
      {activeSubTab === "auto" && (
        <div className="space-y-6">
          {/* Auto Reminder Toggle */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Zap className="w-8 h-8 text-green-400" />
                  Otomatik Hatırlatma Sistemi
                </h3>
                <p className="text-white/60 mt-1">Sistem belirlenen günlerde otomatik olarak kullanıcılara hatırlatma gönderir</p>
              </div>
              <button
                onClick={toggleAutoReminder}
                className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                  reminderSettings.auto_reminder_enabled
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-gray-600 hover:bg-gray-500 text-white'
                }`}
              >
                {reminderSettings.auto_reminder_enabled ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    AKTİF
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    KAPALI
                  </>
                )}
              </button>
            </div>

            {reminderSettings.auto_reminder_enabled && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                <p className="text-green-300 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Otomatik hatırlatma sistemi aktif! Belirlenen günlerde kullanıcılara otomatik e-posta gönderilecek.
                </p>
                {reminderSettings.last_auto_run && (
                  <p className="text-green-300/70 text-sm mt-2">
                    Son çalışma: {new Date(reminderSettings.last_auto_run).toLocaleString('tr-TR')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Auto Reminder Settings */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <Settings className="w-6 h-6 text-blue-400" />
              Otomatik Hatırlatma Ayarları
            </h3>

            {/* Reminder Days */}
            <div className="mb-6">
              <label className="text-white/80 text-sm block mb-3">Hangi günlerde hatırlatma gönderilsin?</label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 5, 7, 10, 14, 21, 30].map((day) => (
                  <button
                    key={day}
                    onClick={() => {
                      const current = reminderSettings.reminder_days || [];
                      const newDays = current.includes(day)
                        ? current.filter(d => d !== day)
                        : [...current, day].sort((a, b) => a - b);
                      setReminderSettings({ ...reminderSettings, reminder_days: newDays });
                    }}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      (reminderSettings.reminder_days || []).includes(day)
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    {day} gün
                  </button>
                ))}
              </div>
              <p className="text-white/40 text-sm mt-2">
                Seçili: {(reminderSettings.reminder_days || []).join(', ')} gün sonra hatırlatma gönderilecek
              </p>
            </div>

            {/* User Type Selection */}
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <label className="text-white/80 text-sm block mb-3">Hangi kullanıcılara gönderilsin?</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderSettings.include_premium_users !== false}
                    onChange={(e) => setReminderSettings({ 
                      ...reminderSettings, 
                      include_premium_users: e.target.checked 
                    })}
                    className="w-5 h-5 rounded accent-blue-500"
                  />
                  <span className="text-white flex items-center gap-2">
                    👑 Premium Kullanıcılar
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderSettings.include_free_users || false}
                    onChange={(e) => setReminderSettings({ 
                      ...reminderSettings, 
                      include_free_users: e.target.checked 
                    })}
                    className="w-5 h-5 rounded accent-blue-500"
                  />
                  <span className="text-white flex items-center gap-2">
                    ⭐ Ücretsiz Kullanıcılar
                  </span>
                </label>
              </div>
            </div>

            {/* Daily Limit */}
            <div className="mb-6">
              <label className="text-white/80 text-sm block mb-2">Günlük e-posta limiti</label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={reminderSettings.daily_limit || 50}
                  onChange={(e) => setReminderSettings({ 
                    ...reminderSettings, 
                    daily_limit: Number(e.target.value) 
                  })}
                  className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-2xl font-black text-blue-400 w-24 text-center">
                  {reminderSettings.daily_limit || 50}
                </span>
              </div>
            </div>

            {/* Save Settings Button */}
            <button
              onClick={saveReminderSettings}
              disabled={savingSettings}
              className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingSettings ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Ayarları Kaydet
                </>
              )}
            </button>
          </div>

          {/* Manual Trigger Auto Reminder */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <Play className="w-6 h-6 text-purple-400" />
              Manuel Tetikleme
            </h3>
            <p className="text-white/60 mb-4">
              Otomatik sistemi şimdi manuel olarak çalıştırın. Belirlenen ayarlara göre uygun kullanıcılara hatırlatma gönderir.
            </p>

            {/* Send Result for Auto */}
            {sendResult && sendResult.results_by_day && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
                <div className="text-green-300 font-bold mb-2">✅ {sendResult.message}</div>
                <div className="space-y-1 text-sm">
                  {sendResult.results_by_day.map((r, idx) => (
                    <div key={idx} className="text-white/70">
                      {r.days} gün: {r.users_found} kullanıcı bulundu, {r.sent} gönderildi
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={runAutoReminder}
              disabled={sendingAuto || !reminderSettings.auto_reminder_enabled}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sendingAuto ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Çalışıyor...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Otomatik Sistemi Şimdi Çalıştır
                </>
              )}
            </button>
            {!reminderSettings.auto_reminder_enabled && (
              <p className="text-orange-400 text-sm text-center mt-2">
                ⚠️ Önce otomatik sistemi aktif etmeniz gerekiyor
              </p>
            )}
          </div>
        </div>
      )}

      {/* Email Settings Tab */}
      {activeSubTab === "settings" && (
        <div className="space-y-6">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Settings className="w-8 h-8 text-blue-400" />
              Genel E-posta Ayarları
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-white/80 text-sm block mb-2">Destek E-posta Adresi</label>
                <input
                  type="email"
                  value={emailSettings.support_email || ""}
                  onChange={(e) => setEmailSettings({...emailSettings, support_email: e.target.value})}
                  placeholder="support@ehliyet.nu"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
                <p className="text-white/40 text-xs mt-1">E-postalarda gösterilecek iletişim adresi</p>
              </div>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Site URL</label>
                <input
                  type="url"
                  value={emailSettings.site_url || ""}
                  onChange={(e) => setEmailSettings({...emailSettings, site_url: e.target.value})}
                  placeholder="https://ehliyet.nu"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
                <p className="text-white/40 text-xs mt-1">E-postalardaki butonların yönlendireceği adres</p>
              </div>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Site Adı</label>
                <input
                  type="text"
                  value={emailSettings.site_name || ""}
                  onChange={(e) => setEmailSettings({...emailSettings, site_name: e.target.value})}
                  placeholder="Ehliyet.nu"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Alt Bilgi Metni</label>
                <input
                  type="text"
                  value={emailSettings.footer_text || ""}
                  onChange={(e) => setEmailSettings({...emailSettings, footer_text: e.target.value})}
                  placeholder="© Ehliyet.nu"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-white/5 rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.tracking_enabled !== false}
                  onChange={(e) => setEmailSettings({...emailSettings, tracking_enabled: e.target.checked})}
                  className="w-5 h-5 rounded accent-blue-500"
                />
                <div>
                  <span className="text-white font-medium">E-posta Okuma Takibi</span>
                  <p className="text-white/40 text-sm">E-postaların açılıp açılmadığını takip et</p>
                </div>
              </label>
            </div>
            
            <button
              onClick={saveEmailSettings}
              disabled={savingEmailSettings}
              className="w-full mt-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingEmailSettings ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Ayarları Kaydet
                </>
              )}
            </button>
          </div>
          
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <h4 className="text-yellow-300 font-bold mb-2">💡 Şablon Değişkenleri</h4>
            <p className="text-white/60 text-sm mb-2">E-posta şablonlarında kullanabileceğiniz değişkenler:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{name}}"}</code>
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{email}}"}</code>
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{support_email}}"}</code>
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{site_url}}"}</code>
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{days_inactive}}"}</code>
              <code className="bg-white/10 px-2 py-1 rounded text-yellow-300">{"{{tracking_pixel}}"}</code>
            </div>
          </div>
        </div>
      )}

      {/* Email Tracking Tab */}
      {activeSubTab === "tracking" && (
        <div className="space-y-6">
          {/* Warning about email tracking limitations */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <h4 className="text-yellow-300 font-bold mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              E-posta Takibi Hakkında Önemli Bilgi
            </h4>
            <p className="text-white/70 text-sm">
              Gmail, Outlook ve diğer e-posta istemcileri güvenlik nedeniyle otomatik görüntü yüklemesini engelleyebilir. 
              Bu durumda kullanıcı e-postayı açsa bile "Okunmadı" olarak görünebilir. 
              <strong className="text-yellow-300"> Okunmayan e-postaları manuel olarak "Okundu" işaretleyebilirsiniz.</strong>
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <Send className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <p className="text-blue-300 text-sm">Toplam Gönderim</p>
                  <p className="text-3xl font-black text-white">{emailTracking.stats?.total_sent || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-500/20 p-3 rounded-xl">
                  <Eye className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <p className="text-green-300 text-sm">Okunan</p>
                  <p className="text-3xl font-black text-white">{emailTracking.stats?.total_opened || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="bg-purple-500/20 p-3 rounded-xl">
                  <TrendingUp className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Açılma Oranı</p>
                  <p className="text-3xl font-black text-white">{emailTracking.stats?.open_rate || 0}%</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tracking List */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-6 h-6 text-green-400" />
                E-posta Okuma Takibi
              </h3>
              <button
                onClick={fetchEmailTracking}
                disabled={refreshingTracking}
                className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${refreshingTracking ? 'animate-spin' : ''}`} />
                {refreshingTracking ? 'Yenileniyor...' : 'Yenile'}
              </button>
            </div>
            
            {emailTracking.tracking?.length === 0 ? (
              <div className="text-center py-12">
                <Eye className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">Henüz takip verisi yok</p>
                <p className="text-white/40 text-sm">E-posta gönderdikten sonra veriler burada görünecek</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10 max-h-96 overflow-y-auto">
                {emailTracking.tracking?.map((track, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            track.email_type === "welcome" ? "bg-blue-500/20 text-blue-300" :
                            track.email_type === "study_reminder" ? "bg-orange-500/20 text-orange-300" :
                            "bg-purple-500/20 text-purple-300"
                          }`}>
                            {track.email_type === "welcome" ? "Hoş Geldin" : 
                             track.email_type === "study_reminder" ? "Hatırlatma" : track.email_type}
                          </span>
                          {track.opened_at ? (
                            <span className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              OKUNDU
                              {track.manual_mark && <span className="text-green-400/60 ml-1">(Manuel)</span>}
                            </span>
                          ) : (
                            <button
                              onClick={() => markEmailAsRead(track.tracking_id)}
                              disabled={markingAsRead === track.tracking_id}
                              className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 transition-all disabled:opacity-50"
                            >
                              {markingAsRead === track.tracking_id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3" />
                              )}
                              Okundu İşaretle
                            </button>
                          )}
                        </div>
                        <p className="text-white font-medium">{track.recipient_name || "İsimsiz"}</p>
                        <p className="text-white/50 text-sm">{track.recipient_email}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-white/40">Gönderim:</p>
                        <p className="text-white/60">{track.sent_at ? new Date(track.sent_at).toLocaleString("tr-TR") : "-"}</p>
                        {track.opened_at && (
                          <>
                            <p className="text-green-400 mt-2">Okunma:</p>
                            <p className="text-green-300">{new Date(track.opened_at).toLocaleString("tr-TR")}</p>
                            {track.open_count > 1 && (
                              <p className="text-green-300/60 text-xs">{track.open_count}x açıldı</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email History */}
      {activeSubTab === "history" && (
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          {sentEmails.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/60">Henüz gönderim geçmişi yok</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {sentEmails.map((email, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-lg text-sm font-semibold">
                      {email.type}
                    </span>
                    <span className="text-white/40 text-sm">
                      {email.sent_at ? new Date(email.sent_at).toLocaleString("tr-TR") : "Tarih yok"}
                    </span>
                  </div>
                  <p className="text-white">
                    {email.users_count} kullanıcıya gönderildi ({email.days_inactive} gün inaktif)
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subscription Expiry Reminders */}
      {activeSubTab === "expiry" && (
        <div className="space-y-6">
          {/* Header with action buttons */}
          <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Clock className="w-8 h-8 text-red-400" />
                  Üyelik Bitiş Hatırlatmaları
                </h3>
                <p className="text-white/60 mt-1">
                  Premium aboneliği sona ermek üzere olan kullanıcılara otomatik hatırlatma gönderin
                </p>
              </div>
              <button
                onClick={runAllExpiryReminders}
                disabled={runningExpiryReminders}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2"
              >
                {runningExpiryReminders ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Tümüne Gönder
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-orange-400" />
              Hatırlatma Ayarları
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-white/80 text-sm block mb-2">
                  Hatırlatma Günleri (virgülle ayırın)
                </label>
                <input
                  type="text"
                  value={expiryReminderSettings.reminder_days?.join(", ") || "7, 3, 1"}
                  onChange={(e) => setExpiryReminderSettings({
                    ...expiryReminderSettings,
                    reminder_days: e.target.value.split(",").map(d => parseInt(d.trim())).filter(d => !isNaN(d))
                  })}
                  placeholder="7, 3, 1"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
                <p className="text-white/40 text-xs mt-1">
                  Abonelik bitmeden kaç gün önce hatırlatma gönderilsin
                </p>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Günlük Limit</label>
                <input
                  type="number"
                  value={expiryReminderSettings.daily_limit || 100}
                  onChange={(e) => setExpiryReminderSettings({
                    ...expiryReminderSettings,
                    daily_limit: parseInt(e.target.value) || 100
                  })}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expiryReminderSettings.auto_reminder_enabled}
                  onChange={(e) => setExpiryReminderSettings({
                    ...expiryReminderSettings,
                    auto_reminder_enabled: e.target.checked
                  })}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white">Otomatik Hatırlatma Aktif</span>
              </label>
              <button
                onClick={saveExpirySettings}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Kaydet
              </button>
            </div>
          </div>

          {/* Filter and User List */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-4">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-red-400" />
                Aboneliği Bitmek Üzere Olan Kullanıcılar
                {expiringUsers.length > 0 && (
                  <span className="bg-red-500 text-white text-sm px-2 py-1 rounded-lg ml-2">
                    {expiringUsers.length}
                  </span>
                )}
              </h4>
              <div className="flex items-center gap-3">
                <select
                  value={expiryDaysFilter}
                  onChange={(e) => setExpiryDaysFilter(parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
                >
                  <option value={1}>1 gün içinde</option>
                  <option value={3}>3 gün içinde</option>
                  <option value={7}>7 gün içinde</option>
                  <option value={14}>14 gün içinde</option>
                  <option value={30}>30 gün içinde</option>
                </select>
                <button
                  onClick={fetchExpiringUsers}
                  className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-xl"
                >
                  <RefreshCw className={`w-5 h-5 ${loadingExpiry ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {loadingExpiry ? (
              <div className="text-center py-12">
                <div className="animate-spin w-10 h-10 border-2 border-red-500/30 border-t-red-500 rounded-full mx-auto mb-4"></div>
                <p className="text-white/60">Yükleniyor...</p>
              </div>
            ) : expiringUsers.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-white/60">
                  {expiryDaysFilter} gün içinde sona eren abonelik yok
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {expiringUsers.map((user, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                        user.days_left <= 1 ? 'bg-red-500' : 
                        user.days_left <= 3 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}>
                        {user.days_left}
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-white/60 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">
                        Bitiş: {new Date(user.premium_until).toLocaleDateString("tr-TR")}
                      </p>
                      <p className="text-white/40 text-xs">{user.premium_package || "Premium"}</p>
                    </div>
                    <button
                      onClick={() => sendExpiryReminderToUser(user)}
                      disabled={sendingExpiry === user.email}
                      className="bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 text-red-300 px-4 py-2 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      {sendingExpiry === user.email ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-red-300/30 border-t-red-300 rounded-full"></div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Hatırlat
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Free to Premium Promotion Tab */}
      {activeSubTab === "promotion" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Gift className="w-8 h-8 text-purple-400" />
                  Free → Premium Pazarlama
                </h3>
                <p className="text-white/60 mt-1">
                  Ücretsiz kullanıcılara Premium avantajlarını anlatan e-postalar gönderin
                </p>
              </div>
              <button
                onClick={runPromotionCampaign}
                disabled={runningPromotion}
                className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2"
              >
                {runningPromotion ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Kampanyayı Çalıştır
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-purple-400" />
              Promosyon Ayarları
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-white/80 text-sm block mb-2">
                  Promosyon Günleri (virgülle ayırın)
                </label>
                <input
                  type="text"
                  value={promotionSettings.promotion_days?.join(", ") || "3, 7, 14"}
                  onChange={(e) => setPromotionSettings({
                    ...promotionSettings,
                    promotion_days: e.target.value.split(",").map(d => parseInt(d.trim())).filter(d => !isNaN(d))
                  })}
                  placeholder="3, 7, 14"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
                <p className="text-white/40 text-xs mt-1">
                  Kayıttan kaç gün sonra promosyon gönderilsin
                </p>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Günlük Limit</label>
                <input
                  type="number"
                  value={promotionSettings.daily_limit || 50}
                  onChange={(e) => setPromotionSettings({
                    ...promotionSettings,
                    daily_limit: parseInt(e.target.value) || 50
                  })}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={promotionSettings.auto_promotion_enabled}
                  onChange={(e) => setPromotionSettings({
                    ...promotionSettings,
                    auto_promotion_enabled: e.target.checked
                  })}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white">Otomatik Promosyon Aktif</span>
              </label>
              <button
                onClick={savePromotionSettings}
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Kaydet
              </button>
            </div>
          </div>

          {/* User List */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-4">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Free Kullanıcılar (Hedef Kitle)
                {freeUsers.length > 0 && (
                  <span className="bg-purple-500 text-white text-sm px-2 py-1 rounded-lg ml-2">
                    {freeUsers.length}
                  </span>
                )}
              </h4>
              <div className="flex items-center gap-3">
                <select
                  value={promotionDaysFilter}
                  onChange={(e) => setPromotionDaysFilter(parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white"
                >
                  <option value={3}>3 gün önce kayıt</option>
                  <option value={7}>7 gün önce kayıt</option>
                  <option value={14}>14 gün önce kayıt</option>
                </select>
                <button
                  onClick={fetchFreeUsers}
                  className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-xl"
                >
                  <RefreshCw className={`w-5 h-5 ${loadingPromotion ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {loadingPromotion ? (
              <div className="text-center py-12">
                <div className="animate-spin w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full mx-auto mb-4"></div>
                <p className="text-white/60">Yükleniyor...</p>
              </div>
            ) : freeUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">
                  {promotionDaysFilter} gün önce kayıt olan free kullanıcı yok
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {freeUsers.map((user, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-purple-500/30 flex items-center justify-center">
                        <span className="text-purple-300 font-bold">{user.days_since_registration}g</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-white/60 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">
                        Kayıt: {new Date(user.created_at).toLocaleDateString("tr-TR")}
                      </p>
                    </div>
                    <button
                      onClick={() => sendPromotionToUser(user)}
                      disabled={sendingPromotion === user.email}
                      className="bg-purple-500/20 hover:bg-purple-500/40 border border-purple-500/30 text-purple-300 px-4 py-2 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      {sendingPromotion === user.email ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-purple-300/30 border-t-purple-300 rounded-full"></div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Promosyon Gönder
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cart Abandonment Tab */}
      {activeSubTab === "cart" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <ShoppingCart className="w-8 h-8 text-orange-400" />
                  Sepet Terk Hatırlatması
                </h3>
                <p className="text-white/60 mt-1">
                  Ödeme sayfasını ziyaret edip satın almayı tamamlamayan kullanıcılara hatırlatma gönderin
                </p>
              </div>
              <button
                onClick={runCartReminders}
                disabled={runningCartReminders}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2"
              >
                {runningCartReminders ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Tümüne Gönder
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-orange-400" />
              Hatırlatma Ayarları
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="text-white/80 text-sm block mb-2">İlk Hatırlatma (saat)</label>
                <input
                  type="number"
                  value={cartSettings.first_reminder_hours || 1}
                  onChange={(e) => setCartSettings({
                    ...cartSettings,
                    first_reminder_hours: parseInt(e.target.value) || 1
                  })}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">İkinci Hatırlatma (saat)</label>
                <input
                  type="number"
                  value={cartSettings.second_reminder_hours || 24}
                  onChange={(e) => setCartSettings({
                    ...cartSettings,
                    second_reminder_hours: parseInt(e.target.value) || 24
                  })}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Günlük Limit</label>
                <input
                  type="number"
                  value={cartSettings.daily_limit || 50}
                  onChange={(e) => setCartSettings({
                    ...cartSettings,
                    daily_limit: parseInt(e.target.value) || 50
                  })}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cartSettings.auto_reminder_enabled}
                  onChange={(e) => setCartSettings({
                    ...cartSettings,
                    auto_reminder_enabled: e.target.checked
                  })}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white">Otomatik Hatırlatma Aktif</span>
              </label>
              <button
                onClick={saveCartSettings}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Kaydet
              </button>
            </div>
          </div>

          {/* Abandoned Carts List */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-orange-400" />
                Terk Edilmiş Sepetler (Son 48 Saat)
                {abandonedCarts.length > 0 && (
                  <span className="bg-orange-500 text-white text-sm px-2 py-1 rounded-lg ml-2">
                    {abandonedCarts.length}
                  </span>
                )}
              </h4>
              <button
                onClick={fetchAbandonedCarts}
                className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-xl"
              >
                <RefreshCw className={`w-5 h-5 ${loadingCarts ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {loadingCarts ? (
              <div className="text-center py-12">
                <div className="animate-spin w-10 h-10 border-2 border-orange-500/30 border-t-orange-500 rounded-full mx-auto mb-4"></div>
                <p className="text-white/60">Yükleniyor...</p>
              </div>
            ) : abandonedCarts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-white/60">Terk edilmiş sepet yok - Harika! 🎉</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {abandonedCarts.map((cart, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                        cart.hours_since_visit < 2 ? 'bg-yellow-500' : 
                        cart.hours_since_visit < 24 ? 'bg-orange-500' : 'bg-red-500'
                      }`}>
                        {Math.round(cart.hours_since_visit)}s
                      </div>
                      <div>
                        <p className="text-white font-medium">{cart.name}</p>
                        <p className="text-white/60 text-sm">{cart.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">
                        Ziyaret: {new Date(cart.visited_at).toLocaleString("tr-TR")}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {cart.reminder_sent_1h && (
                          <span className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded">1s ✓</span>
                        )}
                        {cart.reminder_sent_24h && (
                          <span className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded">24s ✓</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => sendCartReminderToUser(cart)}
                      disabled={sendingCartReminder === cart.email}
                      className="bg-orange-500/20 hover:bg-orange-500/40 border border-orange-500/30 text-orange-300 px-4 py-2 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      {sendingCartReminder === cart.email ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-orange-300/30 border-t-orange-300 rounded-full"></div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Hatırlat
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Template Modal with WYSIWYG Editor */}
      {editingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-purple-500/30">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-purple-400" />
              E-posta Şablonunu Düzenle
            </h3>
            
            {/* Help box */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
              <p className="text-blue-300 text-sm">
                💡 <strong>İpucu:</strong> Normal bir belge gibi yazabilirsiniz! Kalın, italik, liste, link ve emoji ekleyebilirsiniz.
                Değişkenler: <code className="bg-black/30 px-1 rounded">{'{name}'}</code> (kullanıcı adı), 
                <code className="bg-black/30 px-1 rounded ml-1">{'{email}'}</code> (e-posta)
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-white/80 text-sm block mb-1">Şablon Adı</label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
              
              <div>
                <label className="text-white/80 text-sm block mb-1">Konu</label>
                <input
                  type="text"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
                />
              </div>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">E-posta İçeriği</label>
                <RichTextEditor
                  value={editingTemplate.body}
                  onChange={(content) => setEditingTemplate({...editingTemplate, body: content})}
                  placeholder="E-posta içeriğini buraya yazın... 📝"
                />
                <p className="text-white/40 text-xs mt-2">
                  📝 Değişkenler otomatik değiştirilir: {'{name}'} → Kullanıcı adı, {'{email}'} → E-posta adresi
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingTemplate.is_active}
                    onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <span className="text-white">Aktif</span>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingTemplate(null)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold"
              >
                İptal
              </button>
              <button
                onClick={() => saveTemplate(editingTemplate)}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

